import { useState, useEffect, useCallback, useRef } from "react";
import "@/App.css";
import axios from "axios";
import { DrawingCanvas, ColoringBook } from "./games/CreativeGames";
import PuzzleGame from "./games/PuzzleGame";
import { OddOneOutGame, SequenceGame, MemoryGame, RiddleGame } from "./games/BrainGames";
import ItalianQuizGame from "./games/ItalianQuizGame";
import AIQuizGame from "./games/AIQuizGame";
import CheckersGame from "./games/CheckersGame";
import SettimanaEnigmistica from "./games/SettimanaEnigmistica";
import ChessGame from "./games/ChessGame";
import { LevelBar, LevelUpOverlay, ComboPopup, RewardBreakdown, RivalCard, WeeklyRankWidget, DailyChestModal, FortuneWheel, MissionNudge, BlitzTimer } from "./components/Engagement";
import { VirtualFriendsList } from "./components/VirtualFriends";
import { PaymentResultModal } from "./components/PaymentResult";
import soundManager from "./utils/SoundManager";
import AuthScreen from "./AuthScreen";

const ConfettiBurst = () => (
  <div className="confetti-container" aria-hidden="true" data-testid="confetti">
    {Array.from({ length: 60 }).map((_, i) => (
      <span
        key={i}
        className="confetti-piece"
        style={{
          left: `${Math.random() * 100}%`,
          backgroundColor: ["#f43f5e", "#f59e0b", "#10b981", "#3b82f6", "#a855f7", "#facc15"][i % 6],
          animationDelay: `${Math.random() * 1.5}s`,
          animationDuration: `${2.5 + Math.random() * 2}s`
        }}
      />
    ))}
  </div>
);

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Translations
const translations = {
  it: {
    title: "CelestApp",
    subtitle: "Impara giocando!",
    selectProfile: "Seleziona il tuo profilo",
    createProfile: "Crea nuovo profilo",
    name: "Nome",
    age: "Età",
    selectAvatar: "Scegli avatar",
    create: "Crea",
    cancel: "Annulla",
    play: "Gioca!",
    coins: "Monete",
    trophies: "Trofei",
    medals: "Medaglie",
    leaderboard: "Classifica",
    competitions: "Competizioni",
    stats: "Statistiche",
    iqAnalysis: "Analisi QI",
    shop: "Negozio",
    challenges: "Sfide 1v1",
    settings: "Impostazioni",
    editProfile: "Modifica Profilo",
    emailOptional: "Email (opzionale)",
    saveChanges: "Salva Modifiche",
    savedOk: "Salvato! ✅",
    dangerZone: "Zona Pericolosa",
    deleteAccount: "Elimina Account",
    deleteWarning: "Questa azione cancellerà per sempre tutti i dati di questo profilo: punteggi, monete, badge, amici e acquisti.",
    deleteConfirm1Title: "Sei sicuro?",
    deleteConfirm1: "Vuoi davvero eliminare questo account? Tutti i dati andranno persi.",
    deleteConfirm2Title: "Ultima conferma!",
    deleteConfirm2: "L'eliminazione è definitiva e NON può essere annullata. Confermi?",
    yesContinue: "Sì, continua",
    deleteForever: "Elimina per sempre",
    myBadges: "I miei Badge",
    noBadgesYet: "Gioca per sbloccare i tuoi primi badge!",
    newBadgeUnlocked: "Nuovo Badge sbloccato!",
    personalization: "Personalizzazione",
    bgColorTitle: "Colore di sfondo",
    layoutTitle: "Layout Dashboard",
    layoutCompact: "Compatto",
    layoutExpanded: "Espanso",
    defaultColor: "Ripristina colore originale",
    activityFeed: "Cosa succede ora",
    noActivity: "Nessuna attività recente",
    justNow: "adesso",
    minutesAgo: "min fa",
    hoursAgo: "ore fa",
    daysAgo: "g fa",
    copyLink: "Copia Link",
    linkCopied: "Copiato! ✅",
    challengedYou: "ti ha sfidato!",
    acceptAndPlay: "Accetta e Gioca",
    challengeReceived: "Hai ricevuto una sfida! Scegli il tuo profilo per accettarla.",
    notNow: "Non ora",
    aiQuiz: "Quiz AI",
    aiQuizDesc: "Sfide create dall'AI per la tua età",
    aiGenerating: "L'AI sta creando sfide su misura per la tua età... mai uguali alle precedenti!",
    aiError: "Ops! La generazione non è riuscita.",
    retry: "Riprova",
    newQuiz: "Nuovo Quiz",
    becomePro: "Diventa PRO",
    proActiveUntil: "PRO attivo fino al",
    proDesc: "Sblocca colori di sfondo e layout personalizzati con CelestApp PRO!",
    proRequired: "Serve CelestApp PRO per personalizzare colori e layout!",
    proPerMonth: "€2,99 / mese",
    paymentSuccess: "Pagamento riuscito!",
    paymentProOk: "Ora sei PRO! 👑 Seleziona il tuo profilo per personalizzare l'app.",
    paymentTournamentOk: "Iscrizione al torneo completata! 🏆",
    paymentCancelled: "Pagamento annullato.",
    paymentError: "Verifica del pagamento non riuscita.",
    challengedYouBanner: "ti ha sfidato! Tocca per rispondere",
    peersOnly: "Competi solo con i tuoi coetanei",
    yearsBand: "anni",
    joinAsGroup: "Con Gruppo",
    chooseGroup: "Scegli il gruppo",
    viewRanking: "Classifica",
    playersTab: "Giocatori",
    groupsTab: "Gruppi",
    noScores: "Ancora nessun punteggio",
    registeredOk: "Iscrizione completata!",
    teamScore: "Punteggio squadra",
    noGroupsYet: "Non fai parte di nessun gruppo. Creane uno dalla sezione Gruppi!",
    parentsArea: "Area Genitori",
    parentsSubtitle: "Progressi, tempo di gioco e spese dei tuoi bambini",
    playtime: "Tempo di gioco",
    coinsSpentLabel: "Monete spese",
    moneySpentLabel: "Spesa reale",
    gamesWeek: "Partite (7 giorni)",
    lastGameLabel: "Ultima partita",
    powerupsLabel: "Power-up",
    doubleActiveLabel: "Monete x2 attive!",
    hintLabel: "Aiuto",
    skipLabel: "Salta",
    hintIs: "La risposta è",
    difficultyLabel: "Difficoltà",
    illustratedLabel: "Immagini Illustrate",
    emojiLabel: "Emoji",
    missionsTitle: "Missioni di oggi",
    checkersLabel: "Dama",
    chessLabel: "Scacchi",
    pinCreateTitle: "Crea il PIN Genitori",
    pinEnterTitle: "PIN Genitori",
    pinWrong: "PIN errato, riprova!",
    pinHint: "4 cifre — servirà per entrare nell'Area Genitori",
    weeklyReport: "Report Settimanale",
    printLabel: "Stampa",
    streakLabel: "giorni di fila",
    coinsTab: "Monete",
    missionsTab: "Missioni",
    sendEmailLabel: "Invia via Email",
    emailSentOk: "Email inviata! ✅",
    emailNeedKey: "Per inviare email serve la chiave Resend: chiedimela in chat!",
    emailFailed: "Invio non riuscito",
    shareVictories: "Condividi",
    shareDownloaded: "Immagine scaricata! 📥",
    gameTypes: {
      addition: "Addizioni",
      subtraction: "Sottrazioni",
      multiplication: "Moltiplicazioni",
      division: "Divisioni",
      mixed: "Misto"
    },
    question: "Domanda",
    of: "di",
    correct: "Corretto!",
    wrong: "Sbagliato!",
    correctAnswer: "La risposta era",
    gameOver: "Partita Finita!",
    score: "Punteggio",
    accuracy: "Precisione",
    coinsEarned: "Monete guadagnate",
    medalEarned: "Medaglia ottenuta",
    iqScore: "Punteggio QI",
    playAgain: "Gioca ancora",
    backHome: "Torna alla Home",
    rank: "Posizione",
    // Game categories
    mathGames: "Matematica",
    creativeArea: "Area Creativa",
    puzzleGames: "Puzzle",
    brainGames: "Brain Games",
    // Creative area
    drawFree: "Disegno Libero",
    coloringBook: "Colora",
    brushSize: "Dimensione",
    eraser: "Gomma",
    clear: "Cancella",
    save: "Salva",
    colors: "Colori",
    chooseImage: "Scegli immagine",
    // Puzzle
    puzzleTitle: "Puzzle",
    pieces: "pezzi",
    completed: "Completato!",
    moves: "Mosse",
    time: "Tempo",
    shuffle: "Mescola",
    // Brain games
    brainTitle: "Brain Games",
    findOddOne: "Trova l'Intruso",
    sequences: "Sequenze",
    memory: "Memory",
    riddles: "Indovinelli",
    compare: "Confronta",
    whichDoesntBelong: "Quale non appartiene al gruppo?",
    completeSequence: "Completa la sequenza",
    findPairs: "Trova le coppie!",
    thinkCarefully: "Pensa bene...",
    whichIsBigger: "Quale è più grande?",
    excellent: "Eccellente!",
    tryAgain: "Riprova!",
    nextLevel: "Prossimo",
    level: "Livello",
    player: "Giocatore",
    ageGroup: "Fascia d'età",
    years: "anni",
    language: "Lingua",
    logout: "Esci",
    welcome: "Benvenuto",
    chooseGame: "Scegli un gioco",
    avgTime: "Tempo medio",
    seconds: "secondi",
    goldMedal: "🥇 Oro",
    silverMedal: "🥈 Argento",
    bronzeMedal: "🥉 Bronzo",
    noMedal: "Nessuna medaglia",
    iqLevels: {
      exceptional: "Eccezionale!",
      above_average: "Sopra la media!",
      average: "Nella media",
      developing: "In sviluppo",
      beginner: "Principiante"
    },
    percentile: "Percentile",
    comparedTo: "rispetto ai bambini della stessa età",
    factors: "Fattori valutati",
    totalGames: "Partite totali",
    joinCompetition: "Partecipa",
    prize: "Premio",
    participants: "Partecipanti",
    // Tournaments
    tournaments: "Tornei",
    tournamentsTitle: "Tornei",
    dailyTournament: "Giornaliero",
    weeklyTournament: "Settimanale",
    premiumTournament: "Premium",
    free: "Gratis",
    entryFee: "Iscrizione",
    prizePool: "Montepremi",
    joinTournament: "Partecipa",
    registered: "Iscritto",
    tournamentLeaderboard: "Classifica Torneo",
    yourRank: "La tua posizione",
    playNow: "Gioca Ora",
    tournamentEnds: "Termina",
    // Friends
    friends: "Amici",
    friendsTitle: "Amici",
    addFriend: "Aggiungi Amico",
    friendRequests: "Richieste",
    searchFriend: "Cerca per nome...",
    sendRequest: "Invia Richiesta",
    acceptRequest: "Accetta",
    rejectRequest: "Rifiuta",
    removeFriend: "Rimuovi",
    noFriends: "Nessun amico ancora",
    noRequests: "Nessuna richiesta",
    requestSent: "Richiesta inviata!",
    // Groups
    groups: "Gruppi",
    groupsTitle: "I Miei Gruppi",
    createGroup: "Crea Gruppo",
    joinGroup: "Unisciti",
    openGroup: "Aperto",
    privateGroup: "Privato",
    inviteOnly: "Solo Inviti",
    groupMembers: "Membri",
    groupAdmin: "Admin",
    leaveGroup: "Esci",
    inviteToGroup: "Invita",
    groupInvites: "Inviti Gruppo",
    pendingRequests: "Richieste in Attesa",
    noGroups: "Nessun gruppo",
    // Italian Quiz
    italianQuiz: "Quiz Italiano",
    // Sounds
    soundOn: "Suoni ON",
    soundOff: "Suoni OFF",
    // Inactivity
    inactivityWarning: "Attenzione Inattività!",
    playToRecover: "Gioca per recuperare!",
    // Shop translations
    shopTitle: "Negozio Magico",
    avatars: "Avatar",
    themes: "Temi",
    powerups: "Power-Up",
    buy: "Compra",
    owned: "Posseduto",
    equip: "Equipaggia",
    equipped: "Equipaggiato",
    notEnoughCoins: "Monete insufficienti!",
    purchaseSuccess: "Acquisto completato!",
    yourCoins: "Le tue monete",
    // Challenge translations
    challengesTitle: "Sfide 1v1",
    createChallenge: "Crea Sfida",
    pendingChallenges: "Sfide Disponibili",
    activeChallenges: "Le Tue Sfide",
    challengeHistory: "Storico",
    acceptChallenge: "Accetta",
    vsText: "VS",
    waitingOpponent: "In attesa dell'avversario...",
    challengeWon: "Hai Vinto!",
    challengeLost: "Hai Perso",
    challengeTie: "Pareggio",
    startChallenge: "Inizia Sfida",
    noChallenges: "Nessuna sfida disponibile",
    createYourChallenge: "Crea la tua sfida!",
    challengeCreated: "Sfida creata!",
    // Multiplayer translations
    multiplayerMode: "Sfida Locale",
    onlineFriends: "Amici Online",
    onlineFriendsDesc: "Vedi chi è online e sfidalo!",
    virtualFriends: "Amici Virtuali",
    virtualFriendsDesc: "Nessun amico online? Sfida un amico virtuale: si adatta alla tua bravura!",
    virtualFriendsTab: "Virtuali",
    difficultyLabel: "Difficoltà",
    diffFacile: "Facile",
    diffEquilibrato: "Equilibrato",
    diffTosto: "Tosto",
    yourRecord: "Il tuo record",
    botStreak: "Vittorie di fila",
    youWon: "Hai vinto!",
    botWon: "ha vinto questa volta",
    tieResult: "Pareggio!",
    rematch: "Rivincita",
    nextOpponent: "Prossimo avversario",
    streakBonus: "Bonus serie",
    searchFriendHint: "Cerca per nome oppure scrivi l'email esatta dell'account",
    alreadyFriend: "Già amici",
    requestPending: "Richiesta inviata",
    foundByEmail: "trovato via email",
    noOneOnline: "Nessun amico online in questo momento. Torna più tardi!",
    challengeBtn: "Sfida",
    inviteSent: "Sfida inviata!",
    multiplayerDesc: "Gioca con amici sullo stesso dispositivo!",
    addPlayer: "Aggiungi",
    removePlayer: "Rimuovi",
    startMultiplayer: "Inizia Sfida!",
    playerTurn: "Tocca a",
    passDeviceTitle: "Passa il dispositivo",
    passDeviceMsg: "Ora tocca a",
    imReady: "Sono pronto!",
    timesUp: "Tempo scaduto!",
    secondsLabel: "s",
    multiplayerResults: "Risultati Sfida",
    winner: "Vincitore",
    fastest: "Più Veloce",
    chooseNewGame: "Scegli Gioco",
    sameGame: "Stesso Gioco",
    addAtLeast2Players: "Aggiungi almeno 2 giocatori",
    changeAvatar: "Cambia Avatar",
    tapToChange: "Tocca per cambiare",
    selectNewAvatar: "Scegli il tuo nuovo avatar!",
    avatarChanged: "Avatar cambiato!"
  },
  en: {
    title: "CelestApp",
    subtitle: "Learn by playing!",
    selectProfile: "Select your profile",
    createProfile: "Create new profile",
    name: "Name",
    age: "Age",
    selectAvatar: "Choose avatar",
    create: "Create",
    cancel: "Cancel",
    play: "Play!",
    coins: "Coins",
    trophies: "Trophies",
    medals: "Medals",
    leaderboard: "Leaderboard",
    competitions: "Competitions",
    stats: "Statistics",
    iqAnalysis: "IQ Analysis",
    shop: "Shop",
    challenges: "1v1 Challenges",
    settings: "Settings",
    editProfile: "Edit Profile",
    emailOptional: "Email (optional)",
    saveChanges: "Save Changes",
    savedOk: "Saved! ✅",
    dangerZone: "Danger Zone",
    deleteAccount: "Delete Account",
    deleteWarning: "This action will permanently delete all data for this profile: scores, coins, badges, friends and purchases.",
    deleteConfirm1Title: "Are you sure?",
    deleteConfirm1: "Do you really want to delete this account? All data will be lost.",
    deleteConfirm2Title: "Final confirmation!",
    deleteConfirm2: "Deletion is permanent and CANNOT be undone. Confirm?",
    yesContinue: "Yes, continue",
    deleteForever: "Delete forever",
    myBadges: "My Badges",
    noBadgesYet: "Play to unlock your first badges!",
    newBadgeUnlocked: "New Badge unlocked!",
    personalization: "Personalization",
    bgColorTitle: "Background color",
    layoutTitle: "Dashboard Layout",
    layoutCompact: "Compact",
    layoutExpanded: "Expanded",
    defaultColor: "Restore original color",
    activityFeed: "What's happening now",
    noActivity: "No recent activity",
    justNow: "just now",
    minutesAgo: "min ago",
    hoursAgo: "h ago",
    daysAgo: "d ago",
    copyLink: "Copy Link",
    linkCopied: "Copied! ✅",
    challengedYou: "challenged you!",
    acceptAndPlay: "Accept & Play",
    challengeReceived: "You received a challenge! Choose your profile to accept it.",
    notNow: "Not now",
    aiQuiz: "AI Quiz",
    aiQuizDesc: "AI-made challenges for your age",
    aiGenerating: "The AI is creating challenges tailored to your age... never the same twice!",
    aiError: "Oops! Generation failed.",
    retry: "Retry",
    newQuiz: "New Quiz",
    becomePro: "Go PRO",
    proActiveUntil: "PRO active until",
    proDesc: "Unlock custom background colors and layouts with CelestApp PRO!",
    proRequired: "CelestApp PRO is required to customize colors and layout!",
    proPerMonth: "€2.99 / month",
    paymentSuccess: "Payment successful!",
    paymentProOk: "You are now PRO! 👑 Select your profile to customize the app.",
    paymentTournamentOk: "Tournament registration complete! 🏆",
    paymentCancelled: "Payment cancelled.",
    paymentError: "Payment verification failed.",
    challengedYouBanner: "challenged you! Tap to respond",
    peersOnly: "Compete only with your peers",
    yearsBand: "years",
    joinAsGroup: "As Group",
    chooseGroup: "Choose group",
    viewRanking: "Ranking",
    playersTab: "Players",
    groupsTab: "Groups",
    noScores: "No scores yet",
    registeredOk: "Registration complete!",
    teamScore: "Team score",
    noGroupsYet: "You're not in any group yet. Create one from the Groups section!",
    parentsArea: "Parents Area",
    parentsSubtitle: "Progress, playtime and spending of your kids",
    playtime: "Playtime",
    coinsSpentLabel: "Coins spent",
    moneySpentLabel: "Real spending",
    gamesWeek: "Games (7 days)",
    lastGameLabel: "Last game",
    powerupsLabel: "Power-ups",
    doubleActiveLabel: "Coins x2 active!",
    hintLabel: "Hint",
    skipLabel: "Skip",
    hintIs: "The answer is",
    difficultyLabel: "Difficulty",
    illustratedLabel: "Illustrated Images",
    emojiLabel: "Emoji",
    missionsTitle: "Today's missions",
    checkersLabel: "Checkers",
    chessLabel: "Chess",
    pinCreateTitle: "Create Parents PIN",
    pinEnterTitle: "Parents PIN",
    pinWrong: "Wrong PIN, try again!",
    pinHint: "4 digits — required to enter the Parents Area",
    weeklyReport: "Weekly Report",
    printLabel: "Print",
    streakLabel: "day streak",
    coinsTab: "Coins",
    missionsTab: "Missions",
    sendEmailLabel: "Send via Email",
    emailSentOk: "Email sent! ✅",
    emailNeedKey: "Sending email requires a Resend key: ask me in chat!",
    emailFailed: "Sending failed",
    shareVictories: "Share",
    shareDownloaded: "Image downloaded! 📥",
    gameTypes: {
      addition: "Addition",
      subtraction: "Subtraction",
      multiplication: "Multiplication",
      division: "Division",
      mixed: "Mixed"
    },
    question: "Question",
    of: "of",
    correct: "Correct!",
    wrong: "Wrong!",
    correctAnswer: "The answer was",
    gameOver: "Game Over!",
    score: "Score",
    accuracy: "Accuracy",
    coinsEarned: "Coins earned",
    medalEarned: "Medal earned",
    iqScore: "IQ Score",
    playAgain: "Play again",
    backHome: "Back to Home",
    rank: "Rank",
    player: "Player",
    ageGroup: "Age group",
    years: "years",
    language: "Language",
    logout: "Logout",
    welcome: "Welcome",
    chooseGame: "Choose a game",
    avgTime: "Average time",
    seconds: "seconds",
    goldMedal: "🥇 Gold",
    silverMedal: "🥈 Silver",
    bronzeMedal: "🥉 Bronze",
    noMedal: "No medal",
    iqLevels: {
      exceptional: "Exceptional!",
      above_average: "Above average!",
      average: "Average",
      developing: "Developing",
      beginner: "Beginner"
    },
    percentile: "Percentile",
    comparedTo: "compared to children of the same age",
    factors: "Evaluated factors",
    totalGames: "Total games",
    joinCompetition: "Join",
    prize: "Prize",
    participants: "Participants",
    // Tournaments
    tournaments: "Tournaments",
    tournamentsTitle: "Tournaments",
    dailyTournament: "Daily",
    weeklyTournament: "Weekly",
    premiumTournament: "Premium",
    free: "Free",
    entryFee: "Entry Fee",
    prizePool: "Prize Pool",
    joinTournament: "Join",
    registered: "Registered",
    tournamentLeaderboard: "Tournament Leaderboard",
    yourRank: "Your Rank",
    playNow: "Play Now",
    tournamentEnds: "Ends",
    // Friends
    friends: "Friends",
    friendsTitle: "Friends",
    addFriend: "Add Friend",
    friendRequests: "Requests",
    searchFriend: "Search by name...",
    sendRequest: "Send Request",
    acceptRequest: "Accept",
    rejectRequest: "Reject",
    removeFriend: "Remove",
    noFriends: "No friends yet",
    noRequests: "No requests",
    requestSent: "Request sent!",
    // Groups
    groups: "Groups",
    groupsTitle: "My Groups",
    createGroup: "Create Group",
    joinGroup: "Join",
    openGroup: "Open",
    privateGroup: "Private",
    inviteOnly: "Invite Only",
    groupMembers: "Members",
    groupAdmin: "Admin",
    leaveGroup: "Leave",
    inviteToGroup: "Invite",
    groupInvites: "Group Invites",
    pendingRequests: "Pending Requests",
    noGroups: "No groups",
    // Italian Quiz
    italianQuiz: "Italian Quiz",
    // Sounds
    soundOn: "Sound ON",
    soundOff: "Sound OFF",
    // Inactivity
    inactivityWarning: "Inactivity Warning!",
    playToRecover: "Play to recover!",
    // Game categories
    mathGames: "Math",
    creativeArea: "Creative Area",
    puzzleGames: "Puzzle",
    brainGames: "Brain Games",
    // Creative area
    drawFree: "Free Draw",
    coloringBook: "Coloring",
    brushSize: "Size",
    eraser: "Eraser",
    clear: "Clear",
    save: "Save",
    colors: "Colors",
    chooseImage: "Choose image",
    // Puzzle
    puzzleTitle: "Puzzle",
    pieces: "pieces",
    completed: "Completed!",
    moves: "Moves",
    time: "Time",
    shuffle: "Shuffle",
    // Brain games
    brainTitle: "Brain Games",
    findOddOne: "Find the Odd One",
    sequences: "Sequences",
    memory: "Memory",
    riddles: "Riddles",
    compare: "Compare",
    whichDoesntBelong: "Which doesn't belong?",
    completeSequence: "Complete the sequence",
    findPairs: "Find the pairs!",
    thinkCarefully: "Think carefully...",
    whichIsBigger: "Which is bigger?",
    excellent: "Excellent!",
    tryAgain: "Try again!",
    nextLevel: "Next",
    level: "Level",
    // Shop translations
    shopTitle: "Magic Shop",
    avatars: "Avatars",
    themes: "Themes",
    powerups: "Power-Ups",
    buy: "Buy",
    owned: "Owned",
    equip: "Equip",
    equipped: "Equipped",
    notEnoughCoins: "Not enough coins!",
    purchaseSuccess: "Purchase complete!",
    yourCoins: "Your coins",
    // Challenge translations
    challengesTitle: "1v1 Challenges",
    createChallenge: "Create Challenge",
    pendingChallenges: "Available Challenges",
    activeChallenges: "Your Challenges",
    challengeHistory: "History",
    acceptChallenge: "Accept",
    vsText: "VS",
    waitingOpponent: "Waiting for opponent...",
    challengeWon: "You Won!",
    challengeLost: "You Lost",
    challengeTie: "Tie",
    startChallenge: "Start Challenge",
    noChallenges: "No challenges available",
    createYourChallenge: "Create your challenge!",
    challengeCreated: "Challenge created!",
    // Multiplayer translations
    multiplayerMode: "Local Challenge",
    onlineFriends: "Online Friends",
    onlineFriendsDesc: "See who's online and challenge them!",
    virtualFriends: "Virtual Friends",
    virtualFriendsDesc: "No friends online? Challenge a virtual friend: it adapts to your skill!",
    virtualFriendsTab: "Virtual",
    difficultyLabel: "Difficulty",
    diffFacile: "Easy",
    diffEquilibrato: "Balanced",
    diffTosto: "Tough",
    yourRecord: "Your record",
    botStreak: "Win streak",
    youWon: "You won!",
    botWon: "won this time",
    tieResult: "It's a tie!",
    rematch: "Rematch",
    nextOpponent: "Next opponent",
    streakBonus: "Streak bonus",
    searchFriendHint: "Search by name or type the exact account email",
    alreadyFriend: "Already friends",
    requestPending: "Request sent",
    foundByEmail: "found by email",
    noOneOnline: "No friends online right now. Come back later!",
    challengeBtn: "Challenge",
    inviteSent: "Challenge sent!",
    multiplayerDesc: "Play with friends on the same device!",
    addPlayer: "Add",
    removePlayer: "Remove",
    startMultiplayer: "Start Challenge!",
    playerTurn: "Turn of",
    passDeviceTitle: "Pass the device",
    passDeviceMsg: "Now it's the turn of",
    imReady: "I'm ready!",
    timesUp: "Time's up!",
    secondsLabel: "s",
    multiplayerResults: "Challenge Results",
    winner: "Winner",
    fastest: "Fastest",
    chooseNewGame: "Choose Game",
    sameGame: "Same Game",
    addAtLeast2Players: "Add at least 2 players",
    changeAvatar: "Change Avatar",
    tapToChange: "Tap to change",
    selectNewAvatar: "Choose your new avatar!",
    avatarChanged: "Avatar changed!"
  }
};

const avatars = ["👦", "👧", "🧒", "👶", "🦸", "🦹", "🧙", "🧚", "🦊", "🐱", "🐶", "🦁", "🐼", "🦄", "🐲", "🤖"];
const premiumAvatars = ["👑", "🐉", "🦋", "🌟", "🧞", "🦚", "🦭", "🐺", "🦖", "🎩", "🧜", "🦩"];

const BG_THEMES = {
  pink: "bg-gradient-to-br from-pink-500 via-rose-400 to-pink-300",
  blue: "bg-gradient-to-br from-blue-700 via-blue-500 to-indigo-400",
  lightblue: "bg-gradient-to-br from-sky-500 via-cyan-400 to-sky-300",
  lightgreen: "bg-gradient-to-br from-green-500 via-emerald-400 to-lime-300",
  // Premium-exclusive themes
  aurora: "bg-gradient-to-br from-fuchsia-600 via-purple-500 to-cyan-400",
  tramonto: "bg-gradient-to-br from-orange-500 via-pink-500 to-purple-600",
  galassia: "bg-gradient-to-br from-indigo-900 via-purple-800 to-fuchsia-700",
  oceano: "bg-gradient-to-br from-cyan-600 via-blue-600 to-indigo-700",
  foresta: "bg-gradient-to-br from-emerald-700 via-green-600 to-teal-500",
  lava: "bg-gradient-to-br from-red-700 via-orange-600 to-amber-500",
};

const PREMIUM_THEME_PREVIEW = [
  { key: "aurora", label_it: "Aurora", label_en: "Aurora", cls: "from-fuchsia-600 via-purple-500 to-cyan-400" },
  { key: "tramonto", label_it: "Tramonto", label_en: "Sunset", cls: "from-orange-500 via-pink-500 to-purple-600" },
  { key: "galassia", label_it: "Galassia", label_en: "Galaxy", cls: "from-indigo-900 via-purple-800 to-fuchsia-700" },
  { key: "oceano", label_it: "Oceano", label_en: "Ocean", cls: "from-cyan-600 via-blue-600 to-indigo-700" },
  { key: "foresta", label_it: "Foresta", label_en: "Forest", cls: "from-emerald-700 via-green-600 to-teal-500" },
  { key: "lava", label_it: "Lava", label_en: "Lava", cls: "from-red-700 via-orange-600 to-amber-500" },
];

const BG_PREVIEW = [
  { key: "pink", label_it: "Rosa", label_en: "Pink", cls: "from-pink-500 via-rose-400 to-pink-300" },
  { key: "blue", label_it: "Blu", label_en: "Blue", cls: "from-blue-700 via-blue-500 to-indigo-400" },
  { key: "lightblue", label_it: "Azzurro", label_en: "Sky Blue", cls: "from-sky-500 via-cyan-400 to-sky-300" },
  { key: "lightgreen", label_it: "Verde Chiaro", label_en: "Light Green", cls: "from-green-500 via-emerald-400 to-lime-300" },
];

const isPro = (c) => !!(c?.pro_until && new Date(c.pro_until) > new Date());
const isPremium = (c) => !!(c?.is_premium && (!c?.premium_until || new Date(c.premium_until) > new Date()));

const THEME_STYLES = {
  ocean: "bg-gradient-to-br from-[#0077be] via-[#0096d6] to-[#00c2e8]",
  forest: "bg-gradient-to-br from-[#1e7a1e] via-[#28a428] to-[#32cd32]",
  space: "bg-gradient-to-br from-[#1a1a2e] via-[#2d2d5f] to-[#4a0080]",
  candy: "bg-gradient-to-br from-[#ff69b4] via-[#ff4da6] to-[#ff1493]",
  rainbow: "bg-gradient-to-br from-red-500 via-yellow-400 to-purple-500",
};

const BADGE_DEFS = {
  primi_passi: { emoji: "🌱", it: "Primi Passi", en: "First Steps", descIt: "Completa la tua prima partita", descEn: "Complete your first game" },
  perfetto: { emoji: "💯", it: "Perfetto", en: "Perfect", descIt: "Fai 10 su 10 in una partita", descEn: "Score 10/10 in a game" },
  veterano: { emoji: "🎖️", it: "Veterano", en: "Veteran", descIt: "Completa 25 partite", descEn: "Complete 25 games" },
  campione_weekend: { emoji: "🏅", it: "Campione del Weekend", en: "Weekend Champion", descIt: "Vinci una medaglia d'oro nel weekend", descEn: "Win a gold medal on a weekend" },
  collezionista: { emoji: "💰", it: "Collezionista", en: "Collector", descIt: "Raggiungi 500 monete", descEn: "Reach 500 coins" },
  cervellone: { emoji: "🧠", it: "Cervellone", en: "Big Brain", descIt: "Raggiungi 80% di QI", descEn: "Reach 80% IQ score" },
  gladiatore: { emoji: "⚔️", it: "Gladiatore", en: "Gladiator", descIt: "Vinci 3 sfide 1v1", descEn: "Win 3 1v1 challenges" },
  costante: { emoji: "🔥", it: "Costante", en: "Consistent", descIt: "Gioca 7 giorni di fila", descEn: "Play 7 days in a row" },
};

function App() {
  const [lang, setLang] = useState("it");
  const [children, setChildren] = useState([]);
  const [selectedChild, setSelectedChild] = useState(null);
  const [authReady, setAuthReady] = useState(false);
  const [view, setView] = useState("home"); // home, game, gameSelect, results, leaderboard, stats, competitions, shop, challenges, drawing, coloring, puzzle, oddOneOut, sequences, memory, riddles, gameCategory
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newChild, setNewChild] = useState({ name: "", age: 6, avatar: "👦" });
  const [gameSession, setGameSession] = useState(null);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [userAnswer, setUserAnswer] = useState("");
  const [feedback, setFeedback] = useState(null);
  const [questionStartTime, setQuestionStartTime] = useState(null);
  const [gameResults, setGameResults] = useState(null);
  const [leaderboard, setLeaderboard] = useState([]);
  const [stats, setStats] = useState(null);
  const [competitions, setCompetitions] = useState([]);
  const [selectedAgeGroup, setSelectedAgeGroup] = useState("6-7");
  const [loading, setLoading] = useState(false);
  // Game category state
  const [selectedCategory, setSelectedCategory] = useState(null); // math, creative, puzzle, brain
  // Shop state
  const [shopItems, setShopItems] = useState([]);
  const [shopCategory, setShopCategory] = useState("avatar");
  const [inventory, setInventory] = useState(null);
  const [shopMessage, setShopMessage] = useState(null);
  // Challenge state
  const [pendingChallenges, setPendingChallenges] = useState([]);
  const [activeChallenges, setActiveChallenges] = useState([]);
  const [onlinePlayers, setOnlinePlayers] = useState([]);
  const [virtualFriends, setVirtualFriends] = useState({ bots: [], streak: 0 });
  const [botMode, setBotMode] = useState(false);
  const [botChallenge, setBotChallenge] = useState(null);
  const [incomingInvites, setIncomingInvites] = useState([]);
  const [challengeHistory, setChallengeHistory] = useState([]);
  const [challengeTab, setChallengeTab] = useState("pending");
  const [currentChallenge, setCurrentChallenge] = useState(null);
  const [challengeMode, setChallengeMode] = useState(false);
  const [challengeAnswers, setChallengeAnswers] = useState([]); // Track challenge answers
  const [challengeStartTime, setChallengeStartTime] = useState(null); // Track total time
  // Tournaments state
  const [tournaments, setTournaments] = useState([]);
  const [selectedTournament, setSelectedTournament] = useState(null);
  const [tournamentLeaderboard, setTournamentLeaderboard] = useState([]);
  const [myTournamentEntry, setMyTournamentEntry] = useState(null);
  // Friends state
  const [friendsList, setFriendsList] = useState([]);
  const [friendRequests, setFriendRequests] = useState([]);
  const [searchResults, setSearchResults] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [friendsTab, setFriendsTab] = useState("list"); // list, requests, search
  // Groups state
  const [myGroups, setMyGroups] = useState([]);
  const [openGroups, setOpenGroups] = useState([]);
  const [groupInvites, setGroupInvites] = useState([]);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [showCreateGroup, setShowCreateGroup] = useState(false);
  const [newGroup, setNewGroup] = useState({ name: "", description: "", avatar: "👥", group_type: "open" });
  // Sound and animation state
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [showScoreAnimation, setShowScoreAnimation] = useState(false);
  const [animatedScore, setAnimatedScore] = useState(0);
  const [showConfetti, setShowConfetti] = useState(false);
  // Inactivity penalty state
  const [activityStatus, setActivityStatus] = useState(null);
  const [showPenaltyWarning, setShowPenaltyWarning] = useState(false);
  // Multiplayer local state
  const [multiplayerMode, setMultiplayerMode] = useState(false);
  const [multiplayerPlayers, setMultiplayerPlayers] = useState([]);
  const [multiplayerQuestions, setMultiplayerQuestions] = useState([]);
  const [currentPlayerIndex, setCurrentPlayerIndex] = useState(0);
  const [multiplayerResults, setMultiplayerResults] = useState([]);
  const [showMultiplayerSetup, setShowMultiplayerSetup] = useState(false);
  const [showGuestGate, setShowGuestGate] = useState(false);
  const [showUpgrade, setShowUpgrade] = useState(false);
  const [dismissExpiry, setDismissExpiry] = useState(false);
  const [premiumPlan, setPremiumPlan] = useState("yearly");
  const [previewTheme, setPreviewTheme] = useState(null);
  const [missionsCollapsed, setMissionsCollapsed] = useState(false);
  const [upgradeForm, setUpgradeForm] = useState({ name: "", password: "", age: 8 });
  const [upgradeError, setUpgradeError] = useState("");
  const [upgradeLoading, setUpgradeLoading] = useState(false);
  const [timeLeft, setTimeLeft] = useState(15);
  const [showPassDevice, setShowPassDevice] = useState(false);
  const [pendingNextPlayer, setPendingNextPlayer] = useState(null);
  const timeoutArmedRef = useRef(false);
  const [newPlayerName, setNewPlayerName] = useState("");
  // Avatar change modal state
  const [showAvatarModal, setShowAvatarModal] = useState(false);
  // Settings & personalization state
  const [settingsForm, setSettingsForm] = useState({ name: "", email: "" });
  const [settingsSaved, setSettingsSaved] = useState(false);
  const [deleteStep, setDeleteStep] = useState(0);
  // Activity feed & badges state
  const [activities, setActivities] = useState([]);
  const [newBadges, setNewBadges] = useState([]);
  // Challenge link state
  const [linkChallengeId, setLinkChallengeId] = useState(null);
  const [linkChallenge, setLinkChallenge] = useState(null);
  const [copiedId, setCopiedId] = useState(null);
  const langExplicit = useRef(false);
  // Notifications, payments, tournaments extra state
  const [notifications, setNotifications] = useState(null);
  const [paymentResult, setPaymentResult] = useState(null);
  const [paymentsConfig, setPaymentsConfig] = useState(null);
  const [premiumError, setPremiumError] = useState(null);
  const [proError, setProError] = useState(false);
  const [tournamentLbTab, setTournamentLbTab] = useState("players");
  const [groupTournamentLb, setGroupTournamentLb] = useState([]);
  const [showGroupPicker, setShowGroupPicker] = useState(null);
  const [groupPickerLoading, setGroupPickerLoading] = useState(false);
  const [groupJoinMsg, setGroupJoinMsg] = useState(null);
  // Parents & powerups state
  const [parentData, setParentData] = useState([]);
  const [gamePowerups, setGamePowerups] = useState({ hint: 0, skip: 0, double_coins: 0 });
  const [doubleCoinsActive, setDoubleCoinsActive] = useState(false);
  const [hintAnswer, setHintAnswer] = useState(null);
  // Missions, PIN, weekly report
  const [missions, setMissions] = useState([]);
  const [pinModal, setPinModal] = useState(null);
  const [weeklyReport, setWeeklyReport] = useState(null);
  const [lbTab, setLbTab] = useState("coins");
  // Engagement pack
  const [combo, setCombo] = useState(0);
  const [blitzTimeLeft, setBlitzTimeLeft] = useState(60);
  const [blitzCorrect, setBlitzCorrect] = useState(0);
  const blitzEndedRef = useRef(false);
  const [levelUp, setLevelUp] = useState(null);
  const [chestAvailable, setChestAvailable] = useState(false);
  const [chestOpen, setChestOpen] = useState(false);
  const chestPromptedRef = useRef(false);
  const [weeklyRank, setWeeklyRank] = useState(null);
  const [weeklyLb, setWeeklyLb] = useState(null);
  const [spinStatus, setSpinStatus] = useState(null);
  const [missionsLb, setMissionsLb] = useState([]);
  const [reportEmail, setReportEmail] = useState("");
  const [emailStatus, setEmailStatus] = useState(null);
  const [alertStatus, setAlertStatus] = useState(null);
  const [alertSending, setAlertSending] = useState(false);

  // Initialize sound manager
  useEffect(() => {
    soundManager.init();
    soundManager.setEnabled(soundEnabled);
  }, [soundEnabled]);

  // Play sound helper
  const playSound = useCallback((type) => {
    if (!soundEnabled) return;
    switch(type) {
      case "correct": soundManager.playCorrect(); break;
      case "wrong": soundManager.playWrong(); break;
      case "victory": soundManager.playVictory(); break;
      case "coins": soundManager.playCoins(); break;
      case "medal": soundManager.playMedal(); break;
      case "click": soundManager.playClick(); break;
      case "complete": soundManager.playComplete(); break;
      case "levelup": soundManager.playLevelUp(); break;
      case "warning": soundManager.playWarning(); break;
      default: break;
    }
  }, [soundEnabled]);

  // Check activity status on profile select
  const checkActivityStatus = async (childId) => {
    try {
      const res = await axios.get(`${API}/children/${childId}/check-activity`);
      setActivityStatus(res.data);
      
      if (res.data.penalty) {
        playSound("warning");
        setShowPenaltyWarning(true);
      } else if (res.data.warning) {
        setShowPenaltyWarning(true);
      }
    } catch (e) {
      console.error("Error checking activity:", e);
    }
  };

  // Animated score counter
  const animateScore = (targetScore, duration = 1500) => {
    setShowScoreAnimation(true);
    setAnimatedScore(0);
    
    const startTime = Date.now();
    const animate = () => {
      const elapsed = Date.now() - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const easeOut = 1 - Math.pow(1 - progress, 3);
      setAnimatedScore(Math.floor(targetScore * easeOut));
      
      if (progress < 1) {
        requestAnimationFrame(animate);
      } else {
        setAnimatedScore(targetScore);
        setTimeout(() => setShowScoreAnimation(false), 500);
      }
    };
    requestAnimationFrame(animate);
  };

  // Confetti effect
  const triggerConfetti = () => {
    setShowConfetti(true);
    playSound("victory");
    setTimeout(() => setShowConfetti(false), 3000);
  };

  const t = translations[lang];

  const bgClass = (fallback) => {
    if (selectedChild?.night_mode) return "bg-gradient-to-br from-slate-900 via-slate-800 to-indigo-950";
    if (selectedChild && (isPro(selectedChild) || isPremium(selectedChild)) && BG_THEMES[selectedChild.bg_color]) return BG_THEMES[selectedChild.bg_color];
    if (selectedChild && THEME_STYLES[selectedChild.theme]) return THEME_STYLES[selectedChild.theme];
    return fallback;
  };

  const toggleNightMode = async () => {
    try {
      const res = await axios.put(`${API}/children/${selectedChild.id}/night-mode?enabled=${!selectedChild.night_mode}`);
      setSelectedChild(res.data.child);
      setChildren(prev => prev.map(c => c.id === res.data.child.id ? res.data.child : c));
      playSound("click");
    } catch (e) {
      console.error("Error toggling night mode:", e);
    }
  };

  const timeAgo = (iso) => {
    const diff = Date.now() - new Date(iso).getTime();
    const m = Math.floor(diff / 60000);
    if (m < 1) return t.justNow;
    if (m < 60) return `${m} ${t.minutesAgo}`;
    const h = Math.floor(m / 60);
    if (h < 24) return `${h} ${t.hoursAgo}`;
    return `${Math.floor(h / 24)} ${t.daysAgo}`;
  };

  const fetchActivities = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/activities?limit=15`);
      setActivities(res.data);
    } catch (e) {
      console.error("Error fetching activities:", e);
    }
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const cid = params.get("challenge");
    if (cid) {
      setLinkChallengeId(cid);
      window.history.replaceState({}, "", window.location.pathname);
    }
    const payment = params.get("payment");
    const sess = params.get("session_id");
    const ptype = params.get("type");
    if (payment === "success" && sess) {
      setPaymentResult({ status: "processing", type: ptype });
      axios.post(`${API}/payments/fulfill/${sess}`).then((res) => {
        setPaymentResult({ status: res.data.status, type: res.data.type || ptype });
        fetchChildren();
      }).catch(() => setPaymentResult({ status: "error", type: ptype }));
      window.history.replaceState({}, "", "/");
    } else if (payment === "cancelled") {
      setPaymentResult({ status: "cancelled" });
      window.history.replaceState({}, "", "/");
    } else if (window.location.pathname === "/dashboard") {
      window.history.replaceState({}, "", "/");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (view === "gameSelect") fetchActivities();
  }, [view, fetchActivities]);

  // After a successful payment, refresh the current profile so premium/pro unlock immediately
  useEffect(() => {
    if (paymentResult?.status === "paid" && selectedChild?.id) {
      axios.get(`${API}/children/${selectedChild.id}`)
        .then((r) => setSelectedChild(r.data))
        .catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paymentResult, selectedChild?.id]);

  const fetchNotifications = useCallback(async () => {
    if (!selectedChild) return;
    try {
      const res = await axios.get(`${API}/children/${selectedChild.id}/notifications`);
      setNotifications(res.data);
    } catch (e) {
      console.error("Error fetching notifications:", e);
    }
  }, [selectedChild]);

  useEffect(() => {
    if (view === "gameSelect") fetchNotifications();
  }, [view, fetchNotifications]);

  useEffect(() => {
    const loadLinkChallenge = async () => {
      if (!linkChallengeId || !selectedChild) return;
      try {
        const res = await axios.get(`${API}/challenges/${linkChallengeId}`);
        if (res.data.status === "pending" && res.data.challenger_id !== selectedChild.id) {
          setLinkChallenge(res.data);
        }
      } catch (e) {
        console.error("Error loading link challenge:", e);
      }
      setLinkChallengeId(null);
    };
    loadLinkChallenge();
  }, [linkChallengeId, selectedChild]);

  const saveSettings = async () => {
    try {
      setLoading(true);
      const res = await axios.put(`${API}/children/${selectedChild.id}/settings`, {
        name: settingsForm.name,
        email: settingsForm.email
      });
      setSelectedChild(res.data.child);
      setChildren(prev => prev.map(c => c.id === res.data.child.id ? res.data.child : c));
      setSettingsSaved(true);
      setTimeout(() => setSettingsSaved(false), 2000);
    } catch (e) {
      console.error("Error saving settings:", e);
    } finally {
      setLoading(false);
    }
  };

  const savePreferences = async (prefs) => {
    try {
      const res = await axios.put(`${API}/children/${selectedChild.id}/preferences`, prefs);
      setSelectedChild(res.data.child);
      setChildren(prev => prev.map(c => c.id === res.data.child.id ? res.data.child : c));
      setProError(false);
      playSound("click");
    } catch (e) {
      if (e.response?.status === 402) {
        setProError(true);
        playSound("warning");
      } else {
        console.error("Error saving preferences:", e);
      }
    }
  };

  const buyPro = async () => {
    try {
      setLoading(true);
      const res = await axios.post(`${API}/payments/create-pro-checkout?child_id=${selectedChild.id}`);
      window.location.href = res.data.checkout_url;
    } catch (e) {
      console.error("Error creating pro checkout:", e);
      setLoading(false);
    }
  };

  const buyPremium = async (plan = "monthly") => {
    try {
      setLoading(true);
      setPremiumError(null);
      const res = await axios.post(`${API}/payments/create-premium-checkout?plan=${plan}`);
      window.location.href = res.data.checkout_url;
    } catch (e) {
      console.error("Error creating premium checkout:", e);
      setPremiumError(e.response?.data?.detail || (lang === "it" ? "Impossibile avviare il pagamento. Riprova." : "Could not start the payment. Please retry."));
      setLoading(false);
    }
  };

  useEffect(() => {
    if (view !== "premium") return;
    axios.get(`${API}/payments/config`).then((r) => setPaymentsConfig(r.data)).catch(() => {});
  }, [view]);

  const deleteAccount = async () => {
    try {
      setLoading(true);
      await axios.delete(`${API}/children/${selectedChild.id}`);
      setChildren(prev => prev.filter(c => c.id !== selectedChild.id));
      setDeleteStep(0);
      localStorage.removeItem("celest_token");
      delete axios.defaults.headers.common["Authorization"];
      setSelectedChild(null);
      setView("home");
    } catch (e) {
      console.error("Error deleting account:", e);
    } finally {
      setLoading(false);
    }
  };

  const copyChallengeLink = (challengeId) => {
    const link = `${window.location.origin}?challenge=${challengeId}`;
    navigator.clipboard.writeText(link).then(() => {
      setCopiedId(challengeId);
      setTimeout(() => setCopiedId(null), 2000);
    }).catch(() => {
      window.prompt("Link:", link);
    });
  };

  const cancelChallenge = async (challengeId) => {
    try {
      await axios.delete(`${API}/challenges/${challengeId}?child_id=${selectedChild.id}`);
      setActiveChallenges(prev => prev.filter(c => c.id !== challengeId));
    } catch (e) {
      console.error("Error cancelling challenge:", e);
    }
  };

  const fetchParentOverview = async () => {
    try {
      const res = await axios.get(`${API}/parent/overview`);
      setParentData(res.data);
    } catch (e) {
      console.error("Error fetching parent overview:", e);
    }
  };

  const openParentsArea = async () => {
    try {
      const st = await axios.get(`${API}/parent/pin-status`);
      setPinModal({ mode: st.data.set ? "verify" : "set", value: "", error: false });
    } catch (e) {
      console.error("Error checking pin status:", e);
    }
  };

  const submitPin = async () => {
    try {
      if (pinModal.mode === "set") {
        await axios.post(`${API}/parent/set-pin`, { pin: pinModal.value });
      } else {
        const res = await axios.post(`${API}/parent/verify-pin`, { pin: pinModal.value });
        if (!res.data.valid) {
          setPinModal({ ...pinModal, value: "", error: true });
          playSound("wrong");
          return;
        }
      }
      setPinModal(null);
      setWeeklyReport(null);
      fetchParentOverview();
      setView("parents");
    } catch (e) {
      setPinModal({ ...pinModal, value: "", error: true });
    }
  };

  const fetchWeeklyReport = async () => {
    try {
      const res = await axios.get(`${API}/parent/weekly-report`);
      setWeeklyReport(res.data);
    } catch (e) {
      console.error("Error fetching weekly report:", e);
    }
  };

  const fetchMissionsLb = async () => {
    try {
      const res = await axios.get(`${API}/leaderboard/missions`);
      setMissionsLb(res.data);
    } catch (e) {
      console.error("Error fetching missions leaderboard:", e);
    }
  };

  const sendReportEmail = async () => {
    setEmailStatus(null);
    try {
      await axios.post(`${API}/parent/send-weekly-report`, { email: reportEmail });
      setEmailStatus({ ok: true, text: t.emailSentOk });
    } catch (e) {
      if (e.response?.status === 503) setEmailStatus({ ok: false, text: t.emailNeedKey });
      else setEmailStatus({ ok: false, text: `${t.emailFailed}: ${e.response?.data?.detail || ""}` });
    }
  };

  const sendTestAlert = async () => {
    setAlertStatus(null);
    setAlertSending(true);
    try {
      const res = await axios.post(`${API}/parent/test-alert`);
      setAlertStatus({ ok: true, text: (lang === "it" ? "Email di prova inviata a " : "Test email sent to ") + (res.data?.sent_to || "") });
    } catch (e) {
      setAlertStatus({ ok: false, text: e.response?.data?.detail || (lang === "it" ? "Invio non riuscito" : "Send failed") });
    } finally {
      setAlertSending(false);
    }
  };

  const shareVictoryCard = async () => {
    const c = selectedChild;
    if (!c) return;
    const canvas = document.createElement("canvas");
    canvas.width = 800;
    canvas.height = 800;
    const ctx = canvas.getContext("2d");
    const grad = ctx.createLinearGradient(0, 0, 800, 800);
    grad.addColorStop(0, "#7c3aed");
    grad.addColorStop(1, "#ec4899");
    ctx.fillStyle = grad;
    ctx.fillRect(0, 0, 800, 800);
    ctx.fillStyle = "rgba(255,255,255,0.95)";
    if (ctx.roundRect) {
      ctx.beginPath();
      ctx.roundRect(60, 60, 680, 680, 40);
      ctx.fill();
    } else {
      ctx.fillRect(60, 60, 680, 680);
    }
    ctx.textAlign = "center";
    ctx.fillStyle = "#7c3aed";
    ctx.font = "bold 44px Arial";
    ctx.fillText("🌟 CelestApp", 400, 145);
    ctx.font = "110px Arial";
    ctx.fillText(c.avatar, 400, 280);
    ctx.fillStyle = "#1f2937";
    ctx.font = "bold 56px Arial";
    ctx.fillText(c.name, 400, 365);
    if ((c.streak_days || 0) >= 2) {
      ctx.fillStyle = "#ea580c";
      ctx.font = "bold 36px Arial";
      ctx.fillText(`🔥 ${c.streak_days} ${t.streakLabel}`, 400, 425);
    }
    const badgeEmojis = (c.badges || []).map((b) => BADGE_DEFS[b]?.emoji || "🏅").join(" ");
    ctx.font = "46px Arial";
    ctx.fillText(badgeEmojis || "🌱", 400, 510);
    ctx.fillStyle = "#6b7280";
    ctx.font = "bold 28px Arial";
    ctx.fillText(`${(c.badges || []).length} badge · 🏆 ${c.trophies || 0} · 💰 ${c.coins}`, 400, 570);
    ctx.fillStyle = "#ca8a04";
    ctx.font = "bold 34px Arial";
    ctx.fillText(lang === "it" ? "Guarda le mie vittorie!" : "Look at my wins!", 400, 640);
    ctx.fillStyle = "#9ca3af";
    ctx.font = "24px Arial";
    ctx.fillText(lang === "it" ? "Gioca anche tu su CelestApp 💜" : "Play CelestApp too 💜", 400, 700);
    canvas.toBlob(async (blob) => {
      if (!blob) return;
      const file = new File([blob], "celestapp-vittorie.png", { type: "image/png" });
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        try {
          await navigator.share({ files: [file], title: "CelestApp" });
          return;
        } catch (e) {
          if (e.name === "AbortError") return;
        }
      }
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "celestapp-vittorie.png";
      a.click();
      URL.revokeObjectURL(url);
    }, "image/png");
  };

  const fetchMissions = useCallback(async () => {
    if (!selectedChild) return;
    try {
      const res = await axios.get(`${API}/children/${selectedChild.id}/missions`);
      setMissions(res.data.missions);
    } catch (e) {
      console.error("Error fetching missions:", e);
    }
  }, [selectedChild]);

  useEffect(() => {
    if (view === "gameSelect") fetchMissions();
  }, [view, fetchMissions]);

  // Engagement: weekly rank + daily chest on dashboard
  useEffect(() => {
    if (view !== "gameSelect" || !selectedChild?.id) return;
    const cid = selectedChild.id;
    axios.get(`${API}/children/${cid}/weekly-rank`).then(r => setWeeklyRank(r.data)).catch(() => {});
    axios.get(`${API}/children/${cid}/daily-chest`).then(r => {
      setChestAvailable(r.data.available);
      if (r.data.available && !chestPromptedRef.current) { chestPromptedRef.current = true; setChestOpen(true); }
    }).catch(() => {});
  }, [view, selectedChild?.id]);

  const refreshSelectedChild = async () => {
    const updated = await axios.get(`${API}/children/${selectedChild.id}`);
    setSelectedChild(updated.data);
    setChildren(prev => prev.map(c => c.id === updated.data.id ? updated.data : c));
    return updated.data;
  };

  const openDailyChest = async () => {
    try {
      const res = await axios.post(`${API}/children/${selectedChild.id}/daily-chest`);
      setChestAvailable(false);
      refreshSelectedChild();
      fetchActivities();
      return res.data;
    } catch (e) {
      setChestAvailable(false);
      return null;
    }
  };

  const spinWheel = async () => {
    try {
      const res = await axios.post(`${API}/children/${selectedChild.id}/spin`);
      setTimeout(refreshSelectedChild, 3300);
      return res.data;
    } catch (e) {
      return null;
    }
  };

  const fetchWeeklyLb = async () => {
    try {
      const res = await axios.get(`${API}/weekly-leaderboard?age_group=${selectedChild.age_group}`);
      setWeeklyLb(res.data);
    } catch (e) {
      console.error("Error fetching weekly leaderboard:", e);
    }
  };

  // Brain games / enigmi: server-side reward, then shared results screen
  const quickComplete = async (game, payload, backView) => {
    try {
      const res = await axios.post(`${API}/games/quick-complete`, { game, ...payload });
      setNewBadges(res.data.new_badges || []);
      setGameResults({ ...res.data, quick: true, backView, correct: res.data.score, total: res.data.total, medal_earned: res.data.total && res.data.score === res.data.total ? "gold" : null });
      if (res.data.leveled_up) setLevelUp({ level: res.data.level_after, bonus: res.data.level_bonus });
      await refreshSelectedChild();
      setView("results");
    } catch (e) {
      console.error("Error completing quick game:", e);
      setView("gameSelect");
    }
  };

  // Blitz countdown
  useEffect(() => {
    if (view !== "game" || gameSession?.mode !== "blitz" || !gameSession) return;
    if (blitzTimeLeft <= 0) {
      if (blitzEndedRef.current) return;
      blitzEndedRef.current = true;
      (async () => {
        try {
          const results = await axios.post(`${API}/games/${gameSession.session_id}/complete`);
          setGameResults(results.data);
          setNewBadges(results.data.new_badges || []);
          if (results.data.leveled_up) setLevelUp({ level: results.data.level_after, bonus: results.data.level_bonus });
          setView("results");
          await refreshSelectedChild();
        } catch (e) { console.error("Blitz complete error:", e); setView("gameSelect"); }
      })();
      return;
    }
    const t = setTimeout(() => setBlitzTimeLeft(s => s - 1), 1000);
    return () => clearTimeout(t);
  }, [view, gameSession, blitzTimeLeft]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (view === "results" && selectedChild?.id && gameResults?.coins_earned > 0) {
      axios.get(`${API}/children/${selectedChild.id}/spin-status`).then(r => setSpinStatus(r.data)).catch(() => setSpinStatus(null));
    }
  }, [view, selectedChild?.id, gameResults]);

  const claimMission = async (missionId) => {
    try {
      const res = await axios.post(`${API}/children/${selectedChild.id}/missions/claim`, { mission_id: missionId });
      playSound("coins");
      setSelectedChild(prev => ({ ...prev, coins: res.data.coins }));
      setChildren(prev => prev.map(c => c.id === selectedChild.id ? { ...c, coins: res.data.coins } : c));
      fetchMissions();
      fetchActivities();
    } catch (e) {
      console.error("Error claiming mission:", e);
    }
  };

  // Victory sounds on results
  useEffect(() => {
    if (view === "results" && gameResults) {
      playSound("victory");
      if (gameResults.medal_earned) setTimeout(() => playSound("medal"), 600);
      if (newBadges.length > 0) setTimeout(() => playSound("levelup"), 1200);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view]);

  const formatPlaytime = (ms) => {
    const mins = Math.floor((ms || 0) / 60000);
    if (mins < 60) return `${mins} min`;
    return `${Math.floor(mins / 60)}h ${mins % 60}m`;
  };

  const loadGamePowerups = async () => {
    try {
      const inv = await axios.get(`${API}/children/${selectedChild.id}/inventory`);
      const counts = { hint: 0, skip: 0, double_coins: 0 };
      (inv.data.inventory.powerups || []).forEach(p => {
        const type = p.data?.type;
        if (counts[type] !== undefined) counts[type] += p.owned_quantity || 0;
      });
      setGamePowerups(counts);
    } catch (e) {
      console.error("Error loading powerups:", e);
    }
    setDoubleCoinsActive(false);
    setHintAnswer(null);
  };

  const usePowerupHint = async () => {
    try {
      const res = await axios.post(`${API}/games/${gameSession.session_id}/use-hint?question_index=${currentQuestion}`);
      setHintAnswer(res.data.correct_answer);
      setGamePowerups(p => ({ ...p, hint: p.hint - 1 }));
      playSound("click");
      setTimeout(() => setHintAnswer(null), 4000);
    } catch (e) {
      console.error("Error using hint:", e);
    }
  };

  const usePowerupDouble = async () => {
    try {
      await axios.post(`${API}/games/${gameSession.session_id}/use-double-coins`);
      setGamePowerups(p => ({ ...p, double_coins: p.double_coins - 1 }));
      setDoubleCoinsActive(true);
      playSound("coins");
    } catch (e) {
      console.error("Error using double coins:", e);
    }
  };

  const usePowerupSkip = async () => {
    try {
      const res = await axios.post(`${API}/games/${gameSession.session_id}/use-skip?question_index=${currentQuestion}`);
      setGamePowerups(p => ({ ...p, skip: p.skip - 1 }));
      setFeedback({ is_correct: true, correct_answer: res.data.correct_answer });
      setHintAnswer(null);
      playSound("correct");
      setTimeout(async () => {
        if (currentQuestion < gameSession.questions.length - 1) {
          setCurrentQuestion(currentQuestion + 1);
          setUserAnswer("");
          setFeedback(null);
          setQuestionStartTime(Date.now());
        } else {
          const results = await axios.post(`${API}/games/${gameSession.session_id}/complete`);
          setGameResults(results.data);
          setNewBadges(results.data.new_badges || []);
          setView("results");
          const updatedChild = await axios.get(`${API}/children/${selectedChild.id}`);
          setSelectedChild(updatedChild.data);
          setChildren(prev => prev.map(c => c.id === selectedChild.id ? updatedChild.data : c));
        }
      }, 1200);
    } catch (e) {
      console.error("Error using skip:", e);
    }
  };

  const fetchChildren = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/children`);
      setChildren(res.data);
    } catch (e) {
      console.error("Error fetching children:", e);
    }
  }, []);

  useEffect(() => {
    // Auth bootstrap: restore session from stored JWT
    const token = localStorage.getItem("celest_token");
    if (!token) { setAuthReady(true); return; }
    axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
    axios.get(`${API}/auth/me`)
      .then((res) => {
        const child = res.data.child;
        setSelectedChild(child);
        setLang(child.language || "it");
        setView("gameSelect");
        fetchChildren();
      })
      .catch(() => {
        localStorage.removeItem("celest_token");
        delete axios.defaults.headers.common["Authorization"];
      })
      .finally(() => setAuthReady(true));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleAuth = (token, child) => {
    localStorage.setItem("celest_token", token);
    axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
    setSelectedChild(child);
    setLang(child.language || "it");
    setView("gameSelect");
    fetchChildren();
    checkActivityStatus(child.id);
    playSound("click");
  };

  const logout = () => {
    localStorage.removeItem("celest_token");
    delete axios.defaults.headers.common["Authorization"];
    setSelectedChild(null);
    setView("home");
  };

  const openUpgrade = () => {
    setUpgradeError("");
    setUpgradeForm({ name: "", password: "", age: selectedChild?.age || 8 });
    setShowGuestGate(false);
    setShowUpgrade(true);
  };

  const handleUpgrade = async () => {
    const name = (upgradeForm.name || "").trim();
    if (name.length < 2) { setUpgradeError(lang === "it" ? "Scegli un nome di almeno 2 caratteri" : "Choose a name of at least 2 characters"); return; }
    if ((upgradeForm.password || "").length < 4) { setUpgradeError(lang === "it" ? "La password deve avere almeno 4 caratteri" : "Password must be at least 4 characters"); return; }
    try {
      setUpgradeLoading(true);
      setUpgradeError("");
      const { data } = await axios.post(`${API}/auth/upgrade`, {
        name,
        password: upgradeForm.password,
        age: Number(upgradeForm.age) || 8,
        avatar: selectedChild?.avatar,
        language: lang,
      });
      setShowUpgrade(false);
      handleAuth(data.token, data.child);
    } catch (e) {
      const d = e.response?.data?.detail;
      setUpgradeError(typeof d === "string" ? d : (lang === "it" ? "Qualcosa è andato storto, riprova" : "Something went wrong, try again"));
    } finally {
      setUpgradeLoading(false);
    }
  };

  const createChild = async () => {
    try {
      setLoading(true);
      const res = await axios.post(`${API}/children`, {
        name: newChild.name,
        age: newChild.age,
        avatar: newChild.avatar,
        language: lang
      });
      setChildren([...children, res.data]);
      setShowCreateModal(false);
      setNewChild({ name: "", age: 6, avatar: "👦" });
    } catch (e) {
      console.error("Error creating child:", e);
    } finally {
      setLoading(false);
    }
  };

  const selectChild = (child) => {
    setSelectedChild(child);
    if (langExplicit.current) {
      axios.put(`${API}/children/${child.id}/language?language=${lang}`).catch(() => {});
      langExplicit.current = false;
    } else {
      setLang(child.language || "it");
    }
    setView("gameSelect");
    // Check activity status
    checkActivityStatus(child.id);
    playSound("click");
  };

  const startGame = async (gameType, mode = "normal") => {
    setNewBadges([]);
    setCombo(0);
    setLevelUp(null);
    setBotMode(false);
    loadGamePowerups();
    try {
      setLoading(true);
      const res = await axios.post(`${API}/games/start?child_id=${selectedChild.id}&game_type=${gameType}&mode=${mode}`);
      setGameSession(res.data);
      setCurrentQuestion(0);
      setQuestionStartTime(Date.now());
      blitzEndedRef.current = false;
      setBlitzTimeLeft(60);
      setBlitzCorrect(0);
      setView("game");
      setFeedback(null);
      setUserAnswer("");
    } catch (e) {
      console.error("Error starting game:", e);
    } finally {
      setLoading(false);
    }
  };

  const submitAnswer = async (arg = false) => {
    const timedOut = arg === true;
    if (!gameSession) return;
    if (!timedOut && userAnswer === "") return;
    timeoutArmedRef.current = false;
    
    const timeTaken = timedOut ? 15000 : (Date.now() - questionStartTime);
    const answer = timedOut ? null : parseInt(userAnswer);
    
    try {
      let isCorrect = false;
      let correctAnswer = 0;
      
      if (botMode && botChallenge) {
        await submitBotAnswer(answer, timeTaken, timedOut);
        return;
      }
      // For regular games
      if (!challengeMode) {
        const res = await axios.post(`${API}/games/${gameSession.session_id}/answer`, {
          question_index: currentQuestion,
          answer: answer,
          time_ms: timeTaken
        });
        
        isCorrect = res.data.is_correct;
        correctAnswer = res.data.correct_answer;
        setCombo(res.data.combo || 0);
        if (isCorrect && gameSession.mode === "blitz") setBlitzCorrect(c => c + 1);
        if (isCorrect && res.data.combo >= 3) playSound(res.data.combo >= 5 ? "levelup" : "medal");
        
        setFeedback({
          correct: isCorrect,
          correctAnswer: correctAnswer
        });
      } else {
        // For challenges - validate answer with server (skip on timeout)
        if (timedOut) {
          const q = gameSession.questions[currentQuestion];
          switch (q.operator) {
            case "+": correctAnswer = q.a + q.b; break;
            case "-": correctAnswer = q.a - q.b; break;
            case "×": correctAnswer = q.a * q.b; break;
            case "÷": correctAnswer = q.a / q.b; break;
            default: correctAnswer = q.a + q.b;
          }
          isCorrect = false;
        } else {
          const res = await axios.post(
            `${API}/challenges/${currentChallenge.id}/answer?child_id=${selectedChild.id}`,
            {
              question_index: currentQuestion,
              answer: answer,
              time_ms: timeTaken
            }
          );
          isCorrect = res.data.is_correct;
          correctAnswer = res.data.correct_answer;
        }
        
        // Track this answer immediately with full list
        const newAnswers = [...challengeAnswers, {
          question_index: currentQuestion,
          is_correct: isCorrect,
          time_ms: timeTaken
        }];
        setChallengeAnswers(newAnswers);
        
        setFeedback({
          correct: isCorrect,
          correctAnswer: correctAnswer,
          timedOut: timedOut
        });
        
        // Use newAnswers directly for the final calculation (since state update is async)
        setTimeout(async () => {
          if (currentQuestion < gameSession.questions.length - 1) {
            setCurrentQuestion(currentQuestion + 1);
            setUserAnswer("");
            setFeedback(null);
            setQuestionStartTime(Date.now());
          } else {
            // Challenge complete - use newAnswers which includes all answers
            const totalAnswerTime = newAnswers.reduce((sum, a) => sum + a.time_ms, 0);
            const correctCount = newAnswers.filter(a => a.is_correct).length;
            
            const challengeResult = await axios.post(`${API}/challenges/${currentChallenge.id}/submit`, {
              child_id: selectedChild.id,
              score: correctCount,
              time_ms: totalAnswerTime
            });
            
            setGameResults({
              correct: correctCount,
              total: 10,
              accuracy: correctCount * 10,
              coins_earned: challengeResult.data.completed && challengeResult.data.winner_id === selectedChild.id ? 50 : 0,
              medal_earned: null,
              iq_score: 0,
              avg_time_seconds: (totalAnswerTime / 10000).toFixed(2),
              challenge_result: challengeResult.data
            });
            
            setChallengeMode(false);
            setCurrentChallenge(null);
            setChallengeAnswers([]);
            setChallengeStartTime(null);
            setView("results");
            
            // Refresh child data
            const updatedChild = await axios.get(`${API}/children/${selectedChild.id}`);
            setSelectedChild(updatedChild.data);
            // Update children list too
            setChildren(prev => prev.map(c => c.id === selectedChild.id ? updatedChild.data : c));
          }
        }, 1500);
        return; // Return early for challenge mode
      }
      
      // Regular game flow continues here
      const isBlitz = gameSession.mode === "blitz";
      setTimeout(async () => {
        if (isBlitz && blitzEndedRef.current) return;
        if (currentQuestion < gameSession.questions.length - 1) {
          setCurrentQuestion(currentQuestion + 1);
          setUserAnswer("");
          setFeedback(null);
          setQuestionStartTime(Date.now());
        } else {
          if (isBlitz) blitzEndedRef.current = true;
          // Game complete
          const results = await axios.post(`${API}/games/${gameSession.session_id}/complete`);
          setGameResults(results.data);
          setNewBadges(results.data.new_badges || []);
          if (results.data.leveled_up) setLevelUp({ level: results.data.level_after, bonus: results.data.level_bonus });
          setView("results");
          // Refresh child data
          const updatedChild = await axios.get(`${API}/children/${selectedChild.id}`);
          setSelectedChild(updatedChild.data);
          // Update children list too
          setChildren(prev => prev.map(c => c.id === selectedChild.id ? updatedChild.data : c));
        }
      }, isBlitz ? 450 : 1500);
    } catch (e) {
      console.error("Error submitting answer:", e);
    }
  };

  const fetchLeaderboard = async (ageGroup) => {
    try {
      const band = selectedChild?.age_band || ageGroup;
      const res = await axios.get(`${API}/leaderboard/band/${encodeURIComponent(band)}`);
      setLeaderboard(res.data);
      setSelectedAgeGroup(ageGroup);
    } catch (e) {
      console.error("Error fetching leaderboard:", e);
    }
  };

  const fetchStats = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/children/${selectedChild.id}/stats`);
      setStats(res.data);
      setView("stats");
    } catch (e) {
      console.error("Error fetching stats:", e);
    } finally {
      setLoading(false);
    }
  };

  const fetchCompetitions = async () => {
    try {
      const res = await axios.get(`${API}/competitions/active`);
      setCompetitions(res.data);
    } catch (e) {
      console.error("Error fetching competitions:", e);
    }
  };

  const joinCompetition = async (competitionId) => {
    try {
      await axios.post(`${API}/competitions/${competitionId}/join?child_id=${selectedChild.id}`);
      fetchCompetitions();
    } catch (e) {
      console.error("Error joining competition:", e);
    }
  };

  // Multiplayer local functions
  const addMultiplayerPlayer = () => {
    if (newPlayerName.trim() && multiplayerPlayers.length < 4) {
      setMultiplayerPlayers([...multiplayerPlayers, {
        name: newPlayerName.trim(),
        avatar: avatars[Math.floor(Math.random() * avatars.length)],
        score: 0,
        time_ms: 0,
        completed: false
      }]);
      setNewPlayerName("");
    }
  };

  const removeMultiplayerPlayer = (index) => {
    setMultiplayerPlayers(multiplayerPlayers.filter((_, i) => i !== index));
  };

  const startMultiplayerGame = async (gameType) => {
    if (multiplayerPlayers.length < 2) return;
    
    try {
      setLoading(true);
      // Fetch a SEPARATE set of questions for EACH player so no one sees the same
      // questions as another player in the local (pass-and-play) challenge.
      const sessions = await Promise.all(
        multiplayerPlayers.map(() =>
          axios.post(`${API}/games/start?child_id=${selectedChild.id}&game_type=${gameType}`)
        )
      );
      const perPlayerQuestions = sessions.map((r) => r.data.questions);
      setMultiplayerQuestions(perPlayerQuestions);
      // gameSession holds the CURRENT player's questions (player 0 to start)
      setGameSession({ ...sessions[0].data, questions: perPlayerQuestions[0] });
      setMultiplayerMode(true);
      setCurrentPlayerIndex(0);
      setMultiplayerResults([]);
      setShowPassDevice(false);
      setPendingNextPlayer(null);
      setTimeLeft(15);
      // Reset all player scores/times for new game
      const resetPlayers = multiplayerPlayers.map(p => ({
        ...p,
        score: 0,
        time_ms: 0,
        completed: false
      }));
      setMultiplayerPlayers(resetPlayers);
      setCurrentQuestion(0);
      setQuestionStartTime(Date.now());
      setView("game");
      setFeedback(null);
      setUserAnswer("");
      setShowMultiplayerSetup(false);
    } catch (e) {
      console.error("Error starting multiplayer game:", e);
    } finally {
      setLoading(false);
    }
  };

  // Tournament functions
  const fetchTournaments = async () => {
    try {
      const res = await axios.get(`${API}/tournaments?age_group=${selectedChild?.age_group || "6-7"}`);
      setTournaments(res.data);
    } catch (e) {
      console.error("Error fetching tournaments:", e);
    }
  };

  const fetchTournamentLeaderboard = async (tournamentId) => {
    try {
      const res = await axios.get(`${API}/tournaments/${tournamentId}/leaderboard`);
      setTournamentLeaderboard(res.data);
    } catch (e) {
      console.error("Error fetching leaderboard:", e);
    }
  };

  const fetchMyTournamentEntry = async (tournamentId) => {
    try {
      const res = await axios.get(`${API}/tournaments/${tournamentId}/my-entry/${selectedChild.id}`);
      setMyTournamentEntry(res.data);
    } catch (e) {
      console.error("Error fetching entry:", e);
    }
  };

  const joinTournament = async (tournament) => {
    try {
      setLoading(true);
      const res = await axios.post(`${API}/tournaments/${tournament.id}/join`, {
        child_id: selectedChild.id
      });
      if (res.data.requires_payment) {
        const pay = await axios.post(`${API}/payments/create-checkout-session?tournament_id=${tournament.id}&child_id=${selectedChild.id}`);
        window.location.href = pay.data.checkout_url;
        return;
      }
      
      fetchTournaments();
      fetchMyTournamentEntry(tournament.id);
    } catch (e) {
      console.error("Error joining tournament:", e);
    } finally {
      setLoading(false);
    }
  };

  const openTournamentLeaderboard = async (tournament) => {
    setSelectedTournament(tournament);
    setTournamentLbTab("players");
    fetchTournamentLeaderboard(tournament.id);
    try {
      const res = await axios.get(`${API}/tournaments/${tournament.id}/group-leaderboard`);
      setGroupTournamentLb(res.data);
    } catch (e) {
      console.error("Error fetching group leaderboard:", e);
    }
  };

  const joinTournamentAsGroup = async (tournamentId, groupId) => {
    try {
      setLoading(true);
      await axios.post(`${API}/tournaments/${tournamentId}/join-as-group?group_id=${groupId}`);
      setGroupJoinMsg({ ok: true, text: t.registeredOk });
      setShowGroupPicker(null);
      setTimeout(() => setGroupJoinMsg(null), 3000);
    } catch (e) {
      setGroupJoinMsg({ ok: false, text: e.response?.data?.detail || "Errore" });
      setTimeout(() => setGroupJoinMsg(null), 3000);
    } finally {
      setLoading(false);
    }
  };

  // Friends functions
  const fetchFriends = async () => {
    try {
      const res = await axios.get(`${API}/friends/${selectedChild.id}`);
      setFriendsList(res.data);
    } catch (e) {
      console.error("Error fetching friends:", e);
    }
  };

  const fetchFriendRequests = async () => {
    try {
      const res = await axios.get(`${API}/friends/${selectedChild.id}/requests`);
      setFriendRequests(res.data);
    } catch (e) {
      console.error("Error fetching requests:", e);
    }
  };

  const searchFriends = async (query) => {
    if (query.length < 2) {
      setSearchResults([]);
      return;
    }
    try {
      const res = await axios.get(`${API}/children/search?query=${encodeURIComponent(query.trim())}`);
      setSearchResults(res.data);
    } catch (e) {
      console.error("Error searching:", e);
    }
  };

  const sendFriendRequest = async (receiverId) => {
    try {
      await axios.post(`${API}/friends/request`, {
        requester_id: selectedChild.id,
        receiver_id: receiverId
      });
      setSearchResults(searchResults.map(r => r.id === receiverId ? { ...r, friend_status: "pending" } : r));
    } catch (e) {
      console.error("Error sending request:", e);
    }
  };

  const acceptFriendRequest = async (friendshipId) => {
    try {
      await axios.post(`${API}/friends/${friendshipId}/accept?child_id=${selectedChild.id}`);
      fetchFriends();
      fetchFriendRequests();
    } catch (e) {
      console.error("Error accepting:", e);
    }
  };

  const rejectFriendRequest = async (friendshipId) => {
    try {
      await axios.post(`${API}/friends/${friendshipId}/reject?child_id=${selectedChild.id}`);
      fetchFriendRequests();
    } catch (e) {
      console.error("Error rejecting:", e);
    }
  };

  // Groups functions
  const fetchMyGroups = async () => {
    try {
      const res = await axios.get(`${API}/groups/my/${selectedChild.id}`);
      setMyGroups(res.data);
    } catch (e) {
      console.error("Error fetching my groups:", e);
    }
  };

  const fetchOpenGroups = async () => {
    try {
      const res = await axios.get(`${API}/groups?age_group=${selectedChild?.age_group}`);
      setOpenGroups(res.data);
    } catch (e) {
      console.error("Error fetching open groups:", e);
    }
  };

  const fetchGroupInvites = async () => {
    try {
      const res = await axios.get(`${API}/groups/invites/${selectedChild.id}`);
      setGroupInvites(res.data);
    } catch (e) {
      console.error("Error fetching invites:", e);
    }
  };

  const createGroup = async () => {
    try {
      setLoading(true);
      await axios.post(`${API}/groups`, {
        ...newGroup,
        owner_id: selectedChild.id
      });
      setShowCreateGroup(false);
      setNewGroup({ name: "", description: "", avatar: "👥", group_type: "open" });
      fetchMyGroups();
    } catch (e) {
      console.error("Error creating group:", e);
    } finally {
      setLoading(false);
    }
  };

  const joinGroup = async (groupId) => {
    try {
      await axios.post(`${API}/groups/${groupId}/join?child_id=${selectedChild.id}`);
      fetchMyGroups();
      fetchOpenGroups();
    } catch (e) {
      console.error("Error joining group:", e);
    }
  };

  const acceptGroupInvite = async (inviteId) => {
    try {
      await axios.post(`${API}/groups/invites/${inviteId}/accept?child_id=${selectedChild.id}`);
      fetchMyGroups();
      fetchGroupInvites();
    } catch (e) {
      console.error("Error accepting invite:", e);
    }
  };

  const leaveGroup = async (groupId) => {
    try {
      await axios.post(`${API}/groups/${groupId}/leave?child_id=${selectedChild.id}`);
      fetchMyGroups();
    } catch (e) {
      console.error("Error leaving group:", e);
    }
  };

  const submitMultiplayerAnswer = async (arg = false) => {
    const timedOut = arg === true;
    if (!gameSession) return;
    if (!timedOut && userAnswer === "") return;
    timeoutArmedRef.current = false;
    
    const timeTaken = timedOut ? 15000 : (Date.now() - questionStartTime);
    const question = gameSession.questions[currentQuestion];
    
    // Calculate correct answer based on operator
    let correctAnswer;
    switch (question.operator) {
      case "+": correctAnswer = question.a + question.b; break;
      case "-": correctAnswer = question.a - question.b; break;
      case "×": correctAnswer = question.a * question.b; break;
      case "÷": correctAnswer = question.a / question.b; break;
      default: correctAnswer = question.a + question.b;
    }
    
    const isCorrect = !timedOut && parseInt(userAnswer) === correctAnswer;
    
    // Update current player's score
    const updatedPlayers = [...multiplayerPlayers];
    if (isCorrect) {
      updatedPlayers[currentPlayerIndex].score += 1;
    }
    updatedPlayers[currentPlayerIndex].time_ms += timeTaken;
    
    setFeedback({
      correct: isCorrect,
      correctAnswer: correctAnswer,
      timedOut: timedOut
    });
    
    setTimeout(() => {
      if (currentQuestion < gameSession.questions.length - 1) {
        // Next question for same player
        setCurrentQuestion(currentQuestion + 1);
        setUserAnswer("");
        setFeedback(null);
        setQuestionStartTime(Date.now());
        setMultiplayerPlayers(updatedPlayers);
      } else {
        // Player finished all questions
        updatedPlayers[currentPlayerIndex].completed = true;
        
        if (currentPlayerIndex < multiplayerPlayers.length - 1) {
          // Show "pass the device" handoff screen before next player
          setMultiplayerPlayers(updatedPlayers);
          setPendingNextPlayer(currentPlayerIndex + 1);
          setUserAnswer("");
          setFeedback(null);
          setShowPassDevice(true);
        } else {
          // All players finished - show results
          setMultiplayerPlayers(updatedPlayers);
          setMultiplayerResults(updatedPlayers.sort((a, b) => {
            if (b.score !== a.score) return b.score - a.score;
            return a.time_ms - b.time_ms; // Faster wins on tie
          }));
          setMultiplayerMode(false);
          setView("multiplayerResults");
        }
      }
    }, 1500);
  };

  const startNextPlayerTurn = () => {
    if (pendingNextPlayer === null) return;
    // Load the next player's OWN set of questions
    if (multiplayerQuestions[pendingNextPlayer]) {
      setGameSession((prev) => ({ ...prev, questions: multiplayerQuestions[pendingNextPlayer] }));
    }
    setCurrentPlayerIndex(pendingNextPlayer);
    setPendingNextPlayer(null);
    setCurrentQuestion(0);
    setUserAnswer("");
    setFeedback(null);
    setShowPassDevice(false);
    setQuestionStartTime(Date.now());
    setTimeLeft(15);
  };

  // Shop functions
  const fetchShopItems = async () => {
    try {
      const res = await axios.get(`${API}/shop/items`);
      setShopItems(res.data);
    } catch (e) {
      console.error("Error fetching shop items:", e);
    }
  };

  const fetchInventory = async () => {
    try {
      const res = await axios.get(`${API}/children/${selectedChild.id}/inventory`);
      setInventory(res.data);
    } catch (e) {
      console.error("Error fetching inventory:", e);
    }
  };

  const purchaseItem = async (item) => {
    if (selectedChild.coins < item.price) {
      setShopMessage({ type: "error", text: t.notEnoughCoins });
      setTimeout(() => setShopMessage(null), 2000);
      return;
    }
    
    try {
      const res = await axios.post(`${API}/shop/purchase`, {
        child_id: selectedChild.id,
        item_id: item.id
      });
      
      if (res.data.success) {
        setShopMessage({ type: "success", text: t.purchaseSuccess });
        // Update child's coins
        const newAvatar = item.category === "avatar" ? item.data.avatar : selectedChild.avatar;
        setSelectedChild({ ...selectedChild, coins: res.data.new_balance, avatar: newAvatar });
        // Update children list too
        setChildren(children.map(c => c.id === selectedChild.id ? { ...c, coins: res.data.new_balance, avatar: newAvatar } : c));
        fetchInventory();
        setTimeout(() => setShopMessage(null), 2000);
      }
    } catch (e) {
      setShopMessage({ type: "error", text: e.response?.data?.detail || "Error" });
      setTimeout(() => setShopMessage(null), 2000);
    }
  };

  const equipItem = async (itemType, itemValue) => {
    try {
      await axios.put(`${API}/children/${selectedChild.id}/equip?item_type=${itemType}&item_value=${itemValue}`);
      const patch = itemType === "avatar" ? { avatar: itemValue } : { theme: itemValue };
      setSelectedChild({ ...selectedChild, ...patch });
      // Update children list too
      setChildren(children.map(c => c.id === selectedChild.id ? { ...c, ...patch } : c));
      fetchInventory();
    } catch (e) {
      console.error("Error equipping item:", e);
    }
  };

  // Change avatar directly from profile
  const changeAvatar = async (newAvatar) => {
    try {
      setLoading(true);
      const res = await axios.put(`${API}/children/${selectedChild.id}/change-avatar?new_avatar=${encodeURIComponent(newAvatar)}`);
      if (res.data.success) {
        setSelectedChild({ ...selectedChild, avatar: newAvatar });
        // Update children list too
        setChildren(children.map(c => c.id === selectedChild.id ? { ...c, avatar: newAvatar } : c));
        setShowAvatarModal(false);
        playSound("coins");
      }
    } catch (e) {
      console.error("Error changing avatar:", e);
      alert(e.response?.data?.detail || "Errore nel cambio avatar");
    } finally {
      setLoading(false);
    }
  };

  // Challenge functions
  const fetchPendingChallenges = async () => {
    try {
      const res = await axios.get(`${API}/challenges/pending/${selectedChild.age_group}`);
      setPendingChallenges(res.data.filter(c => c.challenger_id !== selectedChild.id));
    } catch (e) {
      console.error("Error fetching pending challenges:", e);
    }
  };

  const fetchActiveChallenges = async () => {
    try {
      const res = await axios.get(`${API}/challenges/active/${selectedChild.id}`);
      setActiveChallenges(res.data);
    } catch (e) {
      console.error("Error fetching active challenges:", e);
    }
  };

  const fetchChallengeHistory = async () => {
    try {
      const res = await axios.get(`${API}/challenges/history/${selectedChild.id}`);
      setChallengeHistory(res.data);
    } catch (e) {
      console.error("Error fetching challenge history:", e);
    }
  };

  const createChallenge = async (gameType, invitedId = null) => {
    try {
      setLoading(true);
      const res = await axios.post(`${API}/challenges/create`, {
        challenger_id: selectedChild.id,
        game_type: gameType,
        invited_id: invitedId
      });
      
      // Start playing the challenge immediately
      const challengeData = await axios.get(`${API}/challenges/${res.data.challenge_id}`);
      setCurrentChallenge(challengeData.data);
      setChallengeMode(true);
      setChallengeAnswers([]); // Reset answers
      setChallengeStartTime(Date.now()); // Track start time
      setGameSession({
        session_id: res.data.challenge_id,
        questions: challengeData.data.questions,
        game_type: gameType,
        age_group: selectedChild.age_group
      });
      setCurrentQuestion(0);
      setQuestionStartTime(Date.now());
      setView("game");
      setFeedback(null);
      setUserAnswer("");
    } catch (e) {
      console.error("Error creating challenge:", e);
    } finally {
      setLoading(false);
    }
  };

  const acceptChallenge = async (challenge) => {
    try {
      setLoading(true);
      await axios.post(`${API}/challenges/${challenge.id}/accept`, {
        opponent_id: selectedChild.id
      });
      
      // Start playing the challenge
      const challengeData = await axios.get(`${API}/challenges/${challenge.id}`);
      setCurrentChallenge(challengeData.data);
      setChallengeMode(true);
      setChallengeAnswers([]); // Reset answers
      setChallengeStartTime(Date.now()); // Track start time
      setGameSession({
        session_id: challenge.id,
        questions: challengeData.data.questions,
        game_type: challenge.game_type,
        age_group: selectedChild.age_group
      });
      setCurrentQuestion(0);
      setQuestionStartTime(Date.now());
      setView("game");
      setFeedback(null);
      setUserAnswer("");
    } catch (e) {
      console.error("Error accepting challenge:", e);
    } finally {
      setLoading(false);
    }
  };

  const fetchOnlinePlayers = async () => {
    try {
      const res = await axios.get(`${API}/presence/online`);
      setOnlinePlayers(res.data);
    } catch (e) { /* ignore */ }
  };

  const inviteToChallenge = async (playerId, gameType = "mixed") => {
    await createChallenge(gameType, playerId);
  };

  const fetchVirtualFriends = async () => {
    try {
      const res = await axios.get(`${API}/virtual-friends`);
      setVirtualFriends(res.data);
    } catch (e) { /* ignore */ }
  };

  const startBotChallenge = async (botId, gameType = "mixed") => {
    try {
      setLoading(true);
      setNewBadges([]);
      setLevelUp(null);
      const res = await axios.post(`${API}/virtual-challenges/start?bot_id=${botId}&game_type=${gameType}`);
      setBotChallenge({ id: res.data.challenge_id, bot: res.data.bot, level: res.data.level, myScore: 0, botScore: 0, taunt: null, streak: res.data.streak });
      setBotMode(true);
      setChallengeMode(false);
      setMultiplayerMode(false);
      setGameSession({ session_id: res.data.challenge_id, questions: [res.data.question], total: res.data.total, game_type: gameType, age_group: selectedChild.age_group, mode: "bot" });
      setCurrentQuestion(0);
      setQuestionStartTime(Date.now());
      setFeedback(null);
      setUserAnswer("");
      setView("game");
    } catch (e) {
      console.error("Error starting bot challenge:", e);
    } finally {
      setLoading(false);
    }
  };

  const submitBotAnswer = async (answer, timeTaken, timedOut) => {
    const res = await axios.post(`${API}/virtual-challenges/${botChallenge.id}/answer`, {
      question_index: currentQuestion, answer, time_ms: timeTaken,
    });
    const d = res.data;
    playSound(d.is_correct ? "correct" : "wrong");
    setBotChallenge((b) => ({ ...b, level: d.level, myScore: d.my_score, botScore: d.bot_score, taunt: d.taunt, lastBotCorrect: d.bot_correct }));
    setFeedback({ correct: d.is_correct, correctAnswer: d.correct_answer, timedOut, botCorrect: d.bot_correct });
    setTimeout(async () => {
      if (!d.completed) {
        setGameSession((s) => ({ ...s, questions: [...s.questions, d.next_question] }));
        setCurrentQuestion(currentQuestion + 1);
        setUserAnswer("");
        setFeedback(null);
        setQuestionStartTime(Date.now());
        return;
      }
      const r = d.result;
      if (r.winner === "me") playSound("victory");
      setGameResults({
        ...r,
        correct: r.my_score, total: 10, accuracy: r.my_score * 10,
        coins_earned: r.coins_earned, medal_earned: r.trophy ? "gold" : null, iq_score: 0,
        avg_time_seconds: (r.my_time_ms / 10000).toFixed(2), mode: "bot",
        bot_result: { ...r, bot: botChallenge.bot, game_type: gameSession.game_type },
      });
      if (r.leveled_up) setLevelUp({ level: r.level_after, bonus: r.level_bonus });
      setBotMode(false);
      setView("results");
      const updatedChild = await axios.get(`${API}/children/${selectedChild.id}`);
      setSelectedChild(updatedChild.data);
      setChildren((prev) => prev.map((c) => (c.id === selectedChild.id ? updatedChild.data : c)));
    }, 1500);
  };

  // Heartbeat presence + poll incoming invites while logged in
  useEffect(() => {
    if (!selectedChild) return;
    let cancelled = false;
    const ping = () => { axios.post(`${API}/presence/ping`).catch(() => {}); };
    const pollInvites = () => {
      axios.get(`${API}/challenges/invited/${selectedChild.id}`)
        .then((r) => { if (!cancelled) setIncomingInvites(r.data || []); })
        .catch(() => {});
    };
    ping(); pollInvites();
    const ivPing = setInterval(ping, 30000);
    const ivInv = setInterval(pollInvites, 10000);
    return () => { cancelled = true; clearInterval(ivPing); clearInterval(ivInv); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedChild?.id]);

  const handleKeyPress = (e) => {
    if (e.key === "Enter") {
      if (multiplayerMode) submitMultiplayerAnswer();
      else submitAnswer();    }
  };

  // 15-second countdown timer for all challenge modes (local + 1v1)
  useEffect(() => {
    if (view !== "game" || !gameSession) return;
    if (!(challengeMode || multiplayerMode || botMode)) return;
    if (feedback || showPassDevice) return;
    setTimeLeft(15);
    timeoutArmedRef.current = true;
    let remaining = 15;
    const iv = setInterval(() => {
      remaining -= 1;
      setTimeLeft(remaining);
      if (remaining <= 0) {
        clearInterval(iv);
        if (!timeoutArmedRef.current) return;
        timeoutArmedRef.current = false;
        if (multiplayerMode) submitMultiplayerAnswer(true);
        else submitAnswer(true);
      }
    }, 1000);
    return () => clearInterval(iv);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view, gameSession, challengeMode, multiplayerMode, botMode, feedback, currentQuestion, currentPlayerIndex, showPassDevice]);

  // Home Screen - Profile Selection
  if (!authReady) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-600 via-pink-500 to-orange-400 flex items-center justify-center" data-testid="auth-loading">
        <div className="text-white text-3xl font-bold animate-pulse">🎮 CelestApp...</div>
      </div>
    );
  }

  if (view === "home") {
    return (
      <AuthScreen
        API={API}
        lang={lang}
        setLang={(l) => { setLang(l); langExplicit.current = true; }}
        onAuth={handleAuth}
      />
    );
  }

  if (view === "__old_home_unused__") {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-600 via-pink-500 to-orange-400 p-4">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="text-center py-8">
            <h1 className="text-5xl md:text-7xl font-bold text-white drop-shadow-lg animate-bounce-slow" data-testid="main-title">
              🎮 {t.title} 🏆
            </h1>
            <p className="text-xl md:text-2xl text-white/90 mt-4">{t.subtitle}</p>
            
            {/* Language Toggle */}
            <div className="mt-4 flex justify-center gap-2">
              <button
                onClick={() => { setLang("it"); langExplicit.current = true; }}
                className={`px-4 py-2 rounded-full font-bold transition-all ${lang === "it" ? "bg-white text-purple-600" : "bg-white/30 text-white hover:bg-white/50"}`}
                data-testid="lang-it"
              >
                🇮🇹 Italiano
              </button>
              <button
                onClick={() => { setLang("en"); langExplicit.current = true; }}
                className={`px-4 py-2 rounded-full font-bold transition-all ${lang === "en" ? "bg-white text-purple-600" : "bg-white/30 text-white hover:bg-white/50"}`}
                data-testid="lang-en"
              >
                🇬🇧 English
              </button>
            </div>
          </div>

          {/* Challenge link banner */}
          {linkChallengeId && (
            <div className="bg-white/90 rounded-2xl p-4 mb-4 text-center font-bold text-purple-700 animate-pulse" data-testid="challenge-link-banner">
              ⚔️ {t.challengeReceived}
            </div>
          )}

          {/* Profile Selection */}
          <div className="bg-white/20 backdrop-blur-lg rounded-3xl p-6 md:p-8">
            <h2 className="text-2xl font-bold text-white mb-6 text-center">{t.selectProfile}</h2>
            
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {children.map((child) => (
                <button
                  key={child.id}
                  onClick={() => selectChild(child)}
                  className="bg-white rounded-2xl p-4 shadow-lg hover:scale-105 transition-transform hover:shadow-xl"
                  data-testid={`profile-${child.id}`}
                >
                  <div className="text-5xl mb-2">{child.avatar}</div>
                  <div className="font-bold text-gray-800">{child.name}</div>
                  <div className="text-sm text-gray-500">{child.age} {t.years}</div>
                  <div className="flex justify-center gap-2 mt-2 text-sm">
                    <span>💰 {child.coins}</span>
                    <span>🏆 {child.trophies}</span>
                  </div>
                </button>
              ))}
              
              {/* Create New Profile */}
              <button
                onClick={() => setShowCreateModal(true)}
                className="bg-white/50 border-4 border-dashed border-white rounded-2xl p-4 hover:bg-white/70 transition-all flex flex-col items-center justify-center"
                data-testid="create-profile-btn"
              >
                <div className="text-5xl mb-2">➕</div>
                <div className="font-bold text-white">{t.createProfile}</div>
              </button>
            </div>
          </div>

          {/* Parents Area */}
          <div className="text-center mt-6">
            <button
              onClick={openParentsArea}
              className="bg-white/30 text-white px-6 py-3 rounded-full font-bold hover:bg-white/50 transition-colors"
              data-testid="parents-area-btn"
            >
              👨‍👩‍👧 {t.parentsArea} 🔒
            </button>
          </div>

          {/* Decorative Elements */}
          <div className="flex justify-center gap-8 mt-8 text-6xl animate-bounce-slow">
            <span>🧮</span>
            <span>📐</span>
            <span>🔢</span>
            <span>⭐</span>
          </div>
        </div>

        {/* Parent PIN Modal */}
        {pinModal && (
          <div className="fixed inset-0 bg-black/60 flex items-center justify-center p-4 z-50" data-testid="pin-modal">
            <div className="bg-white rounded-3xl p-8 max-w-sm w-full text-center">
              <div className="text-5xl mb-3">🔒</div>
              <h3 className="text-2xl font-bold text-gray-800 mb-1">
                {pinModal.mode === "set" ? t.pinCreateTitle : t.pinEnterTitle}
              </h3>
              <p className="text-sm text-gray-400 mb-4">{t.pinHint}</p>
              {pinModal.error && <p className="text-red-500 font-bold text-sm mb-2" data-testid="pin-error">{t.pinWrong}</p>}
              <input
                type="password"
                inputMode="numeric"
                maxLength={4}
                value={pinModal.value}
                onChange={(e) => setPinModal({ ...pinModal, value: e.target.value.replace(/\D/g, "").slice(0, 4) })}
                className="w-40 mx-auto block text-center text-3xl tracking-[0.5em] px-4 py-3 rounded-xl border-2 border-purple-300 focus:border-purple-500 focus:outline-none mb-4"
                data-testid="pin-input"
                autoFocus
              />
              <div className="flex gap-3">
                <button onClick={() => setPinModal(null)} className="flex-1 px-4 py-3 rounded-xl border-2 border-gray-300 text-gray-600 font-bold" data-testid="pin-cancel-btn">{t.cancel}</button>
                <button
                  onClick={submitPin}
                  disabled={pinModal.value.length !== 4}
                  className="flex-1 px-4 py-3 rounded-xl bg-gradient-to-r from-purple-600 to-pink-500 text-white font-bold disabled:opacity-50"
                  data-testid="pin-submit-btn"
                >
                  {pinModal.mode === "set" ? t.create : "OK"}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Payment Result Modal */}
        {paymentResult && (
          <div className="fixed inset-0 bg-black/60 flex items-center justify-center p-4 z-50" data-testid="payment-result-modal-legacy">
            <div className="bg-white rounded-3xl p-8 max-w-md w-full text-center">
              <div className="text-6xl mb-4">
                {paymentResult.status === "paid" ? (paymentResult.type === "pro" || paymentResult.type === "premium" ? "👑" : "🏆") : paymentResult.status === "cancelled" ? "🙅" : "⚠️"}
              </div>
              <h3 className="text-2xl font-bold text-gray-800 mb-2">
                {paymentResult.status === "paid" ? t.paymentSuccess : paymentResult.status === "cancelled" ? t.paymentCancelled : t.paymentError}
              </h3>
              {paymentResult.status === "paid" && (
                <p className="text-gray-500 mb-4">
                  {paymentResult.type === "premium"
                    ? (lang === "it" ? "Premium attivato! Tutti i contenuti sono sbloccati. 👑" : "Premium activated! All content is unlocked. 👑")
                    : paymentResult.type === "pro" ? t.paymentProOk : t.paymentTournamentOk}
                </p>
              )}
              <button
                onClick={() => setPaymentResult(null)}
                className="mt-2 px-8 py-3 rounded-xl bg-gradient-to-r from-purple-600 to-pink-500 text-white font-bold hover:opacity-90"
                data-testid="payment-result-ok-btn"
              >
                OK
              </button>
            </div>
          </div>
        )}

        {/* Create Profile Modal */}
        {showCreateModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-3xl p-6 md:p-8 max-w-md w-full">
              <h3 className="text-2xl font-bold text-purple-600 mb-6">{t.createProfile}</h3>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-gray-700 font-medium mb-2">{t.name}</label>
                  <input
                    type="text"
                    value={newChild.name}
                    onChange={(e) => setNewChild({...newChild, name: e.target.value})}
                    className="w-full px-4 py-3 rounded-xl border-2 border-purple-200 focus:border-purple-500 focus:outline-none text-lg"
                    placeholder="Mario"
                    data-testid="input-name"
                  />
                </div>
                
                <div>
                  <label className="block text-gray-700 font-medium mb-2">{t.age}</label>
                  <input
                    type="number"
                    min="5"
                    max="16"
                    value={newChild.age}
                    onChange={(e) => setNewChild({...newChild, age: parseInt(e.target.value)})}
                    className="w-full px-4 py-3 rounded-xl border-2 border-purple-200 focus:border-purple-500 focus:outline-none text-lg"
                    data-testid="input-age"
                  />
                </div>
                
                <div>
                  <label className="block text-gray-700 font-medium mb-2">{t.selectAvatar}</label>
                  <div className="grid grid-cols-8 gap-2">
                    {avatars.map((avatar) => (
                      <button
                        key={avatar}
                        onClick={() => setNewChild({...newChild, avatar})}
                        className={`text-3xl p-2 rounded-lg transition-all ${newChild.avatar === avatar ? "bg-purple-500 scale-110" : "hover:bg-purple-100"}`}
                        data-testid={`avatar-${avatar}`}
                      >
                        {avatar}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
              
              <div className="flex gap-4 mt-6">
                <button
                  onClick={() => setShowCreateModal(false)}
                  className="flex-1 px-4 py-3 rounded-xl border-2 border-gray-300 text-gray-600 font-bold hover:bg-gray-100 transition-colors"
                  data-testid="cancel-create-btn"
                >
                  {t.cancel}
                </button>
                <button
                  onClick={createChild}
                  disabled={!newChild.name || loading}
                  className="flex-1 px-4 py-3 rounded-xl bg-gradient-to-r from-purple-600 to-pink-500 text-white font-bold hover:opacity-90 transition-opacity disabled:opacity-50"
                  data-testid="confirm-create-btn"
                >
                  {loading ? "..." : t.create}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  // Parents Dashboard
  if (view === "parents") {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-800 via-slate-700 to-slate-600 p-4">
        <div className="max-w-4xl mx-auto">
          <button
            onClick={() => setView("home")}
            className="bg-white/20 text-white px-4 py-2 rounded-full font-bold hover:bg-white/30 transition-colors mb-6"
            data-testid="parents-back-btn"
          >
            ← {t.backHome}
          </button>
          <h2 className="text-3xl font-bold text-white text-center mb-1">👨‍👩‍👧 {t.parentsArea}</h2>
          <p className="text-center text-white/70 mb-4">{t.parentsSubtitle}</p>

          <div className="text-center mb-6">
            {!weeklyReport ? (
              <button
                onClick={fetchWeeklyReport}
                className="bg-white/20 text-white px-6 py-3 rounded-full font-bold hover:bg-white/30 transition-colors"
                data-testid="weekly-report-btn"
              >
                📄 {t.weeklyReport}
              </button>
            ) : (
              <button
                onClick={() => window.print()}
                className="bg-white text-slate-700 px-6 py-3 rounded-full font-bold hover:bg-gray-100 transition-colors"
                data-testid="print-report-btn"
              >
                🖨️ {t.printLabel}
              </button>
            )}
          </div>

          <div className="bg-white/10 rounded-2xl p-4 mb-6 text-center" data-testid="alert-monitor-section">
            <p className="text-white/80 text-sm mb-3">
              {lang === "it"
                ? "🔔 Ricevi un'email se il servizio va in errore. Invia un test per verificare che arrivi."
                : "🔔 Get an email if the service fails. Send a test to confirm delivery."}
            </p>
            <button
              onClick={sendTestAlert}
              disabled={alertSending}
              className="bg-white/20 text-white px-6 py-3 rounded-full font-bold hover:bg-white/30 transition-colors disabled:opacity-50"
              data-testid="send-test-alert-btn"
            >
              {alertSending
                ? (lang === "it" ? "Invio…" : "Sending…")
                : "🔔 " + (lang === "it" ? "Invia email di prova" : "Send test alert")}
            </button>
            {alertStatus && (
              <p className={`text-sm font-bold mt-3 ${alertStatus.ok ? "text-green-300" : "text-red-300"}`} data-testid="alert-status">
                {alertStatus.text}
              </p>
            )}
          </div>

          {weeklyReport && (
            <div className="bg-white rounded-3xl p-6 shadow-xl mb-6" data-testid="weekly-report-section">
              <h3 className="text-xl font-bold text-gray-800 mb-1">📄 {t.weeklyReport}</h3>
              <p className="text-xs text-gray-400 mb-4">
                {new Date(weeklyReport.from).toLocaleDateString(lang === "it" ? "it-IT" : "en-GB")} → {new Date(weeklyReport.to).toLocaleDateString(lang === "it" ? "it-IT" : "en-GB")}
              </p>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-gray-600 border-b">
                      <th className="py-2">{lang === "it" ? "Bambino" : "Child"}</th>
                      <th className="py-2">🎮 {lang === "it" ? "Partite" : "Games"}</th>
                      <th className="py-2">✨ Quiz AI</th>
                      <th className="py-2">🎯 {lang === "it" ? "Precisione" : "Accuracy"}</th>
                      <th className="py-2">⏱️ {lang === "it" ? "Tempo" : "Time"}</th>
                      <th className="py-2">🏅 Badge</th>
                      <th className="py-2">⚔️ {lang === "it" ? "Sfide" : "Wins"}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {weeklyReport.children.map((c) => (
                      <tr key={c.id} className="border-b last:border-0" data-testid={`report-row-${c.id}`}>
                        <td className="py-2 font-bold text-gray-700">{c.avatar} {c.name}</td>
                        <td className="py-2">{c.games_7d}</td>
                        <td className="py-2">{c.ai_quizzes_7d}</td>
                        <td className="py-2">{c.accuracy_7d}%</td>
                        <td className="py-2">{formatPlaytime(c.playtime_7d_ms)}</td>
                        <td className="py-2">{c.badges_7d}</td>
                        <td className="py-2">{c.challenge_wins_7d}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p className="text-xs text-gray-500 mt-3">{lang === "it" ? "Dati degli ultimi 7 giorni" : "Data from the last 7 days"}</p>
              <div className="mt-4 border-t pt-4">
                <div className="flex gap-2">
                  <input
                    type="email"
                    value={reportEmail}
                    onChange={(e) => setReportEmail(e.target.value)}
                    placeholder="genitore@email.it"
                    className="flex-1 px-4 py-2 rounded-xl border-2 border-gray-200 focus:border-purple-400 focus:outline-none text-sm"
                    data-testid="report-email-input"
                  />
                  <button
                    onClick={sendReportEmail}
                    disabled={!reportEmail.includes("@")}
                    className="px-4 py-2 rounded-xl bg-gradient-to-r from-purple-600 to-pink-500 text-white font-bold text-sm disabled:opacity-50"
                    data-testid="send-report-email-btn"
                  >
                    📧 {t.sendEmailLabel}
                  </button>
                </div>
                {emailStatus && (
                  <p className={`text-sm font-bold mt-2 ${emailStatus.ok ? "text-green-600" : "text-red-500"}`} data-testid="email-status">
                    {emailStatus.text}
                  </p>
                )}
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {parentData.map((c) => (
              <div key={c.id} className="bg-white rounded-3xl p-6 shadow-xl" data-testid={`parent-card-${c.id}`}>
                <div className="flex items-center gap-3 mb-4">
                  <span className="text-5xl">{c.avatar}</span>
                  <div className="flex-1">
                    <div className="font-bold text-xl text-gray-800">{c.name}</div>
                    <div className="text-sm text-gray-500">{c.age} {t.years} · {lang === "it" ? "Fascia" : "Band"}: {c.age_band}</div>
                  </div>
                  {c.is_pro && <span className="bg-gradient-to-r from-amber-400 to-yellow-500 text-white text-xs px-2 py-1 rounded-full font-bold">👑 PRO</span>}
                </div>

                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div className="bg-blue-50 rounded-xl p-3">
                    <div className="text-gray-500">⏱️ {t.playtime}</div>
                    <div className="font-bold text-blue-700 text-lg">{formatPlaytime(c.playtime_ms)}</div>
                  </div>
                  <div className="bg-purple-50 rounded-xl p-3">
                    <div className="text-gray-500">🎮 {t.totalGames}</div>
                    <div className="font-bold text-purple-700 text-lg">{c.total_games} <span className="text-xs font-normal text-gray-400">({c.games_last_7_days} · 7gg)</span></div>
                  </div>
                  <div className="bg-green-50 rounded-xl p-3 col-span-2">
                    <div className="flex justify-between text-gray-500 mb-1">
                      <span>🎯 {t.accuracy}</span>
                      <span className="font-bold text-green-700">{c.accuracy}%</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div className="bg-gradient-to-r from-green-400 to-emerald-500 h-2 rounded-full" style={{ width: `${Math.min(c.accuracy, 100)}%` }}></div>
                    </div>
                  </div>
                  <div className="bg-yellow-50 rounded-xl p-3">
                    <div className="text-gray-500">💰 {t.coins}</div>
                    <div className="font-bold text-yellow-700 text-lg">{c.coins}</div>
                  </div>
                  <div className="bg-amber-50 rounded-xl p-3">
                    <div className="text-gray-500">🏅 Badge</div>
                    <div className="font-bold text-amber-700 text-lg">{c.badges_count}/8</div>
                  </div>
                  <div className="bg-orange-50 rounded-xl p-3">
                    <div className="text-gray-500">🛒 {t.coinsSpentLabel}</div>
                    <div className="font-bold text-orange-700 text-lg">{c.coins_spent}</div>
                  </div>
                  <div className="bg-red-50 rounded-xl p-3">
                    <div className="text-gray-500">💳 {t.moneySpentLabel}</div>
                    <div className="font-bold text-red-700 text-lg">€{c.money_spent_eur.toFixed(2)}</div>
                  </div>
                </div>
                {c.last_game_at && (
                  <p className="text-xs text-gray-400 mt-3">🕐 {t.lastGameLabel}: {new Date(c.last_game_at).toLocaleDateString(lang === "it" ? "it-IT" : "en-GB")}</p>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }
  // Online friends screen — see who's online and challenge them
  if (view === "online") {
    return (
      <div className={`min-h-screen ${bgClass("bg-gradient-to-br from-teal-600 via-cyan-500 to-green-400")} p-4`} data-testid="online-screen">
        <div className="max-w-2xl mx-auto">
          <div className="flex items-center justify-between mb-6">
            <button
              onClick={() => setView("gameSelect")}
              className="bg-white/20 text-white px-4 py-2 rounded-full font-bold hover:bg-white/30"
              data-testid="online-back-btn"
            >
              ← {t.backHome}
            </button>
            <button
              onClick={() => { fetchOnlinePlayers(); fetchVirtualFriends(); }}
              className="bg-white/20 text-white px-4 py-2 rounded-full font-bold hover:bg-white/30"
              data-testid="online-refresh-btn"
            >
              🔄
            </button>
          </div>
          <div className="text-center mb-6">
            <div className="text-6xl mb-2">🟢</div>
            <h2 className="text-3xl font-bold text-white">{t.onlineFriends}</h2>
            <p className="text-white/80 mt-1">{t.onlineFriendsDesc}</p>
          </div>

          {selectedChild?.is_guest ? (
            <div className="space-y-6">
              <VirtualFriendsList data={virtualFriends} lang={lang} t={t} onChallenge={(id) => startBotChallenge(id, "mixed")} loading={loading} />
              <div className="bg-white/95 rounded-2xl p-6 text-center" data-testid="online-guest-prompt">
                <div className="text-4xl mb-2">🔒</div>
                <p className="text-gray-700 font-bold mb-1">{lang === "it" ? "Crea un account per trovare amici veri e sfidarli!" : "Create an account to find real friends and challenge them!"}</p>
                <p className="text-gray-500 text-sm mb-4">{lang === "it" ? "Giochi come ospite: registrati per la modalità sociale." : "You're playing as a guest: sign up for social mode."}</p>
                <button
                  onClick={() => { setView("gameSelect"); openUpgrade(); }}
                  className="bg-gradient-to-r from-purple-600 to-pink-500 text-white px-6 py-3 rounded-2xl font-bold hover:opacity-90"
                  data-testid="online-guest-register-btn"
                >
                  💾 {lang === "it" ? "Salva i progressi e registrati" : "Save progress & sign up"}
                </button>
              </div>
            </div>
          ) : onlinePlayers.length === 0 ? (
            <div className="space-y-4">
              <div className="bg-white/90 rounded-2xl p-4 text-center text-gray-600 text-sm" data-testid="online-empty">
                {t.noOneOnline}
              </div>
              <VirtualFriendsList data={virtualFriends} lang={lang} t={t} onChallenge={(id) => startBotChallenge(id, "mixed")} loading={loading} />
            </div>
          ) : (
            <div className="space-y-3" data-testid="online-list">
              {onlinePlayers.map((p) => (
                <div key={p.id} className="bg-white rounded-2xl p-4 shadow-lg flex items-center justify-between" data-testid={`online-player-${p.id}`}>
                  <div className="flex items-center gap-3">
                    <div className="relative">
                      <span className="text-4xl">{p.avatar}</span>
                      <span className="absolute bottom-0 right-0 w-3 h-3 bg-green-500 rounded-full border-2 border-white"></span>
                    </div>
                    <span className="text-xl font-bold text-gray-800">{p.name}</span>
                  </div>
                  <button
                    onClick={() => inviteToChallenge(p.id, "mixed")}
                    disabled={loading}
                    className="bg-gradient-to-r from-purple-600 to-pink-500 text-white px-5 py-2 rounded-full font-bold hover:opacity-90 disabled:opacity-50 flex items-center gap-2"
                    data-testid={`online-challenge-${p.id}`}
                  >
                    ⚔️ {t.challengeBtn}
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  }

  // Dedicated full-screen category page (opens like the Checkers game)
  if (view === "gameCategory") {
    const goBack = () => { setSelectedCategory(null); setView("gameSelect"); };
    const catMeta = {
      math: { icon: "🔢", label: t.mathGames, bg: "bg-gradient-to-br from-green-500 via-emerald-500 to-teal-500" },
      creative: { icon: "🎨", label: t.creativeArea, bg: "bg-gradient-to-br from-pink-500 via-rose-500 to-fuchsia-500" },
      brain: { icon: "🧠", label: t.brainGames, bg: "bg-gradient-to-br from-purple-500 via-indigo-500 to-violet-600" },
    }[selectedCategory] || { icon: "🎮", label: t.chooseGame, bg: "bg-gradient-to-br from-blue-600 to-teal-400" };

    const mathTiles = [
      { type: "addition", icon: "➕", color: "from-green-400 to-emerald-500" },
      { type: "subtraction", icon: "➖", color: "from-red-400 to-rose-500" },
      { type: "multiplication", icon: "✖️", color: "from-blue-400 to-indigo-500" },
      { type: "division", icon: "➗", color: "from-purple-400 to-violet-500" },
      { type: "mixed", icon: "🎲", color: "from-orange-400 to-amber-500" },
    ];
    const brainTiles = [
      { view: "oddOneOut", icon: "🔍", label: t.findOddOne, color: "from-green-400 to-teal-500", testid: "game-oddoneout" },
      { view: "sequences", icon: "🔢", label: t.sequences, color: "from-purple-400 to-pink-500", testid: "game-sequences" },
      { view: "memory", icon: "🃏", label: t.memory, color: "from-indigo-400 to-purple-500", testid: "game-memory" },
      { view: "riddles", icon: "🤔", label: t.riddles, color: "from-amber-400 to-orange-500", testid: "game-riddles" },
      { view: "italianQuiz", icon: "🇮🇹", label: t.italianQuiz, color: "from-green-500 to-emerald-600", testid: "game-italian" },
    ];
    const creativeTiles = [
      { view: "drawing", icon: "✏️", label: t.drawFree, color: "from-pink-400 to-purple-500", testid: "game-drawing" },
      { view: "coloring", icon: "🖍️", label: t.coloringBook, color: "from-yellow-400 to-orange-500", testid: "game-coloring" },
    ];

    return (
      <div className={`min-h-screen ${bgClass(catMeta.bg)} p-4`} data-testid="game-category-screen">
        <div className="max-w-2xl mx-auto">
          <button
            onClick={goBack}
            className="bg-white/20 text-white px-4 py-2 rounded-full font-bold hover:bg-white/30 transition-colors mb-6"
            data-testid="category-back-btn"
          >
            ← {t.backHome}
          </button>
          <div className="text-center mb-8">
            <div className="text-6xl mb-2">{catMeta.icon}</div>
            <h2 className="text-3xl font-bold text-white" data-testid="category-title">{catMeta.label}</h2>
            <p className="text-white/80 mt-1">{t.chooseGame}</p>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
            {selectedCategory === "math" && mathTiles.map((g) => (
              <button
                key={g.type}
                onClick={() => startGame(g.type)}
                disabled={loading}
                className={`bg-gradient-to-br ${g.color} rounded-2xl p-6 shadow-lg hover:scale-105 transition-transform text-white disabled:opacity-50`}
                data-testid={`game-${g.type}`}
              >
                <div className="text-4xl mb-2">{g.icon}</div>
                <div className="font-bold">{t.gameTypes[g.type]}</div>
              </button>
            ))}
            {selectedCategory === "math" && (
              <button
                onClick={() => startGame("mixed", "blitz")}
                disabled={loading}
                className="col-span-2 sm:col-span-3 bg-gradient-to-r from-red-600 via-orange-500 to-amber-400 rounded-2xl p-5 shadow-lg hover:scale-[1.02] transition-transform text-white disabled:opacity-50 flex items-center gap-4"
                data-testid="game-blitz"
              >
                <div className="text-5xl animate-pulse">⚡</div>
                <div className="text-left">
                  <div className="font-black text-xl">{lang === "it" ? "BLITZ 60 secondi" : "BLITZ 60 seconds"}</div>
                  <div className="text-sm opacity-90">{lang === "it" ? "Quante ne risolvi in un minuto? Combo, record e medaglie!" : "How many can you solve in a minute? Combos, records & medals!"}{selectedChild?.records?.blitz !== undefined ? ` · ${lang === "it" ? "Record" : "Best"}: ${selectedChild.records.blitz}` : ""}</div>
                </div>
              </button>
            )}
            {selectedCategory === "creative" && creativeTiles.map((g) => (
              <button
                key={g.view}
                onClick={() => setView(g.view)}
                className={`bg-gradient-to-br ${g.color} rounded-2xl p-6 shadow-lg hover:scale-105 transition-transform text-white`}
                data-testid={g.testid}
              >
                <div className="text-4xl mb-2">{g.icon}</div>
                <div className="font-bold">{g.label}</div>
              </button>
            ))}
            {selectedCategory === "brain" && brainTiles.map((g) => (
              <button
                key={g.view}
                onClick={() => setView(g.view)}
                className={`bg-gradient-to-br ${g.color} rounded-2xl p-6 shadow-lg hover:scale-105 transition-transform text-white`}
                data-testid={g.testid}
              >
                <div className="text-4xl mb-2">{g.icon}</div>
                <div className="font-bold">{g.label}</div>
              </button>
            ))}
          </div>
        </div>
      </div>
    );
  }



  // Game Selection Screen
  if (view === "gameSelect") {
    return (
      <div className={`min-h-screen ${bgClass("bg-gradient-to-br from-blue-600 via-cyan-500 to-teal-400")} p-4`}>
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="flex justify-between items-center mb-6">
            <button
              onClick={logout}
              className="bg-white/20 text-white px-4 py-2 rounded-full font-bold hover:bg-white/30 transition-colors"
              data-testid="logout-btn"
            >
              ← {t.logout}
            </button>
            
            <div className="flex gap-2">
              {/* Night mode */}
              <button
                onClick={toggleNightMode}
                className={`px-3 py-2 rounded-full font-bold transition-colors ${selectedChild.night_mode ? "bg-indigo-300 text-indigo-900" : "bg-white/20 text-white hover:bg-white/30"}`}
                data-testid="night-mode-btn"
                title={lang === "it" ? "Modalità Notte" : "Night Mode"}
              >
                {selectedChild.night_mode ? "☀️" : "🌙"}
              </button>
              {/* Settings */}
              <button
                onClick={() => { setSettingsForm({ name: selectedChild.name, email: selectedChild.email || "" }); setView("settings"); }}
                className="px-3 py-2 rounded-full font-bold bg-white/20 text-white hover:bg-white/30 transition-colors"
                data-testid="settings-btn"
              >
                ⚙️
              </button>
              {/* Sound Toggle */}
              <button
                onClick={() => { setSoundEnabled(!soundEnabled); playSound("click"); }}
                className={`px-3 py-2 rounded-full font-bold transition-colors ${soundEnabled ? "bg-green-500 text-white" : "bg-white/20 text-white"}`}
                data-testid="sound-toggle"
              >
                {soundEnabled ? "🔊" : "🔇"}
              </button>
              <button
                onClick={() => {
                  const nl = lang === "it" ? "en" : "it";
                  setLang(nl);
                  axios.put(`${API}/children/${selectedChild.id}/language?language=${nl}`).catch(() => {});
                  setSelectedChild(prev => ({ ...prev, language: nl }));
                  setChildren(prev => prev.map(c => c.id === selectedChild.id ? { ...c, language: nl } : c));
                }}
                className="bg-white/20 text-white px-3 py-2 rounded-full font-bold hover:bg-white/30 transition-colors"
              >
                {lang === "it" ? "🇬🇧" : "🇮🇹"}
              </button>
            </div>
          </div>

          {/* Penalty/Inactivity Warning */}
          {showPenaltyWarning && activityStatus && (
            <div className={`mb-4 p-4 rounded-2xl ${activityStatus.penalty ? "bg-red-100 border-2 border-red-400" : "bg-yellow-100 border-2 border-yellow-400"} animate-pulse`}>
              <div className="flex items-center gap-3">
                <span className="text-3xl">{activityStatus.penalty ? "⚠️" : "⏰"}</span>
                <div className="flex-1">
                  <p className={`font-bold ${activityStatus.penalty ? "text-red-700" : "text-yellow-700"}`}>
                    {lang === "it" ? (activityStatus.message_it || activityStatus.message) : (activityStatus.message_en || activityStatus.message)}
                  </p>
                  <p className="text-sm text-gray-600">{t.playToRecover}</p>
                </div>
                <button
                  onClick={() => setShowPenaltyWarning(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  ✕
                </button>
              </div>
            </div>
          )}

          {/* Friend Challenge Notification */}
          {notifications?.count > 0 && (
            <button
              onClick={() => { fetchPendingChallenges(); fetchActiveChallenges(); fetchChallengeHistory(); setView("challenges"); }}
              className="w-full mb-4 p-4 rounded-2xl bg-orange-100 border-2 border-orange-400 flex items-center gap-3 hover:bg-orange-200 transition-colors text-left"
              data-testid="challenge-notification-banner"
            >
              <span className="text-3xl">⚔️</span>
              <span className="flex-1 font-bold text-orange-700">
                {notifications.friend_challenges[0]?.challenger_avatar} {notifications.friend_challenges[0]?.challenger_name} {t.challengedYouBanner}
                {notifications.count > 1 && ` (+${notifications.count - 1})`}
              </span>
              <span className="bg-orange-500 text-white text-sm font-bold rounded-full w-7 h-7 flex items-center justify-center">{notifications.count}</span>
            </button>
          )}

          {/* Guest expiry reminder: progress deleted after 7 days */}
          {selectedChild?.is_guest && !dismissExpiry && (() => {
            const created = selectedChild?.created_at ? new Date(selectedChild.created_at) : new Date();
            const daysLeft = Math.max(0, 7 - Math.floor((Date.now() - created.getTime()) / 86400000));
            return (
              <div className="w-full mb-4 p-4 rounded-2xl bg-gradient-to-r from-amber-100 to-orange-100 border-2 border-amber-300 flex items-center gap-3" data-testid="guest-expiry-banner">
                <span className="text-3xl">⏳</span>
                <div className="flex-1">
                  <p className="font-bold text-amber-800 text-sm">
                    {lang === "it"
                      ? (daysLeft > 0
                          ? `I tuoi progressi come ospite scadono tra ${daysLeft} ${daysLeft === 1 ? "giorno" : "giorni"}`
                          : "I tuoi progressi come ospite stanno per scadere")
                      : (daysLeft > 0
                          ? `Your guest progress expires in ${daysLeft} ${daysLeft === 1 ? "day" : "days"}`
                          : "Your guest progress is about to expire")}
                  </p>
                  <p className="text-amber-700 text-xs">
                    {lang === "it" ? "Salva l'account per non perdere monete, badge e statistiche." : "Save your account to keep coins, badges and stats."}
                  </p>
                </div>
                <button
                  onClick={openUpgrade}
                  className="bg-gradient-to-r from-amber-500 to-orange-500 text-white text-sm font-bold px-4 py-2 rounded-full hover:opacity-90 whitespace-nowrap"
                  data-testid="guest-expiry-save-btn"
                >
                  💾 {lang === "it" ? "Salva" : "Save"}
                </button>
                <button
                  onClick={() => setDismissExpiry(true)}
                  className="text-amber-500 hover:text-amber-700 text-lg"
                  data-testid="guest-expiry-dismiss-btn"
                  aria-label="dismiss"
                >
                  ✕
                </button>
              </div>
            );
          })()}

          {/* Profile Card */}
          <div className="bg-white rounded-3xl p-4 shadow-xl mb-4">
            <div className="flex items-center gap-4">
              <button
                onClick={() => setShowAvatarModal(true)}
                className="text-6xl hover:scale-110 transition-transform cursor-pointer relative group"
                data-testid="change-avatar-btn"
              >
                {selectedChild.avatar}
                <span className="absolute -bottom-1 left-1/2 -translate-x-1/2 text-xs bg-blue-500 text-white px-2 py-0.5 rounded-full opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap">
                  {t.tapToChange}
                </span>
              </button>
              <div className="flex-1">
                <h2 className="text-2xl font-bold text-gray-800">{t.welcome}, {selectedChild.name}!</h2>
                <p className="text-gray-500">
                  {selectedChild.age_group} {t.years}
                  {(selectedChild.streak_days || 0) >= 2 && (
                    <span className="ml-2 bg-orange-100 text-orange-600 text-sm font-bold px-2 py-0.5 rounded-full" data-testid="streak-flame">
                      🔥 {selectedChild.streak_days} {t.streakLabel}
                    </span>
                  )}
                </p>
                {(selectedChild.badges || []).length > 0 && (
                  <div className="flex gap-1 mt-1 flex-wrap items-center" data-testid="profile-badges-row">
                    {selectedChild.badges.map((b) => (
                      <span key={b} title={lang === "it" ? BADGE_DEFS[b]?.it : BADGE_DEFS[b]?.en} className="text-lg bg-amber-50 border border-amber-200 rounded-full px-1.5">
                        {BADGE_DEFS[b]?.emoji}
                      </span>
                    ))}
                    <button
                      onClick={shareVictoryCard}
                      title={t.shareVictories}
                      className="text-lg bg-purple-100 hover:bg-purple-200 border border-purple-200 rounded-full px-1.5 transition-colors"
                      data-testid="dashboard-share-btn"
                    >
                      📤
                    </button>
                  </div>
                )}
              </div>
            </div>
            
            {/* Stats Bar */}
            <div className="grid grid-cols-4 gap-2 mt-3">
              <div className="bg-yellow-100 rounded-xl p-2 text-center">
                <div className="text-xl">💰</div>
                <div className="font-bold text-yellow-700 text-sm" data-testid="dashboard-coins">{selectedChild.coins}</div>
                <div className="text-xs text-yellow-600">{t.coins}</div>
              </div>
              <div className="bg-purple-100 rounded-xl p-2 text-center">
                <div className="text-xl">🏆</div>
                <div className="font-bold text-purple-700 text-sm">{selectedChild.trophies}</div>
                <div className="text-xs text-purple-600">{t.trophies}</div>
              </div>
              <div className="bg-amber-100 rounded-xl p-2 text-center">
                <div className="text-xl">🥇</div>
                <div className="font-bold text-amber-700 text-sm">{selectedChild.gold_medals}</div>
                <div className="text-xs text-amber-600">{t.medals}</div>
              </div>
              <div className="bg-blue-100 rounded-xl p-2 text-center">
                <div className="text-xl">🧠</div>
                <div className="font-bold text-blue-700 text-sm">{selectedChild.iq_score}%</div>
                <div className="text-xs text-blue-600">QI</div>
              </div>
            </div>
          </div>

          {/* Engagement: livello, classifica settimanale, scrigno, nudge missioni */}
          <div className="space-y-3 mb-4" data-testid="engagement-panel">
            <LevelBar child={selectedChild} lang={lang} />
            {!selectedChild.is_guest && <WeeklyRankWidget rank={weeklyRank} lang={lang} onOpen={() => { setLbTab("weekly"); fetchWeeklyLb(); setView("leaderboard"); }} />}
            <div className="flex gap-3">
              {chestAvailable && (
                <button onClick={() => setChestOpen(true)} className="flex-1 bg-gradient-to-r from-amber-400 to-orange-500 text-white rounded-2xl p-3 font-black shadow-lg animate-pulse" data-testid="daily-chest-btn">
                  🎁 {lang === "it" ? "Apri lo scrigno del giorno!" : "Open today's chest!"}
                </button>
              )}
            </div>
            <MissionNudge missions={missions} lang={lang} onOpen={() => setMissionsCollapsed(false)} />
          </div>
          {levelUp && <LevelUpOverlay level={levelUp.level} bonus={levelUp.bonus} lang={lang} onClose={() => setLevelUp(null)} />}
          {chestOpen && <DailyChestModal onOpen={openDailyChest} onClose={() => setChestOpen(false)} streak={selectedChild.streak_days || 0} lang={lang} playSound={playSound} />}

          {/* Game Categories */}
          <h3 className="text-2xl font-bold text-white mb-4 text-center">{t.chooseGame}</h3>
          {selectedChild.dashboard_layout === "compact" && (isPro(selectedChild) || isPremium(selectedChild)) ? (
          <div className="space-y-2 mb-6">
            {[
              { key: "math", icon: "🔢", label: t.mathGames, action: () => { setSelectedCategory("math"); setView("gameCategory"); } },
              { key: "creative", icon: "🎨", label: t.creativeArea, action: () => { setSelectedCategory("creative"); setView("gameCategory"); } },
              { key: "puzzle", icon: "🧩", label: t.puzzleGames, action: () => setView("puzzle") },
              { key: "brain", icon: "🧠", label: t.brainGames, action: () => { setSelectedCategory("brain"); setView("gameCategory"); } },
              { key: "aiquiz", icon: "✨", label: t.aiQuiz, action: () => setView("aiQuiz") },
              { key: "checkers", icon: "🔴", label: t.checkersLabel, action: () => setView("checkers") },
              { key: "chess", icon: "♔", label: t.chessLabel, action: () => setView("chess") }
            ].map((cat) => (
              <button
                key={cat.key}
                onClick={cat.action}
                className="w-full bg-white/90 rounded-xl px-4 py-3 shadow flex items-center gap-4 hover:bg-white transition-colors text-left"
                data-testid={`category-${cat.key}`}
              >
                <span className="text-3xl">{cat.icon}</span>
                <span className="font-bold text-gray-800 flex-1">{cat.label}</span>
                <span className="text-gray-400">→</span>
              </button>
            ))}
          </div>
          ) : (
          <div className="grid grid-cols-2 gap-4 mb-6">
            {/* Math Games */}
            <button
              onClick={() => { setSelectedCategory("math"); setView("gameCategory"); }}
              className="bg-gradient-to-br from-green-400 to-emerald-600 rounded-2xl p-6 shadow-lg hover:scale-105 transition-transform text-white"
              data-testid="category-math"
            >
              <div className="text-5xl mb-2">🔢</div>
              <div className="font-bold text-lg">{t.mathGames}</div>
            </button>
            
            {/* Creative Area */}
            <button
              onClick={() => { setSelectedCategory("creative"); setView("gameCategory"); }}
              className="bg-gradient-to-br from-pink-400 to-rose-600 rounded-2xl p-6 shadow-lg hover:scale-105 transition-transform text-white"
              data-testid="category-creative"
            >
              <div className="text-5xl mb-2">🎨</div>
              <div className="font-bold text-lg">{t.creativeArea}</div>
            </button>
            
            {/* Puzzle Games */}
            <button
              onClick={() => setView("puzzle")}
              className="bg-gradient-to-br from-cyan-400 to-blue-600 rounded-2xl p-6 shadow-lg hover:scale-105 transition-transform text-white"
              data-testid="category-puzzle"
            >
              <div className="text-5xl mb-2">🧩</div>
              <div className="font-bold text-lg">{t.puzzleGames}</div>
            </button>
            
            {/* Brain Games */}
            <button
              onClick={() => { setSelectedCategory("brain"); setView("gameCategory"); }}
              className="bg-gradient-to-br from-purple-400 to-indigo-600 rounded-2xl p-6 shadow-lg hover:scale-105 transition-transform text-white"
              data-testid="category-brain"
            >
              <div className="text-5xl mb-2">🧠</div>
              <div className="font-bold text-lg">{t.brainGames}</div>
            </button>
            
            {/* AI Quiz */}
            <button
              onClick={() => setView("aiQuiz")}
              className="bg-gradient-to-br from-violet-500 to-fuchsia-600 rounded-2xl p-6 shadow-lg hover:scale-105 transition-transform text-white"
              data-testid="category-aiquiz"
            >
              <div className="text-5xl mb-2">✨</div>
              <div className="font-bold text-lg">{t.aiQuiz}</div>
              <div className="text-sm opacity-90">{t.aiQuizDesc}</div>
            </button>
            
            {/* Checkers */}
            <button
              onClick={() => setView("checkers")}
              className="bg-gradient-to-br from-stone-600 to-amber-800 rounded-2xl p-6 shadow-lg hover:scale-105 transition-transform text-white"
              data-testid="category-checkers"
            >
              <div className="text-5xl mb-2">🔴</div>
              <div className="font-bold text-lg">{t.checkersLabel}</div>
              <div className="text-sm opacity-90">🤖 vs AI</div>
            </button>

            {/* Chess */}
            <button
              onClick={() => setView("chess")}
              className="bg-gradient-to-br from-slate-700 to-emerald-800 rounded-2xl p-6 shadow-lg hover:scale-105 transition-transform text-white"
              data-testid="category-chess"
            >
              <div className="text-5xl mb-2">♔</div>
              <div className="font-bold text-lg">{t.chessLabel}</div>
              <div className="text-sm opacity-90">🧓→🦁 5 avversari</div>
            </button>
          </div>
          )}

          {/* Activity Feed */}
          <div className="bg-black/25 backdrop-blur rounded-2xl p-4 mb-6" data-testid="activity-feed">
            <h4 className="font-bold text-white mb-2 flex items-center gap-2">⚡ {t.activityFeed}</h4>
            {activities.length === 0 ? (
              <p className="text-white/70 text-sm">{t.noActivity}</p>
            ) : (
              <div className="space-y-1.5">
                {activities.slice(0, 5).map((a) => (
                  <div key={a.id} className="flex items-center gap-2 text-sm text-white" data-testid="activity-item">
                    <span className="text-lg">{a.avatar}</span>
                    <span className="flex-1 truncate">
                      <b>{a.child_name}</b> {lang === "it" ? a.text_it : a.text_en} {a.icon}
                    </span>
                    <span className="text-xs text-white/80 whitespace-nowrap">{timeAgo(a.created_at)}</span>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Daily Missions */}
          {missions.length > 0 && (
            <div className="bg-white/90 rounded-2xl p-3 mb-4" data-testid="missions-section">
              <button
                onClick={() => setMissionsCollapsed(v => !v)}
                className="w-full flex items-center gap-2 font-bold text-gray-800 mb-2"
                data-testid="missions-toggle-btn"
              >
                <span>🎯 {t.missionsTitle}</span>
                {missions.filter(m => m.completed && !m.claimed).length > 0 && (
                  <span className="bg-red-500 text-white text-xs font-bold rounded-full w-6 h-6 flex items-center justify-center animate-pulse" data-testid="missions-claimable-dot">
                    {missions.filter(m => m.completed && !m.claimed).length}
                  </span>
                )}
                <span className={`ml-auto text-gray-400 transition-transform ${missionsCollapsed ? "" : "rotate-180"}`}>▾</span>
              </button>
              {!missionsCollapsed && (
              <div className="space-y-1.5" data-testid="missions-list">
                {missions.map((m) => (
                  <div key={m.id} className="flex items-center gap-3 bg-gray-50 rounded-xl p-2" data-testid={`mission-${m.id}`}>
                    <span className="text-xl">{m.icon}</span>
                    <div className="flex-1">
                      <div className="font-bold text-sm text-gray-700">{lang === "it" ? m.text_it : m.text_en}</div>
                      <div className="w-full bg-gray-200 rounded-full h-1.5 mt-1">
                        <div className="bg-gradient-to-r from-purple-500 to-pink-500 h-1.5 rounded-full transition-all" style={{ width: `${(m.progress / m.target) * 100}%` }}></div>
                      </div>
                      <div className="text-xs text-gray-400">{m.progress}/{m.target}</div>
                    </div>
                    {m.claimed ? (
                      <span className="text-green-600 font-bold" data-testid={`mission-claimed-${m.id}`}>✅</span>
                    ) : m.completed ? (
                      <button
                        onClick={() => claimMission(m.id)}
                        className="bg-gradient-to-r from-amber-500 to-yellow-500 text-white px-3 py-1.5 rounded-lg font-bold text-xs animate-pulse"
                        data-testid={`claim-mission-${m.id}`}
                      >
                        💰 +{m.reward}
                      </button>
                    ) : (
                      <span className="text-gray-400 font-bold text-xs">+{m.reward} 💰</span>
                    )}
                  </div>
                ))}
              </div>
              )}
            </div>
          )}

          {/* La Settimana Enigmistica — area per i grandi */}
          <button
            onClick={() => setView("enigmistica")}
            className="w-full mb-6 bg-gradient-to-r from-stone-800 to-neutral-700 rounded-2xl p-4 shadow-lg hover:scale-[1.02] transition-transform text-white flex items-center gap-4"
            data-testid="category-enigmistica"
          >
            <span className="text-4xl">🧩</span>
            <span className="text-left flex-1">
              <span className="block font-bold text-lg">La Settimana Enigmistica</span>
              <span className="block text-sm opacity-80">Cruciverba, Sudoku, Rebus e Crucipuzzle — per i grandi</span>
            </span>
            <span className="text-white/60">→</span>
          </button>


          {incomingInvites.length > 0 && (
            <div className="mb-6 space-y-2" data-testid="incoming-invites">
              {incomingInvites.map((inv) => (
                <div key={inv.id} className="bg-yellow-300 text-gray-900 rounded-2xl p-4 shadow-lg flex items-center justify-between animate-pulse" data-testid={`invite-${inv.id}`}>
                  <div className="flex items-center gap-3">
                    <span className="text-3xl">{inv.challenger_avatar}</span>
                    <div className="font-bold">{inv.challenger_name} {t.challengedYou}</div>
                  </div>
                  <button
                    onClick={() => { setIncomingInvites((p) => p.filter((i) => i.id !== inv.id)); acceptChallenge(inv); }}
                    className="bg-green-600 text-white px-5 py-2 rounded-full font-bold hover:bg-green-700"
                    data-testid={`invite-accept-${inv.id}`}
                  >
                    ✓ {t.acceptChallenge}
                  </button>
                </div>
              ))}
            </div>
          )}

          {/* Online friends button */}
          <div className="mb-4">
            <button
              onClick={() => { fetchOnlinePlayers(); fetchVirtualFriends(); setView("online"); }}
              className="w-full bg-gradient-to-r from-green-500 via-teal-500 to-cyan-500 rounded-2xl p-4 shadow-lg hover:scale-102 transition-transform text-white"
              data-testid="online-friends-btn"
            >
              <div className="flex items-center justify-center gap-3">
                <span className="text-3xl">🟢</span>
                <div>
                  <div className="font-bold text-xl">{t.onlineFriends}</div>
                  <div className="text-sm opacity-90">{t.onlineFriendsDesc}</div>
                </div>
              </div>
            </button>
          </div>


          {/* Multiplayer Local Button */}
          <div className="mb-6">
            <button
              onClick={() => setShowMultiplayerSetup(true)}
              className="w-full bg-gradient-to-r from-pink-500 via-red-500 to-yellow-500 rounded-2xl p-4 shadow-lg hover:scale-102 transition-transform text-white"
              data-testid="multiplayer-btn"
            >
              <div className="flex items-center justify-center gap-3">
                <span className="text-3xl">👥</span>
                <div>
                  <div className="font-bold text-xl">{t.multiplayerMode}</div>
                  <div className="text-sm opacity-90">{t.multiplayerDesc}</div>
                </div>
              </div>
            </button>
          </div>

          {/* Navigation Buttons */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            <button
              onClick={() => { fetchLeaderboard(selectedChild.age_group); setView("leaderboard"); }}
              className="bg-white/20 backdrop-blur rounded-xl p-4 text-white hover:bg-white/30 transition-colors"
              data-testid="leaderboard-btn"
            >
              <div className="text-3xl mb-2">📊</div>
              <div className="font-bold">{t.leaderboard}</div>
            </button>
            <button
              onClick={fetchStats}
              className="bg-white/20 backdrop-blur rounded-xl p-4 text-white hover:bg-white/30 transition-colors"
              data-testid="stats-btn"
            >
              <div className="text-3xl mb-2">📈</div>
              <div className="font-bold">{t.stats}</div>
            </button>
            <button
              onClick={() => { fetchCompetitions(); setView("competitions"); }}
              className="bg-white/20 backdrop-blur rounded-xl p-4 text-white hover:bg-white/30 transition-colors"
              data-testid="competitions-btn"
            >
              <div className="text-3xl mb-2">🏅</div>
              <div className="font-bold">{t.competitions}</div>
            </button>
            <button
              onClick={() => { fetchShopItems(); fetchInventory(); setView("shop"); }}
              className="bg-white/20 backdrop-blur rounded-xl p-4 text-white hover:bg-white/30 transition-colors"
              data-testid="shop-btn"
            >
              <div className="text-3xl mb-2">🛒</div>
              <div className="font-bold">{t.shop}</div>
            </button>
            <button
              onClick={() => { fetchPendingChallenges(); fetchActiveChallenges(); fetchChallengeHistory(); setView("challenges"); }}
              className="bg-white/20 backdrop-blur rounded-xl p-4 text-white hover:bg-white/30 transition-colors relative"
              data-testid="challenges-btn"
            >
              {notifications?.count > 0 && (
                <span className="absolute -top-2 -right-2 bg-red-500 text-white text-xs font-bold rounded-full w-6 h-6 flex items-center justify-center" data-testid="challenges-notification-badge">
                  {notifications.count}
                </span>
              )}
              <div className="text-3xl mb-2">⚔️</div>
              <div className="font-bold">{t.challenges}</div>
            </button>
          </div>

          {/* Social & Tournaments Navigation */}
          <div className="grid grid-cols-3 gap-4 mb-6">
            <button
              onClick={() => { if (selectedChild?.is_guest) { setShowGuestGate(true); return; } fetchTournaments(); setView("tournaments"); }}
              className="bg-gradient-to-br from-yellow-400 to-orange-500 rounded-xl p-4 text-white hover:scale-105 transition-transform shadow-lg"
              data-testid="tournaments-btn"
            >
              <div className="text-3xl mb-2">🏆</div>
              <div className="font-bold">{t.tournaments}</div>
            </button>
            <button
              onClick={() => { if (selectedChild?.is_guest) { setShowGuestGate(true); return; } fetchFriends(); fetchFriendRequests(); fetchVirtualFriends(); setView("friends"); }}
              className="bg-gradient-to-br from-blue-400 to-indigo-500 rounded-xl p-4 text-white hover:scale-105 transition-transform shadow-lg"
              data-testid="friends-btn"
            >
              <div className="text-3xl mb-2">👫</div>
              <div className="font-bold">{t.friends}</div>
            </button>
            <button
              onClick={() => { if (selectedChild?.is_guest) { setShowGuestGate(true); return; } fetchMyGroups(); fetchOpenGroups(); fetchGroupInvites(); setView("groups"); }}
              className="bg-gradient-to-br from-green-400 to-teal-500 rounded-xl p-4 text-white hover:scale-105 transition-transform shadow-lg"
              data-testid="groups-btn"
            >
              <div className="text-3xl mb-2">👥</div>
              <div className="font-bold">{t.groups}</div>
            </button>
          </div>

          {/* Premium subscription CTA */}
          <div className="mb-6">
            <button
              onClick={() => { if (selectedChild?.is_guest) { setShowGuestGate(true); return; } setView("premium"); }}
              className="w-full bg-gradient-to-r from-amber-500 via-yellow-500 to-orange-500 rounded-2xl p-4 shadow-lg hover:scale-102 transition-transform text-white"
              data-testid="premium-btn"
            >
              <div className="flex items-center justify-center gap-3">
                <span className="text-3xl">👑</span>
                <div className="text-left">
                  <div className="font-bold text-xl">
                    {isPremium(selectedChild)
                      ? (lang === "it" ? "Premium attivo" : "Premium active")
                      : (lang === "it" ? "Passa a Premium" : "Go Premium")}
                  </div>
                  <div className="text-sm opacity-90">
                    {isPremium(selectedChild)
                      ? (lang === "it" ? "Tutti i contenuti sbloccati 🎉" : "All content unlocked 🎉")
                      : (lang === "it" ? "Sblocca tutti i contenuti di CelestApp" : "Unlock all CelestApp content")}
                  </div>
                </div>
              </div>
            </button>
          </div>
        </div>

        {/* Payment result (return from Stripe Checkout → /dashboard) */}
        <PaymentResultModal result={paymentResult} lang={lang} onClose={() => setPaymentResult(null)} />

        {/* Guest gate modal: social/paid features require an account */}
        {showGuestGate && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50" data-testid="guest-gate-modal">
            <div className="bg-white rounded-3xl p-6 md:p-8 max-w-md w-full text-center">
              <div className="text-5xl mb-3">🔒</div>
              <h3 className="text-2xl font-bold text-purple-600 mb-2">
                {lang === "it" ? "Serve un account" : "Account needed"}
              </h3>
              <p className="text-gray-700 font-semibold mb-1">
                {lang === "it" ? "Registrati per amici, gruppi, tornei e sfide!" : "Sign up for friends, groups, tournaments and challenges!"}
              </p>
              <p className="text-gray-500 text-sm mb-6">
                {lang === "it" ? "Giochi come ospite: puoi giocare liberamente, ma la modalità social richiede la registrazione." : "You're playing as a guest: free play is open, but social mode requires signing up."}
              </p>
              <div className="flex flex-col gap-3">
                <button
                  onClick={openUpgrade}
                  className="bg-gradient-to-r from-purple-600 to-pink-500 text-white px-6 py-3 rounded-2xl font-bold hover:opacity-90"
                  data-testid="guest-gate-register-btn"
                >
                  💾 {lang === "it" ? "Salva i progressi e registrati" : "Save progress & sign up"}
                </button>
                <button
                  onClick={logout}
                  className="text-purple-600 font-semibold text-sm hover:underline"
                  data-testid="guest-gate-login-btn"
                >
                  {lang === "it" ? "Ho già un account → Accedi" : "I already have an account → Log in"}
                </button>
                <button
                  onClick={() => setShowGuestGate(false)}
                  className="text-gray-500 font-semibold hover:text-gray-700"
                  data-testid="guest-gate-close-btn"
                >
                  {lang === "it" ? "Continua a giocare come ospite" : "Keep playing as guest"}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Upgrade guest -> registered account (keeps coins & stats) */}
        {showUpgrade && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50" data-testid="upgrade-modal">
            <div className="bg-white rounded-3xl p-6 md:p-8 max-w-md w-full">
              <div className="text-center mb-4">
                <div className="text-5xl mb-2">💾</div>
                <h3 className="text-2xl font-bold text-purple-600">
                  {lang === "it" ? "Salva i tuoi progressi" : "Save your progress"}
                </h3>
                <p className="text-gray-600 text-sm mt-1">
                  {lang === "it"
                    ? `Mantieni le tue ${selectedChild?.coins ?? 0} monete e le statistiche creando un account.`
                    : `Keep your ${selectedChild?.coins ?? 0} coins and stats by creating an account.`}
                </p>
              </div>
              <div className="space-y-3">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">{lang === "it" ? "Nome giocatore" : "Player name"}</label>
                  <input
                    type="text"
                    value={upgradeForm.name}
                    onChange={(e) => setUpgradeForm({ ...upgradeForm, name: e.target.value })}
                    placeholder={lang === "it" ? "Es. SuperMario7" : "e.g. SuperMario7"}
                    className="w-full border-2 border-purple-200 rounded-xl px-4 py-2 focus:border-purple-500 outline-none"
                    data-testid="upgrade-name-input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">Password</label>
                  <input
                    type="password"
                    value={upgradeForm.password}
                    onChange={(e) => setUpgradeForm({ ...upgradeForm, password: e.target.value })}
                    placeholder={lang === "it" ? "Almeno 4 caratteri" : "At least 4 characters"}
                    className="w-full border-2 border-purple-200 rounded-xl px-4 py-2 focus:border-purple-500 outline-none"
                    data-testid="upgrade-password-input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">{lang === "it" ? "Età" : "Age"}</label>
                  <input
                    type="number"
                    min="5"
                    max="16"
                    value={upgradeForm.age}
                    onChange={(e) => setUpgradeForm({ ...upgradeForm, age: e.target.value })}
                    className="w-full border-2 border-purple-200 rounded-xl px-4 py-2 focus:border-purple-500 outline-none"
                    data-testid="upgrade-age-input"
                  />
                </div>
                {upgradeError && (
                  <p className="text-red-600 text-sm font-semibold" data-testid="upgrade-error">{upgradeError}</p>
                )}
                <button
                  onClick={handleUpgrade}
                  disabled={upgradeLoading}
                  className="w-full bg-gradient-to-r from-purple-600 to-pink-500 text-white px-6 py-3 rounded-2xl font-bold hover:opacity-90 disabled:opacity-60"
                  data-testid="upgrade-submit-btn"
                >
                  {upgradeLoading ? (lang === "it" ? "Salvataggio..." : "Saving...") : (lang === "it" ? "✨ Crea account e salva" : "✨ Create account & save")}
                </button>
                <button
                  onClick={() => setShowUpgrade(false)}
                  className="w-full text-gray-500 font-semibold hover:text-gray-700"
                  data-testid="upgrade-cancel-btn"
                >
                  {lang === "it" ? "Annulla" : "Cancel"}
                </button>
              </div>
            </div>
          </div>
        )}


        {/* Multiplayer Setup Modal */}
        {showMultiplayerSetup && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-3xl p-6 md:p-8 max-w-lg w-full max-h-[90vh] overflow-y-auto">
              <h3 className="text-2xl font-bold text-pink-600 mb-4 flex items-center gap-2">
                👥 {t.multiplayerMode}
              </h3>
              <p className="text-gray-500 mb-6">{t.multiplayerDesc}</p>
              
              {/* Add Player */}
              <div className="flex gap-2 mb-4">
                <input
                  type="text"
                  value={newPlayerName}
                  onChange={(e) => setNewPlayerName(e.target.value)}
                  onKeyPress={(e) => e.key === "Enter" && addMultiplayerPlayer()}
                  placeholder={t.name}
                  className="flex-1 px-4 py-3 rounded-xl border-2 border-pink-200 focus:border-pink-500 focus:outline-none"
                  data-testid="multiplayer-player-input"
                />
                <button
                  onClick={addMultiplayerPlayer}
                  disabled={!newPlayerName.trim() || multiplayerPlayers.length >= 4}
                  className="px-6 py-3 rounded-xl bg-pink-500 text-white font-bold hover:bg-pink-600 disabled:opacity-50"
                  data-testid="add-player-btn"
                >
                  {t.addPlayer}
                </button>
              </div>
              
              {/* Players List */}
              <div className="space-y-2 mb-6">
                {multiplayerPlayers.map((player, idx) => (
                  <div key={player.id || player.name || idx} className="flex items-center justify-between bg-gray-50 rounded-xl p-3" data-testid={`multiplayer-player-item-${idx}`}>
                    <div className="flex items-center gap-3">
                      <span className="text-2xl">{player.avatar}</span>
                      <span className="font-bold text-gray-700">{player.name}</span>
                    </div>
                    <button
                      onClick={() => removeMultiplayerPlayer(idx)}
                      className="text-red-500 hover:text-red-700 font-bold"
                      data-testid={`remove-player-${idx}`}
                    >
                      {t.removePlayer}
                    </button>
                  </div>
                ))}
                {multiplayerPlayers.length < 2 && (
                  <p className="text-center text-gray-400 py-4">{t.addAtLeast2Players}</p>
                )}
              </div>
              
              {/* Game Type Selection */}
              {multiplayerPlayers.length >= 2 && (
                <div className="mb-6">
                  <h4 className="font-bold text-gray-700 mb-3">{t.chooseGame}</h4>
                  <div className="grid grid-cols-3 gap-2">
                    {[
                      { type: "addition", icon: "➕" },
                      { type: "subtraction", icon: "➖" },
                      { type: "multiplication", icon: "✖️" },
                      { type: "division", icon: "➗" },
                      { type: "mixed", icon: "🎲" }
                    ].map((game) => (
                      <button
                        key={game.type}
                        onClick={() => startMultiplayerGame(game.type)}
                        disabled={loading}
                        className="bg-gradient-to-br from-pink-400 to-red-500 rounded-xl p-3 text-white hover:scale-105 transition-transform"
                        data-testid={`multiplayer-game-${game.type}`}
                      >
                        <div className="text-2xl">{game.icon}</div>
                        <div className="text-xs font-bold">{t.gameTypes[game.type]}</div>
                      </button>
                    ))}
                  </div>
                </div>
              )}
              
              <button
                onClick={() => { setShowMultiplayerSetup(false); setMultiplayerPlayers([]); }}
                className="w-full px-4 py-3 rounded-xl border-2 border-gray-300 text-gray-600 font-bold hover:bg-gray-100"
                data-testid="multiplayer-cancel-btn"
              >
                {t.cancel}
              </button>
            </div>
          </div>
        )}

        {/* Link Challenge Modal */}
        {linkChallenge && (
          <div className="fixed inset-0 bg-black/60 flex items-center justify-center p-4 z-50" data-testid="link-challenge-modal">
            <div className="bg-white rounded-3xl p-8 max-w-md w-full text-center">
              <div className="text-6xl mb-3">{linkChallenge.challenger_avatar}</div>
              <h3 className="text-2xl font-bold text-purple-600 mb-2">⚔️ {linkChallenge.challenger_name} {t.challengedYou}</h3>
              <p className="text-gray-500 mb-6">{t.gameTypes[linkChallenge.game_type]} · {linkChallenge.age_group}</p>
              <div className="flex gap-3">
                <button
                  onClick={() => setLinkChallenge(null)}
                  className="flex-1 px-4 py-3 rounded-xl border-2 border-gray-300 text-gray-600 font-bold hover:bg-gray-100"
                  data-testid="decline-link-challenge-btn"
                >
                  {t.notNow}
                </button>
                <button
                  onClick={() => { const c = linkChallenge; setLinkChallenge(null); acceptChallenge(c); }}
                  className="flex-1 px-4 py-3 rounded-xl bg-gradient-to-r from-green-500 to-emerald-500 text-white font-bold hover:opacity-90"
                  data-testid="accept-link-challenge-btn"
                >
                  ⚔️ {t.acceptAndPlay}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Avatar Change Modal */}
        {showAvatarModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-3xl p-6 md:p-8 max-w-md w-full max-h-[90vh] overflow-y-auto">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-2xl font-bold text-purple-600">{t.changeAvatar}</h3>
                <button
                  onClick={() => setShowAvatarModal(false)}
                  className="text-gray-400 hover:text-gray-600 text-2xl"
                  data-testid="close-avatar-modal"
                >
                  ✕
                </button>
              </div>
              
              <p className="text-gray-500 mb-4 text-center">{t.selectNewAvatar}</p>
              
              {/* Current Avatar */}
              <div className="text-center mb-4">
                <div className="text-6xl mb-2">{selectedChild.avatar}</div>
                <div className="text-sm text-gray-500">{selectedChild.name}</div>
              </div>
              
              {/* Avatar Grid */}
              <div className="grid grid-cols-6 gap-2">
                {avatars.map((avatar) => (
                  <button
                    key={avatar}
                    onClick={() => changeAvatar(avatar)}
                    disabled={loading || avatar === selectedChild.avatar}
                    className={`text-3xl p-2 rounded-xl transition-all ${
                      avatar === selectedChild.avatar 
                        ? "bg-purple-500 scale-110 ring-2 ring-purple-300" 
                        : "hover:bg-purple-100 hover:scale-105"
                    } ${loading ? "opacity-50" : ""}`}
                    data-testid={`avatar-select-${avatar}`}
                  >
                    {avatar}
                  </button>
                ))}
              </div>
              
              {/* Premium-exclusive avatars */}
              <div className="mt-5 pt-4 border-t border-amber-200">
                <div className="flex items-center justify-between mb-2">
                  <span className="font-bold text-amber-600 flex items-center gap-1">👑 {lang === "it" ? "Avatar Premium" : "Premium avatars"}</span>
                  {!isPremium(selectedChild) && (
                    <button
                      onClick={() => { setShowAvatarModal(false); if (selectedChild?.is_guest) { setShowGuestGate(true); } else { setView("premium"); } }}
                      className="text-xs bg-amber-500 text-white px-2 py-1 rounded-full font-bold hover:bg-amber-600"
                      data-testid="avatar-premium-upsell-btn"
                    >
                      {lang === "it" ? "Sblocca" : "Unlock"}
                    </button>
                  )}
                </div>
                <div className="grid grid-cols-6 gap-2">
                  {premiumAvatars.map((avatar) => {
                    const locked = !isPremium(selectedChild);
                    return (
                      <button
                        key={avatar}
                        onClick={() => { if (locked) { setShowAvatarModal(false); if (selectedChild?.is_guest) { setShowGuestGate(true); } else { setView("premium"); } } else { changeAvatar(avatar); } }}
                        disabled={loading || avatar === selectedChild.avatar}
                        className={`relative text-3xl p-2 rounded-xl transition-all ${
                          avatar === selectedChild.avatar
                            ? "bg-amber-500 scale-110 ring-2 ring-amber-300"
                            : "hover:bg-amber-100 hover:scale-105"
                        } ${locked ? "opacity-50 grayscale" : ""}`}
                        data-testid={`avatar-premium-${avatar}`}
                        title={locked ? (lang === "it" ? "Riservato ai Premium" : "Premium only") : ""}
                      >
                        {avatar}
                        {locked && <span className="absolute -top-1 -right-1 text-xs">🔒</span>}
                      </button>
                    );
                  })}
                </div>
              </div>
              
              <p className="text-xs text-gray-400 text-center mt-4">
                {lang === "it" ? "Altri avatar disponibili nel Negozio!" : "More avatars available in the Shop!"}
              </p>
            </div>
          </div>
        )}
      </div>
    );
  }

  // Settings Screen
  if (view === "settings" && selectedChild) {
    const allBadgeIds = Object.keys(BADGE_DEFS);
    const myBadges = selectedChild.badges || [];
    return (
      <div className={`min-h-screen ${bgClass("bg-gradient-to-br from-slate-700 via-slate-600 to-slate-500")} p-4`}>
        {/* Full-screen Premium theme preview */}
        {previewTheme && (
          <div className={`fixed inset-0 z-50 bg-gradient-to-br ${previewTheme.cls} flex flex-col items-center justify-center p-6`} data-testid="theme-preview-overlay">
            <button
              onClick={() => setPreviewTheme(null)}
              className="absolute top-5 right-5 text-white/90 hover:text-white text-3xl"
              data-testid="theme-preview-close-btn"
              aria-label="close"
            >
              ✕
            </button>
            <div className="text-center">
              <div className="text-6xl mb-3">🎨</div>
              <p className="text-white/80 font-semibold mb-1">{lang === "it" ? "Anteprima tema Premium" : "Premium theme preview"}</p>
              <h2 className="text-4xl font-bold text-white mb-2">{lang === "it" ? previewTheme.label_it : previewTheme.label_en}</h2>
              <p className="text-white/80 mb-8 max-w-sm">
                {lang === "it" ? "Ecco come apparirà l'app con questo tema." : "This is how the app will look with this theme."}
              </p>
            </div>
            {/* Mock preview card to show the theme in context */}
            <div className="bg-white/90 backdrop-blur rounded-3xl p-5 w-full max-w-sm shadow-2xl mb-8">
              <div className="flex items-center gap-3 mb-3">
                <span className="text-4xl">{selectedChild.avatar}</span>
                <div>
                  <div className="font-bold text-gray-800">{selectedChild.name}</div>
                  <div className="text-xs text-gray-500">{selectedChild.age_group} {t.years}</div>
                </div>
              </div>
              <div className={`rounded-2xl h-16 bg-gradient-to-br ${previewTheme.cls}`}></div>
            </div>
            {isPremium(selectedChild) ? (
              <button
                onClick={() => { savePreferences({ bg_color: previewTheme.key }); setPreviewTheme(null); }}
                className="bg-white text-gray-800 px-8 py-3 rounded-2xl font-bold text-lg hover:scale-105 transition-transform shadow-lg"
                data-testid="theme-preview-apply-btn"
              >
                ✓ {lang === "it" ? "Applica questo tema" : "Apply this theme"}
              </button>
            ) : (
              <div className="text-center">
                <button
                  onClick={() => { setPreviewTheme(null); if (selectedChild?.is_guest) { setShowGuestGate(true); } else { setView("premium"); } }}
                  className="bg-gradient-to-r from-amber-400 to-yellow-500 text-white px-8 py-3 rounded-2xl font-bold text-lg hover:scale-105 transition-transform shadow-lg"
                  data-testid="theme-preview-unlock-btn"
                >
                  👑 {lang === "it" ? "Sblocca con Premium" : "Unlock with Premium"}
                </button>
                <p className="text-white/80 text-sm mt-3">
                  {lang === "it" ? "Abbonati per usare tutti i temi esclusivi" : "Subscribe to use all exclusive themes"}
                </p>
              </div>
            )}
          </div>
        )}

        <div className="max-w-2xl mx-auto">
          <button
            onClick={() => setView("gameSelect")}
            className="bg-white/20 text-white px-4 py-2 rounded-full font-bold hover:bg-white/30 transition-colors mb-6"
            data-testid="settings-back-btn"
          >
            ← {t.backHome}
          </button>

          <h2 className="text-3xl font-bold text-white text-center mb-6">⚙️ {t.settings}</h2>

          {/* Edit Profile */}
          <div className="bg-white rounded-3xl p-6 shadow-xl mb-6" data-testid="settings-profile-section">
            <h3 className="text-xl font-bold text-gray-800 mb-4">👤 {t.editProfile}</h3>
            <div className="flex items-center gap-4 mb-4">
              <button
                onClick={() => setShowAvatarModal(true)}
                className="text-6xl hover:scale-110 transition-transform"
                data-testid="settings-avatar-btn"
              >
                {selectedChild.avatar}
              </button>
              <span className="text-sm text-gray-400">{t.tapToChange}</span>
            </div>
            <label className="block text-gray-700 font-medium mb-1">{t.name}</label>
            <input
              type="text"
              value={settingsForm.name}
              onChange={(e) => setSettingsForm({ ...settingsForm, name: e.target.value })}
              className="w-full px-4 py-3 rounded-xl border-2 border-purple-200 focus:border-purple-500 focus:outline-none mb-3"
              data-testid="settings-name-input"
            />
            <label className="block text-gray-700 font-medium mb-1">{t.emailOptional}</label>
            <input
              type="email"
              value={settingsForm.email}
              onChange={(e) => setSettingsForm({ ...settingsForm, email: e.target.value })}
              placeholder="genitore@email.it"
              className="w-full px-4 py-3 rounded-xl border-2 border-purple-200 focus:border-purple-500 focus:outline-none"
              data-testid="settings-email-input"
            />
            <button
              onClick={saveSettings}
              disabled={loading || !settingsForm.name.trim()}
              className="mt-4 w-full py-3 rounded-xl bg-gradient-to-r from-purple-600 to-pink-500 text-white font-bold hover:opacity-90 transition-opacity disabled:opacity-50"
              data-testid="settings-save-btn"
            >
              {settingsSaved ? t.savedOk : t.saveChanges}
            </button>
          </div>

          {/* Badges */}
          <div className="bg-white rounded-3xl p-6 shadow-xl mb-6" data-testid="settings-badges-section">
            <div className="flex justify-between items-center mb-1">
              <h3 className="text-xl font-bold text-gray-800">🏅 {t.myBadges} ({myBadges.length}/{allBadgeIds.length})</h3>
              <button
                onClick={shareVictoryCard}
                className="bg-gradient-to-r from-purple-600 to-pink-500 text-white px-4 py-2 rounded-full font-bold text-sm hover:opacity-90"
                data-testid="share-victory-btn"
              >
                📤 {t.shareVictories}
              </button>
            </div>
            {myBadges.length === 0 && <p className="text-sm text-gray-400 mb-3">{t.noBadgesYet}</p>}
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mt-3">
              {allBadgeIds.map((id) => {
                const def = BADGE_DEFS[id];
                const unlocked = myBadges.includes(id);
                return (
                  <div
                    key={id}
                    className={`rounded-xl p-3 text-center border-2 ${unlocked ? "bg-amber-50 border-amber-300" : "bg-gray-100 border-gray-200 grayscale"}`}
                    data-testid={`badge-${id}`}
                  >
                    <div className="text-3xl mb-1">{unlocked ? def.emoji : "🔒"}</div>
                    <div className="font-bold text-sm text-gray-800">{lang === "it" ? def.it : def.en}</div>
                    <div className="text-xs text-gray-500">{lang === "it" ? def.descIt : def.descEn}</div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Personalization */}
          <div className="bg-white rounded-3xl p-6 shadow-xl mb-6" data-testid="settings-personalization-section">
            <h3 className="text-xl font-bold text-gray-800 mb-4">🎨 {t.personalization}</h3>
            <p className="font-medium text-gray-700 mb-2 flex items-center gap-2">
              {t.bgColorTitle}
              {isPro(selectedChild) ? (
                <span className="bg-gradient-to-r from-amber-400 to-yellow-500 text-white text-xs px-2 py-0.5 rounded-full font-bold">👑 PRO</span>
              ) : (
                <span className="text-gray-400">🔒</span>
              )}
            </p>
            {!isPro(selectedChild) && !isPremium(selectedChild) && (
              <div className="bg-gradient-to-r from-amber-50 to-yellow-50 border-2 border-amber-300 rounded-xl p-4 mb-4" data-testid="pro-upsell">
                <p className="text-sm text-gray-700 font-medium mb-3">👑 {t.proDesc}</p>
                {proError && <p className="text-sm text-red-600 font-bold mb-2" data-testid="pro-required-error">{t.proRequired}</p>}
                <button
                  onClick={buyPro}
                  disabled={loading}
                  className="w-full py-3 rounded-xl bg-gradient-to-r from-amber-500 to-yellow-500 text-white font-bold hover:opacity-90 disabled:opacity-50"
                  data-testid="become-pro-btn"
                >
                  👑 {t.becomePro} — {t.proPerMonth}
                </button>
              </div>
            )}
            {isPro(selectedChild) && (
              <p className="text-xs text-amber-600 font-bold mb-3" data-testid="pro-active-label">
                👑 {t.proActiveUntil} {new Date(selectedChild.pro_until).toLocaleDateString(lang === "it" ? "it-IT" : "en-GB")}
              </p>
            )}
            <div className={`flex gap-4 mb-2 flex-wrap ${!(isPro(selectedChild) || isPremium(selectedChild)) ? "opacity-60" : ""}`}>
              {BG_PREVIEW.map((c) => (
                <button
                  key={c.key}
                  onClick={() => savePreferences({ bg_color: c.key })}
                  className="flex flex-col items-center gap-1 group"
                  data-testid={`bg-color-${c.key}`}
                >
                  <span className={`w-14 h-14 rounded-full bg-gradient-to-br ${c.cls} shadow-md border-4 transition-transform group-hover:scale-110 flex items-center justify-center ${selectedChild.bg_color === c.key ? "border-gray-800" : "border-white"}`}>
                    {selectedChild.bg_color === c.key && <span className="text-white text-xl font-bold">✓</span>}
                  </span>
                  <span className="text-xs text-gray-600 font-medium">{lang === "it" ? c.label_it : c.label_en}</span>
                </button>
              ))}
            </div>
            <button
              onClick={() => savePreferences({ bg_color: "default" })}
              className="text-sm text-gray-500 underline px-3 py-2 rounded-full hover:bg-gray-100"
              data-testid="bg-color-default"
            >
              {t.defaultColor}
            </button>

            {/* Premium-exclusive themes */}
            <div className="mt-6 pt-5 border-t border-amber-200">
              <p className="font-medium text-gray-700 mb-1 flex items-center gap-2">
                👑 {lang === "it" ? "Temi Premium" : "Premium themes"}
                {!isPremium(selectedChild) && <span className="text-gray-400">🔒</span>}
              </p>
              {!isPremium(selectedChild) && (
                <button
                  onClick={() => { if (selectedChild?.is_guest) { setShowGuestGate(true); } else { setView("premium"); } }}
                  className="text-sm bg-amber-500 text-white font-bold px-3 py-1.5 rounded-full hover:bg-amber-600 mb-3"
                  data-testid="premium-themes-unlock-btn"
                >
                  {lang === "it" ? "Sblocca con Premium" : "Unlock with Premium"}
                </button>
              )}
              <div className="flex gap-4 flex-wrap mt-2">
                {PREMIUM_THEME_PREVIEW.map((th) => {
                  const locked = !isPremium(selectedChild);
                  return (
                    <button
                      key={th.key}
                      onClick={() => setPreviewTheme(th)}
                      className="flex flex-col items-center gap-1 group"
                      data-testid={`premium-theme-${th.key}`}
                    >
                      <span className={`relative w-14 h-14 rounded-full bg-gradient-to-br ${th.cls} shadow-md border-4 transition-transform group-hover:scale-110 flex items-center justify-center ${selectedChild.bg_color === th.key ? "border-gray-800" : "border-white"} ${locked ? "grayscale opacity-60" : ""}`}>
                        {selectedChild.bg_color === th.key && <span className="text-white text-xl font-bold">✓</span>}
                        {locked && <span className="absolute -top-1 -right-1 text-sm">🔒</span>}
                      </span>
                      <span className="text-xs text-gray-600 font-medium">{lang === "it" ? th.label_it : th.label_en}</span>
                    </button>
                  );
                })}
              </div>
            </div>

            <p className="font-medium text-gray-700 mb-2 mt-6">{t.layoutTitle} {!(isPro(selectedChild) || isPremium(selectedChild)) && <span className="text-gray-400">🔒</span>}</p>
            <div className={`grid grid-cols-2 gap-4 ${!(isPro(selectedChild) || isPremium(selectedChild)) ? "opacity-60" : ""}`}>
              <button
                onClick={() => savePreferences({ dashboard_layout: "compact" })}
                className={`rounded-xl p-4 border-2 transition-colors ${selectedChild.dashboard_layout === "compact" ? "border-purple-500 bg-purple-50" : "border-gray-200 hover:border-purple-300"}`}
                data-testid="layout-compact"
              >
                <div className="space-y-1.5 mb-3">
                  <div className="h-2.5 bg-gray-300 rounded w-full"></div>
                  <div className="h-2.5 bg-gray-300 rounded w-full"></div>
                  <div className="h-2.5 bg-gray-300 rounded w-full"></div>
                </div>
                <div className="font-bold text-sm text-gray-700">📋 {t.layoutCompact}</div>
              </button>
              <button
                onClick={() => savePreferences({ dashboard_layout: "expanded" })}
                className={`rounded-xl p-4 border-2 transition-colors ${selectedChild.dashboard_layout !== "compact" ? "border-purple-500 bg-purple-50" : "border-gray-200 hover:border-purple-300"}`}
                data-testid="layout-expanded"
              >
                <div className="grid grid-cols-2 gap-1.5 mb-3">
                  <div className="h-5 bg-gray-300 rounded"></div>
                  <div className="h-5 bg-gray-300 rounded"></div>
                  <div className="h-5 bg-gray-300 rounded"></div>
                  <div className="h-5 bg-gray-300 rounded"></div>
                </div>
                <div className="font-bold text-sm text-gray-700">🗂️ {t.layoutExpanded}</div>
              </button>
            </div>
          </div>

          {/* Danger Zone */}
          <div className="bg-white rounded-3xl p-6 shadow-xl mb-6 border-4 border-red-300" data-testid="danger-zone-section">
            <h3 className="text-xl font-bold text-red-600 mb-2">⚠️ {t.dangerZone}</h3>
            <p className="text-sm text-gray-500 mb-4">{t.deleteWarning}</p>
            <button
              onClick={() => setDeleteStep(1)}
              className="w-full py-3 rounded-xl bg-red-600 text-white font-bold hover:bg-red-700 transition-colors"
              data-testid="delete-account-btn"
            >
              🗑️ {t.deleteAccount}
            </button>
          </div>
        </div>

        {/* Delete Confirmation Step 1 */}
        {deleteStep === 1 && (
          <div className="fixed inset-0 bg-black/60 flex items-center justify-center p-4 z-50" data-testid="delete-modal-1">
            <div className="bg-white rounded-3xl p-8 max-w-md w-full text-center">
              <div className="text-5xl mb-3">⚠️</div>
              <h3 className="text-2xl font-bold text-red-600 mb-2">{t.deleteConfirm1Title}</h3>
              <p className="text-gray-600 mb-6">{t.deleteConfirm1}</p>
              <div className="flex gap-3">
                <button
                  onClick={() => setDeleteStep(0)}
                  className="flex-1 px-4 py-3 rounded-xl border-2 border-gray-300 text-gray-600 font-bold hover:bg-gray-100"
                  data-testid="delete-cancel-btn"
                >
                  {t.cancel}
                </button>
                <button
                  onClick={() => setDeleteStep(2)}
                  className="flex-1 px-4 py-3 rounded-xl bg-red-500 text-white font-bold hover:bg-red-600"
                  data-testid="delete-confirm-1-btn"
                >
                  {t.yesContinue}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Delete Confirmation Step 2 (final) */}
        {deleteStep === 2 && (
          <div className="fixed inset-0 bg-black/70 flex items-center justify-center p-4 z-50" data-testid="delete-modal-2">
            <div className="bg-white rounded-3xl p-8 max-w-md w-full text-center border-4 border-red-500">
              <div className="text-5xl mb-3">🚨</div>
              <h3 className="text-2xl font-bold text-red-600 mb-2">{t.deleteConfirm2Title}</h3>
              <p className="text-gray-600 mb-6">{t.deleteConfirm2}</p>
              <div className="flex gap-3">
                <button
                  onClick={() => setDeleteStep(0)}
                  className="flex-1 px-4 py-3 rounded-xl border-2 border-gray-300 text-gray-600 font-bold hover:bg-gray-100"
                  data-testid="delete-cancel-2-btn"
                >
                  {t.cancel}
                </button>
                <button
                  onClick={deleteAccount}
                  disabled={loading}
                  className="flex-1 px-4 py-3 rounded-xl bg-red-600 text-white font-bold hover:bg-red-700 disabled:opacity-50"
                  data-testid="delete-confirm-2-btn"
                >
                  {loading ? "..." : t.deleteForever}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Avatar Change Modal (settings) */}
        {showAvatarModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-3xl p-6 md:p-8 max-w-md w-full max-h-[90vh] overflow-y-auto">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-2xl font-bold text-purple-600">{t.changeAvatar}</h3>
                <button
                  onClick={() => setShowAvatarModal(false)}
                  className="text-gray-400 hover:text-gray-600 text-2xl"
                  data-testid="close-avatar-modal-settings"
                >
                  ✕
                </button>
              </div>
              <div className="grid grid-cols-6 gap-2">
                {avatars.map((avatar) => (
                  <button
                    key={avatar}
                    onClick={() => changeAvatar(avatar)}
                    disabled={loading || avatar === selectedChild.avatar}
                    className={`text-3xl p-2 rounded-xl transition-all ${
                      avatar === selectedChild.avatar
                        ? "bg-purple-500 scale-110 ring-2 ring-purple-300"
                        : "hover:bg-purple-100 hover:scale-105"
                    } ${loading ? "opacity-50" : ""}`}
                    data-testid={`settings-avatar-select-${avatar}`}
                  >
                    {avatar}
                  </button>
                ))}
              </div>
              <p className="text-xs text-gray-400 text-center mt-4">
                {lang === "it" ? "Altri avatar disponibili nel Negozio!" : "More avatars available in the Shop!"}
              </p>
            </div>
          </div>
        )}
      </div>
    );
  }

  // Game Screen
  if (view === "game" && gameSession) {
    const question = gameSession.questions[currentQuestion];
    const currentPlayer = multiplayerMode ? multiplayerPlayers[currentPlayerIndex] : null;

    // Pass-the-device handoff screen (local challenge, between players)
    if (showPassDevice && pendingNextPlayer !== null && multiplayerPlayers[pendingNextPlayer]) {
      const nextP = multiplayerPlayers[pendingNextPlayer];
      return (
        <div className={`min-h-screen ${bgClass("bg-gradient-to-br from-indigo-600 via-purple-500 to-pink-400")} p-4 flex items-center justify-center`} data-testid="pass-device-screen">
          <div className="max-w-md w-full bg-white rounded-3xl p-8 shadow-2xl text-center">
            <div className="text-6xl mb-4 animate-bounce">📱➡️</div>
            <h2 className="text-2xl font-bold text-gray-800 mb-2" data-testid="pass-device-title">{t.passDeviceTitle}</h2>
            <p className="text-gray-500 mb-6">{t.passDeviceMsg}</p>
            <div className="flex items-center justify-center gap-3 mb-8">
              <span className="text-5xl">{nextP.avatar}</span>
              <span className="text-3xl font-bold text-purple-600" data-testid="pass-device-next-player">{nextP.name}</span>
            </div>
            <button
              onClick={startNextPlayerTurn}
              className="w-full bg-gradient-to-r from-purple-600 to-pink-500 text-white text-2xl font-bold py-4 rounded-2xl hover:opacity-90 transition-opacity"
              data-testid="pass-device-ready-btn"
            >
              ✓ {t.imReady}
            </button>
          </div>
        </div>
      );
    }
    
    return (
      <div className={`min-h-screen ${bgClass("bg-gradient-to-br from-indigo-600 via-purple-500 to-pink-400")} p-4 flex items-center justify-center`}>
        <div className="max-w-lg w-full">
          {/* Powerup bar (single player only) */}
          {!multiplayerMode && !challengeMode && !botMode && (
            <div className="flex justify-center gap-2 mb-3" data-testid="powerup-bar">
              <button
                onClick={usePowerupHint}
                disabled={gamePowerups.hint <= 0 || !!feedback}
                className="bg-white/20 backdrop-blur text-white px-3 py-2 rounded-full font-bold text-sm hover:bg-white/30 disabled:opacity-40"
                data-testid="powerup-hint-btn"
              >
                💡 {t.hintLabel} ({gamePowerups.hint})
              </button>
              <button
                onClick={usePowerupSkip}
                disabled={gamePowerups.skip <= 0 || !!feedback}
                className="bg-white/20 backdrop-blur text-white px-3 py-2 rounded-full font-bold text-sm hover:bg-white/30 disabled:opacity-40"
                data-testid="powerup-skip-btn"
              >
                ⏭️ {t.skipLabel} ({gamePowerups.skip})
              </button>
              <button
                onClick={usePowerupDouble}
                disabled={gamePowerups.double_coins <= 0 || doubleCoinsActive}
                className={`px-3 py-2 rounded-full font-bold text-sm backdrop-blur ${doubleCoinsActive ? "bg-yellow-400 text-yellow-900" : "bg-white/20 text-white hover:bg-white/30 disabled:opacity-40"}`}
                data-testid="powerup-double-btn"
              >
                💰 x2 ({gamePowerups.double_coins})
              </button>
            </div>
          )}
          {doubleCoinsActive && (
            <p className="text-center text-yellow-200 font-bold text-sm mb-2" data-testid="double-coins-active">✨ {t.doubleActiveLabel}</p>
          )}
          {hintAnswer !== null && (
            <div className="bg-yellow-100 border-2 border-yellow-400 text-yellow-800 rounded-xl p-3 text-center font-bold mb-3 animate-pulse" data-testid="hint-banner">
              💡 {t.hintIs}: {hintAnswer}
            </div>
          )}
          {/* Multiplayer Player Indicator */}
          {multiplayerMode && currentPlayer && (
            <div className="bg-white/20 backdrop-blur rounded-2xl p-4 mb-4 text-center">
              <div className="text-white text-lg">{t.playerTurn}:</div>
              <div className="flex items-center justify-center gap-3">
                <span className="text-4xl">{currentPlayer.avatar}</span>
                <span className="text-2xl font-bold text-white" data-testid="multiplayer-current-player">{currentPlayer.name}</span>
              </div>
            </div>
          )}
          
          {/* Virtual friend header */}
          {botMode && botChallenge && (
            <div className="bg-white/20 backdrop-blur rounded-2xl p-4 mb-4" data-testid="bot-header">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className="text-3xl">{selectedChild?.avatar}</span>
                  <div>
                    <div className="text-white text-sm font-bold">{selectedChild?.name}</div>
                    <div className="text-3xl font-bold text-white leading-none" data-testid="bot-my-score">{botChallenge.myScore}</div>
                  </div>
                </div>
                <div className="text-white/70 font-black text-xl">VS</div>
                <div className="flex items-center gap-2 text-right">
                  <div>
                    <div className="text-white text-sm font-bold">{botChallenge.bot.name}</div>
                    <div className="text-3xl font-bold text-white leading-none" data-testid="bot-score">{botChallenge.botScore}</div>
                  </div>
                  <span className={`text-3xl ${feedback && botChallenge.lastBotCorrect ? "animate-bounce" : ""}`}>{botChallenge.bot.avatar}</span>
                </div>
              </div>
              <div className="flex items-center justify-center gap-1.5 mt-3" data-testid="bot-level">
                <span className="text-white/80 text-xs mr-1">{t.difficultyLabel}</span>
                {[1, 2, 3, 4, 5].map((l) => (
                  <span key={l} className={`w-3 h-3 rounded-full transition-colors duration-300 ${l <= botChallenge.level ? "bg-yellow-300 shadow" : "bg-white/30"}`} />
                ))}
              </div>
              {feedback && botChallenge.taunt && (
                <div className="text-center text-white text-sm mt-2 italic" data-testid="bot-taunt">
                  {botChallenge.bot.avatar} {botChallenge.lastBotCorrect ? "✅" : "❌"} {botChallenge.taunt}
                </div>
              )}
            </div>
          )}

          {/* Rival + Blitz (single player only) */}
          {!multiplayerMode && !challengeMode && !botMode && gameSession.mode === "blitz" && (
            <BlitzTimer timeLeft={blitzTimeLeft} correct={blitzCorrect} />
          )}
          {!multiplayerMode && !challengeMode && !botMode && gameSession.rival && currentQuestion === 0 && !feedback && (
            <div className="mb-3"><RivalCard rival={gameSession.rival} lang={lang} record={selectedChild?.records?.[gameSession.mode === "blitz" ? "blitz" : "math"]} /></div>
          )}
          {!multiplayerMode && !challengeMode && !botMode && <ComboPopup combo={combo} />}

          {/* Progress */}
          <div className="mb-6">
            <div className="flex justify-between text-white mb-2">
              <span>{gameSession.mode === "blitz" ? `⚡ Blitz · ${t.question} ${currentQuestion + 1}` : `${t.question} ${currentQuestion + 1} ${t.of} ${gameSession.total || gameSession.questions.length}`}</span>
              <span>{t.gameTypes[gameSession.game_type]}</span>
            </div>
            <div className="h-3 bg-white/30 rounded-full overflow-hidden">
              <div 
                className="h-full bg-white transition-all duration-300"
                style={{ width: `${((currentQuestion + 1) / (gameSession.total || gameSession.questions.length)) * 100}%` }}
              />
            </div>
          </div>

          {/* Countdown timer (challenge & local challenge only) */}
          {(challengeMode || multiplayerMode || botMode) && !feedback && (
            <div className="flex justify-center mb-4" data-testid="challenge-timer">
              <div className={`px-6 py-2 rounded-full font-bold text-2xl flex items-center gap-2 transition-colors ${timeLeft <= 5 ? "bg-red-500 text-white animate-pulse" : "bg-white/90 text-purple-600"}`}>
                ⏱️ {timeLeft}{t.secondsLabel}
              </div>
            </div>
          )}

          {/* Question Card */}
          <div className={`bg-white rounded-3xl p-8 shadow-2xl transition-all duration-300 ${feedback ? (feedback.correct ? "ring-4 ring-green-400" : "ring-4 ring-red-400") : ""}`}>
            <div className="text-center">
              <div className="text-6xl md:text-8xl font-bold text-gray-800 mb-8" data-testid="question-display">
                {question.a} {question.operator} {question.b} = ?
              </div>
              
              {feedback ? (
                <div className={`text-2xl font-bold mb-4 ${feedback.correct ? "text-green-500" : "text-red-500"}`}>
                  {feedback.correct ? (
                    <span>✅ {t.correct}</span>
                  ) : feedback.timedOut ? (
                    <span data-testid="timeout-feedback">⏰ {t.timesUp} {t.correctAnswer}: {feedback.correctAnswer}</span>
                  ) : (
                    <span>❌ {t.wrong}{!multiplayerMode && ` ${t.correctAnswer}: ${feedback.correctAnswer}`}</span>
                  )}
                </div>
              ) : (
                <>
                  <input
                    type="number"
                    value={userAnswer}
                    onChange={(e) => setUserAnswer(e.target.value)}
                    onKeyPress={handleKeyPress}
                    className="w-full text-4xl text-center py-4 px-6 rounded-2xl border-4 border-purple-200 focus:border-purple-500 focus:outline-none mb-4"
                    placeholder="?"
                    autoFocus
                    data-testid="answer-input"
                  />
                  
                  <button
                    onClick={() => (multiplayerMode ? submitMultiplayerAnswer() : submitAnswer())}
                    disabled={userAnswer === ""}
                    className="w-full bg-gradient-to-r from-purple-600 to-pink-500 text-white text-2xl font-bold py-4 rounded-2xl hover:opacity-90 transition-opacity disabled:opacity-50"
                    data-testid="submit-answer-btn"
                  >
                    ✓ {t.play}
                  </button>
                </>
              )}
            </div>
          </div>

          {/* Decorative */}
          <div className="flex justify-center gap-4 mt-6 text-4xl">
            <span className="animate-bounce" style={{ animationDelay: "0s" }}>⭐</span>
            <span className="animate-bounce" style={{ animationDelay: "0.1s" }}>🌟</span>
            <span className="animate-bounce" style={{ animationDelay: "0.2s" }}>✨</span>
          </div>
        </div>
      </div>
    );
  }

  // Results Screen
  if (view === "results" && gameResults) {
    const getMedalText = (medal) => {
      if (medal === "gold") return t.goldMedal;
      if (medal === "silver") return t.silverMedal;
      if (medal === "bronze") return t.bronzeMedal;
      return t.noMedal;
    };

    return (
      <div className="min-h-screen bg-gradient-to-br from-yellow-400 via-orange-400 to-red-400 p-4 flex items-center justify-center">
        {levelUp && <LevelUpOverlay level={levelUp.level} bonus={levelUp.bonus} lang={lang} onClose={() => setLevelUp(null)} />}
        <div className="max-w-lg w-full">
          <div className="bg-white rounded-3xl p-8 shadow-2xl text-center">
            <div className="text-6xl mb-4">
              {gameResults.medal_earned === "gold" ? "🥇" : gameResults.medal_earned === "silver" ? "🥈" : gameResults.medal_earned === "bronze" ? "🥉" : "🎮"}
            </div>
            
            <h2 className="text-3xl font-bold text-gray-800 mb-2">{gameResults.mode === "blitz" ? "⚡ Blitz!" : gameResults.mode === "bot" ? (gameResults.bot_result.winner === "me" ? `🏆 ${t.youWon}` : gameResults.bot_result.winner === "tie" ? `🤝 ${t.tieResult}` : `${gameResults.bot_result.bot.name} ${t.botWon}`) : t.gameOver}</h2>
            {gameResults.bot_result && (() => {
              const br = gameResults.bot_result;
              return (
                <div className="bg-gradient-to-r from-indigo-50 to-purple-50 border-2 border-indigo-200 rounded-2xl p-4 mb-4" data-testid="bot-result">
                  <div className="flex items-center justify-between">
                    <div className="text-center flex-1">
                      <div className="text-4xl">{selectedChild?.avatar}</div>
                      <div className="text-sm font-bold text-gray-600">{selectedChild?.name}</div>
                      <div className={`text-4xl font-black ${br.winner === "me" ? "text-green-600" : "text-gray-700"}`} data-testid="bot-result-my-score">{br.my_score}</div>
                    </div>
                    <div className="text-2xl font-black text-indigo-400">VS</div>
                    <div className="text-center flex-1">
                      <div className="text-4xl">{br.bot.avatar}</div>
                      <div className="text-sm font-bold text-gray-600">{br.bot.name}</div>
                      <div className={`text-4xl font-black ${br.winner === "bot" ? "text-red-500" : "text-gray-700"}`} data-testid="bot-result-bot-score">{br.bot_score}</div>
                    </div>
                  </div>
                  <div className="flex justify-center gap-3 mt-3 text-xs font-bold flex-wrap">
                    <span className="bg-white rounded-full px-3 py-1 text-indigo-700" data-testid="bot-result-record">{t.yourRecord}: {br.record.wins}W · {br.record.losses}L</span>
                    <span className="bg-white rounded-full px-3 py-1 text-amber-700" data-testid="bot-result-streak">🔥 {t.botStreak}: {br.streak}</span>
                    <span className="bg-white rounded-full px-3 py-1 text-purple-700">{t.difficultyLabel}: {br.start_level} → {br.end_level}</span>
                    {br.streak_bonus > 0 && <span className="bg-yellow-200 rounded-full px-3 py-1 text-yellow-800">+{br.streak_bonus} {t.streakBonus}</span>}
                  </div>
                </div>
              );
            })()}
            {gameResults.quick && <p className="text-gray-500 mb-4" data-testid="quick-game-label">{({ oddOneOut: "Trova l'Intruso", sequences: "Sequenze", memory: "Memory", riddles: "Indovinelli", puzzle: "Puzzle", italianQuiz: "Quiz Italiano" })[gameResults.game] || gameResults.game}</p>}
            {!gameResults.quick && <div className="mb-4" />}
            {(gameResults.medal_earned || newBadges.length > 0 || gameResults.new_record) && <ConfettiBurst />}
            <RewardBreakdown res={gameResults} lang={lang} />
            
            {newBadges.length > 0 && (
              <div className="bg-gradient-to-r from-amber-100 to-yellow-100 border-2 border-amber-300 rounded-xl p-3 mb-4" data-testid="new-badges-banner">
                <div className="font-bold text-amber-700 mb-1">🎉 {t.newBadgeUnlocked}</div>
                <div className="flex justify-center gap-2 flex-wrap">
                  {newBadges.map((b) => (
                    <span key={b} className="bg-white rounded-full px-3 py-1 text-sm font-bold text-gray-700 shadow">
                      {BADGE_DEFS[b]?.emoji} {lang === "it" ? BADGE_DEFS[b]?.it : BADGE_DEFS[b]?.en}
                    </span>
                  ))}
                </div>
              </div>
            )}
            
            {selectedChild?.is_guest && (gameResults.medal_earned || (selectedChild?.coins || 0) >= 100) && (
              <div className="bg-gradient-to-r from-purple-100 to-pink-100 border-2 border-purple-300 rounded-xl p-4 mb-4" data-testid="guest-save-reminder">
                <div className="text-3xl mb-1">💾</div>
                <div className="font-bold text-purple-700 mb-1">
                  {gameResults.medal_earned
                    ? (lang === "it" ? "Che vittoria! Non perdere questo risultato." : "What a win! Don't lose this result.")
                    : (lang === "it" ? `Hai già ${selectedChild?.coins} monete! 🎉` : `You already have ${selectedChild?.coins} coins! 🎉`)}
                </div>
                <p className="text-gray-600 text-sm mb-3">
                  {lang === "it"
                    ? "Giochi come ospite: salva i progressi per non perdere monete, badge e statistiche."
                    : "You're playing as a guest: save your progress so you don't lose coins, badges and stats."}
                </p>
                <button
                  onClick={() => { setView("gameSelect"); openUpgrade(); }}
                  className="w-full bg-gradient-to-r from-purple-600 to-pink-500 text-white px-4 py-2.5 rounded-xl font-bold hover:opacity-90"
                  data-testid="guest-save-reminder-btn"
                >
                  💾 {lang === "it" ? "Salva i progressi" : "Save my progress"}
                </button>
              </div>
            )}

            <div className="space-y-4 mb-6">
              <div className="bg-purple-100 rounded-xl p-4">
                <div className="text-gray-500">{t.score}</div>
                <div className="text-3xl font-bold text-purple-600" data-testid="results-score">
                  {gameResults.quick && gameResults.game && ["memory", "puzzle"].includes(gameResults.game) ? `${gameResults.score} ${lang === "it" ? "mosse" : "moves"}` : gameResults.mode === "blitz" ? `${gameResults.correct} ${lang === "it" ? "risposte corrette" : "correct answers"}` : `${gameResults.correct}/${gameResults.total}`}
                </div>
              </div>
              
              {!gameResults.quick && (
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-blue-100 rounded-xl p-4">
                  <div className="text-gray-500">{t.accuracy}</div>
                  <div className="text-2xl font-bold text-blue-600">{gameResults.accuracy}%</div>
                </div>
                <div className="bg-green-100 rounded-xl p-4">
                  <div className="text-gray-500">{t.avgTime}</div>
                  <div className="text-2xl font-bold text-green-600">{gameResults.avg_time_seconds}s</div>
                </div>
              </div>
              )}
              
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-yellow-100 rounded-xl p-4">
                  <div className="text-gray-500">{t.coinsEarned}</div>
                  <div className="text-2xl font-bold text-yellow-600" data-testid="results-coins">+{gameResults.coins_earned} 💰</div>
                </div>
                <div className="bg-amber-100 rounded-xl p-4">
                  <div className="text-gray-500">{t.medalEarned}</div>
                  <div className="text-xl font-bold text-amber-600">{getMedalText(gameResults.medal_earned)}</div>
                </div>
              </div>
              
              {!gameResults.quick && gameResults.mode !== "blitz" && (
              <div className="bg-gradient-to-r from-indigo-100 to-purple-100 rounded-xl p-4">
                <div className="text-gray-500">{t.iqScore}</div>
                <div className="text-3xl font-bold text-indigo-600">{gameResults.iq_score}% 🧠</div>
              </div>
              )}
            </div>

            {spinStatus && spinStatus.spins_left > 0 && gameResults.coins_earned > 0 && (
              <FortuneWheel segments={spinStatus.segments} spinsLeft={spinStatus.spins_left} onSpin={spinWheel} lang={lang} playSound={playSound} />
            )}
            <MissionNudge missions={missions} lang={lang} onOpen={() => setView("gameSelect")} />
            
            <div className="flex gap-4 mt-4">
              <button
                onClick={() => setView("gameSelect")}
                className="flex-1 px-4 py-3 rounded-xl bg-gradient-to-r from-purple-600 to-pink-500 text-white font-bold hover:opacity-90 transition-opacity"
                data-testid="choose-game-btn"
              >
                🎮 {t.chooseNewGame}
              </button>
              {gameResults.bot_result ? (
                <button
                  onClick={() => startBotChallenge(gameResults.bot_result.bot.id, gameResults.bot_result.game_type)}
                  className="flex-1 px-4 py-3 rounded-xl border-2 border-purple-400 text-purple-600 font-bold hover:bg-purple-50 transition-colors"
                  data-testid="bot-rematch-btn"
                >
                  ⚔️ {t.rematch}
                </button>
              ) : (
              <button
                onClick={() => (gameResults.quick ? setView(gameResults.backView || gameResults.game) : startGame(gameSession.game_type, gameResults.mode === "blitz" ? "blitz" : "normal"))}
                className="flex-1 px-4 py-3 rounded-xl border-2 border-purple-400 text-purple-600 font-bold hover:bg-purple-50 transition-colors"
                data-testid="play-again-btn"
              >
                🔄 {t.sameGame}
              </button>
              )}
            </div>
            {gameResults.bot_result?.next_bot && (
              <button
                onClick={() => startBotChallenge(gameResults.bot_result.next_bot.id, gameResults.bot_result.game_type)}
                className="w-full mt-3 px-4 py-3 rounded-xl bg-gradient-to-r from-amber-400 to-orange-500 text-white font-bold hover:opacity-90 transition-opacity flex items-center justify-center gap-2"
                data-testid="bot-next-opponent-btn"
              >
                <span className="text-2xl">{gameResults.bot_result.next_bot.avatar}</span>
                {t.nextOpponent}: {gameResults.bot_result.next_bot.name} · {t[`diff${gameResults.bot_result.next_bot.difficulty.charAt(0).toUpperCase() + gameResults.bot_result.next_bot.difficulty.slice(1)}`]}
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Leaderboard Screen
  if (view === "leaderboard") {
    return (
      <div className={`min-h-screen ${bgClass("bg-gradient-to-br from-emerald-600 via-teal-500 to-cyan-400")} p-4`}>
        <div className="max-w-2xl mx-auto">
          <button
            onClick={() => setView("gameSelect")}
            className="bg-white/20 text-white px-4 py-2 rounded-full font-bold hover:bg-white/30 transition-colors mb-6"
            data-testid="leaderboard-back-btn"
          >
            ← {t.backHome}
          </button>
          
          <h2 className="text-3xl font-bold text-white text-center mb-2" data-testid="leaderboard-title">🏆 {t.leaderboard}</h2>
          <p className="text-center text-white/90 font-bold mb-4" data-testid="leaderboard-band-label">
            {lbTab === "missions"
              ? (lang === "it" ? "🎯 Chi ha completato più missioni (7 giorni)" : "🎯 Most missions completed (7 days)")
              : lbTab === "weekly" ? (lang === "it" ? "🏅 Classifica della settimana · punti = monete vinte" : "🏅 This week's leaderboard · points = coins earned")
              : `${selectedChild?.age_band} ${t.yearsBand} · ${t.peersOnly}`}
          </p>

          <div className="flex justify-center gap-2 mb-4">
            <button
              onClick={() => setLbTab("coins")}
              className={`px-5 py-2 rounded-full font-bold transition-colors ${lbTab === "coins" ? "bg-white text-teal-600" : "bg-black/30 text-white hover:bg-black/40"}`}
              data-testid="lb-tab-coins"
            >
              💰 {t.coinsTab}
            </button>
            <button
              onClick={() => { setLbTab("missions"); fetchMissionsLb(); }}
              className={`px-5 py-2 rounded-full font-bold transition-colors ${lbTab === "missions" ? "bg-white text-teal-600" : "bg-black/30 text-white hover:bg-black/40"}`}
              data-testid="lb-tab-missions"
            >
              🎯 {t.missionsTab}
            </button>
            <button
              onClick={() => { setLbTab("weekly"); fetchWeeklyLb(); }}
              className={`px-5 py-2 rounded-full font-bold transition-colors ${lbTab === "weekly" ? "bg-white text-teal-600" : "bg-black/30 text-white hover:bg-black/40"}`}
              data-testid="lb-tab-weekly"
            >
              🏅 {lang === "it" ? "Settimana" : "Week"}
            </button>
          </div>
          
          {lbTab === "weekly" ? (
          <div className="bg-white rounded-3xl p-6 shadow-xl" data-testid="weekly-leaderboard">
            <p className="text-center text-sm text-gray-500 mb-4">
              {lang === "it" ? "Si azzera ogni lunedì · Premi: 🥇100 🥈60 🥉30 monete (+ 🏆 al primo)" : "Resets every Monday · Prizes: 🥇100 🥈60 🥉30 coins (+ 🏆 for #1)"}
              {weeklyLb && ` · ${Math.max(0, Math.ceil(weeklyLb.seconds_left / 86400))}${lang === "it" ? "g rimasti" : "d left"}`}
            </p>
            {!weeklyLb || weeklyLb.entries.length === 0 ? (
              <p className="text-center text-gray-500 py-8">{lang === "it" ? "Nessun punto ancora questa settimana: gioca e sarai il primo!" : "No points yet this week: play and be the first!"}</p>
            ) : (
              <div className="space-y-3">
                {weeklyLb.entries.map((p, idx) => (
                  <div key={p.child_id} className={`flex items-center gap-4 p-4 rounded-xl ${p.child_id === selectedChild?.id ? "ring-2 ring-rose-400 " : ""}${idx < 3 ? "bg-gradient-to-r from-rose-100 to-orange-100" : "bg-gray-50"}`} data-testid={`weekly-lb-row-${p.child_id}`}>
                    <div className="text-3xl font-bold text-gray-400 w-10">{idx === 0 ? "🥇" : idx === 1 ? "🥈" : idx === 2 ? "🥉" : `#${p.rank}`}</div>
                    <div className="text-4xl">{p.avatar}</div>
                    <div className="flex-1 font-bold text-gray-800">{p.name}{p.child_id === selectedChild?.id && <span className="ml-2 text-xs text-rose-500">({lang === "it" ? "tu" : "you"})</span>}</div>
                    <div className="font-bold text-rose-600">{p.points} pt</div>
                  </div>
                ))}
              </div>
            )}
          </div>
          ) : lbTab === "missions" ? (
          <div className="bg-white rounded-3xl p-6 shadow-xl" data-testid="missions-leaderboard">
            {missionsLb.length === 0 ? (
              <p className="text-center text-gray-500 py-8">{t.noScores}</p>
            ) : (
              <div className="space-y-3">
                {missionsLb.map((p, idx) => (
                  <div
                    key={p.child_id}
                    className={`flex items-center gap-4 p-4 rounded-xl ${idx < 3 ? "bg-gradient-to-r from-purple-100 to-pink-100" : "bg-gray-50"}`}
                    data-testid={`missions-lb-row-${p.child_id}`}
                  >
                    <div className="text-3xl font-bold text-gray-400 w-10">
                      {idx === 0 ? "🥇" : idx === 1 ? "🥈" : idx === 2 ? "🥉" : `#${p.rank}`}
                    </div>
                    <div className="text-4xl">{p.avatar}</div>
                    <div className="flex-1">
                      <div className="font-bold text-gray-800">{p.name}</div>
                      {p.streak_days >= 2 && <div className="text-sm text-orange-500 font-bold">🔥 {p.streak_days} {t.streakLabel}</div>}
                    </div>
                    <div className="text-right">
                      <div className="font-bold text-purple-600">🎯 {p.missions_completed}</div>
                      <div className="text-xs text-gray-400">{lang === "it" ? "ultimi 7 giorni" : "last 7 days"}</div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
          ) : (
          <div className="bg-white rounded-3xl p-6 shadow-xl">
            {leaderboard.length === 0 ? (
              <p className="text-center text-gray-500 py-8">{t.noScores}</p>
            ) : (
              <div className="space-y-3">
                {leaderboard.map((player, idx) => (
                  <div
                    key={player.id}
                    className={`flex items-center gap-4 p-4 rounded-xl ${idx < 3 ? "bg-gradient-to-r from-yellow-100 to-amber-100" : "bg-gray-50"}`}
                    data-testid={`leaderboard-row-${player.id}`}
                  >
                    <div className="text-3xl font-bold text-gray-400 w-10">
                      {idx === 0 ? "🥇" : idx === 1 ? "🥈" : idx === 2 ? "🥉" : `#${player.rank}`}
                    </div>
                    <div className="text-4xl">{player.avatar}</div>
                    <div className="flex-1">
                      <div className="font-bold text-gray-800">{player.name}</div>
                      <div className="text-sm text-gray-500">{player.age} {t.years}</div>
                    </div>
                    <div className="text-right">
                      <div className="font-bold text-yellow-600">💰 {player.coins}</div>
                      <div className="text-sm text-gray-500">🧠 {player.iq_score}%</div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
          )}
        </div>
      </div>
    );
  }

  // Stats Screen (IQ Analysis)
  if (view === "stats" && stats) {
    const iqAnalysis = stats.iq_analysis;
    
    return (
      <div className="min-h-screen bg-gradient-to-br from-violet-600 via-purple-500 to-fuchsia-400 p-4">
        <div className="max-w-2xl mx-auto">
          <button
            onClick={() => setView("gameSelect")}
            className="bg-white/20 text-white px-4 py-2 rounded-full font-bold hover:bg-white/30 transition-colors mb-6"
          >
            ← {t.backHome}
          </button>
          
          <h2 className="text-3xl font-bold text-white text-center mb-6">📊 {t.stats} & {t.iqAnalysis}</h2>
          
          <div className="space-y-6">
            {/* IQ Analysis Card */}
            <div className="bg-white rounded-3xl p-6 shadow-xl">
              <h3 className="text-xl font-bold text-purple-600 mb-4">🧠 {t.iqAnalysis}</h3>
              
              <div className="text-center mb-6">
                <div className="text-6xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-purple-600 to-pink-600">
                  {iqAnalysis.score}%
                </div>
                <div className="text-xl font-bold text-gray-700 mt-2">
                  {t.iqLevels[iqAnalysis.level]}
                </div>
                <p className="text-gray-500 mt-2">
                  {lang === "it" ? iqAnalysis.description_it : iqAnalysis.description_en}
                </p>
              </div>
              
              {/* Progress Bar */}
              <div className="mb-4">
                <div className="flex justify-between text-sm text-gray-500 mb-1">
                  <span>{t.percentile}</span>
                  <span>{iqAnalysis.percentile}%</span>
                </div>
                <div className="h-4 bg-gray-200 rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-gradient-to-r from-purple-500 to-pink-500 transition-all duration-1000"
                    style={{ width: `${iqAnalysis.percentile}%` }}
                  />
                </div>
                <p className="text-sm text-gray-500 mt-1 text-center">{t.comparedTo}</p>
              </div>
              
              {/* Factors */}
              <div className="bg-purple-50 rounded-xl p-4">
                <h4 className="font-bold text-purple-600 mb-2">{t.factors}:</h4>
                <div className="flex flex-wrap gap-2">
                  {iqAnalysis.benchmark.factors.map((factor, idx) => (
                    <span key={factor} className="bg-purple-200 text-purple-700 px-3 py-1 rounded-full text-sm">
                      {factor}
                    </span>
                  ))}
                </div>
              </div>
            </div>
            
            {/* General Stats */}
            <div className="bg-white rounded-3xl p-6 shadow-xl">
              <h3 className="text-xl font-bold text-blue-600 mb-4">📈 {t.stats}</h3>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-blue-50 rounded-xl p-4 text-center">
                  <div className="text-3xl font-bold text-blue-600">{stats.total_games}</div>
                  <div className="text-sm text-gray-500">{t.totalGames}</div>
                </div>
                <div className="bg-green-50 rounded-xl p-4 text-center">
                  <div className="text-3xl font-bold text-green-600">{stats.accuracy}%</div>
                  <div className="text-sm text-gray-500">{t.accuracy}</div>
                </div>
              </div>
              
              {/* Stats by Game Type */}
              {Object.keys(stats.type_stats).length > 0 && (
                <div className="mt-4 space-y-2">
                  {Object.entries(stats.type_stats).map(([type, data]) => (
                    <div key={type} className="flex items-center justify-between bg-gray-50 rounded-lg p-3">
                      <span className="font-medium">{t.gameTypes[type] || type}</span>
                      <span className="text-gray-600">
                        {data.total > 0 ? Math.round(data.correct / data.total * 100) : 0}% ({data.games} partite)
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
            
            {/* Medals Summary */}
            <div className="bg-white rounded-3xl p-6 shadow-xl">
              <h3 className="text-xl font-bold text-amber-600 mb-4">🏅 {t.medals}</h3>
              <div className="flex justify-around">
                <div className="text-center">
                  <div className="text-4xl">🥇</div>
                  <div className="text-2xl font-bold text-amber-600">{stats.child.gold_medals}</div>
                </div>
                <div className="text-center">
                  <div className="text-4xl">🥈</div>
                  <div className="text-2xl font-bold text-gray-500">{stats.child.silver_medals}</div>
                </div>
                <div className="text-center">
                  <div className="text-4xl">🥉</div>
                  <div className="text-2xl font-bold text-amber-700">{stats.child.bronze_medals}</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Competitions Screen
  if (view === "competitions") {
    return (
      <div className="min-h-screen bg-gradient-to-br from-rose-600 via-pink-500 to-orange-400 p-4">
        <div className="max-w-2xl mx-auto">
          <button
            onClick={() => setView("gameSelect")}
            className="bg-white/20 text-white px-4 py-2 rounded-full font-bold hover:bg-white/30 transition-colors mb-6"
          >
            ← {t.backHome}
          </button>
          
          <h2 className="text-3xl font-bold text-white text-center mb-6">🏅 {t.competitions}</h2>
          
          {competitions.length === 0 ? (
            <div className="bg-white rounded-3xl p-8 shadow-xl text-center">
              <div className="text-6xl mb-4">🏆</div>
              <p className="text-gray-500">Nessuna competizione attiva al momento</p>
              <p className="text-gray-400 text-sm mt-2">Le competizioni verranno aggiunte presto!</p>
            </div>
          ) : (
            <div className="space-y-4">
              {competitions.map((comp) => (
                <div key={comp.id} className="bg-white rounded-2xl p-6 shadow-xl">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-xl font-bold text-gray-800">{comp.name}</h3>
                    <span className="bg-green-100 text-green-700 px-3 py-1 rounded-full text-sm font-bold">
                      {comp.age_group} {t.years}
                    </span>
                  </div>
                  
                  <div className="flex items-center gap-4 mb-4">
                    <div className="flex-1">
                      <div className="text-sm text-gray-500">{t.prize}</div>
                      <div className="font-bold text-yellow-600">💰 {comp.prize_coins} + 🏆</div>
                    </div>
                    <div className="flex-1">
                      <div className="text-sm text-gray-500">{t.participants}</div>
                      <div className="font-bold text-blue-600">{comp.participants?.length || 0}</div>
                    </div>
                  </div>
                  
                  <button
                    onClick={() => joinCompetition(comp.id)}
                    className="w-full bg-gradient-to-r from-pink-500 to-orange-500 text-white font-bold py-3 rounded-xl hover:opacity-90 transition-opacity"
                  >
                    {t.joinCompetition}
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  }

  // Shop Screen
  if (view === "shop") {
    const filteredItems = shopItems.filter(item => item.category === shopCategory);
    const ownedItemIds = inventory?.inventory ? [
      ...inventory.inventory.avatars.map(i => i.id),
      ...inventory.inventory.themes.map(i => i.id),
      ...inventory.inventory.powerups.map(i => i.id)
    ] : [];

    return (
      <div className={`min-h-screen ${bgClass("bg-gradient-to-br from-amber-500 via-orange-500 to-red-500")} p-4`}>
        <div className="max-w-4xl mx-auto">
          <button
            onClick={() => setView("gameSelect")}
            className="bg-white/20 text-white px-4 py-2 rounded-full font-bold hover:bg-white/30 transition-colors mb-6"
            data-testid="shop-back-btn"
          >
            ← {t.backHome}
          </button>
          
          <div className="text-center mb-6">
            <h2 className="text-3xl font-bold text-white mb-2">🛒 {t.shopTitle}</h2>
            <div className="inline-flex items-center gap-2 bg-white/20 px-4 py-2 rounded-full">
              <span className="text-2xl">💰</span>
              <span className="text-xl font-bold text-white" data-testid="shop-coin-balance">{selectedChild?.coins || 0} {t.coins}</span>
            </div>
          </div>

          {/* Shop Message */}
          {shopMessage && (
            <div className={`mb-4 p-4 rounded-xl text-center font-bold ${shopMessage.type === "success" ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"}`} data-testid="shop-message">
              {shopMessage.text}
            </div>
          )}

          {/* Category Tabs */}
          <div className="flex justify-center gap-2 mb-6">
            {[
              { key: "avatar", icon: "👤", label: t.avatars },
              { key: "theme", icon: "🎨", label: t.themes },
              { key: "powerup", icon: "⚡", label: t.powerups }
            ].map((cat) => (
              <button
                key={cat.key}
                onClick={() => setShopCategory(cat.key)}
                className={`px-4 py-2 rounded-full font-bold transition-all flex items-center gap-2 ${shopCategory === cat.key ? "bg-white text-orange-600" : "bg-white/30 text-white hover:bg-white/50"}`}
                data-testid={`shop-tab-${cat.key}`}
              >
                <span>{cat.icon}</span>
                <span>{cat.label}</span>
              </button>
            ))}
          </div>

          {/* Items Grid */}
          {shopCategory === "theme" && isPro(selectedChild) && selectedChild?.bg_color && selectedChild.bg_color !== "default" && (
            <p className="text-center text-white/90 text-sm font-bold mb-4 bg-black/20 rounded-full px-4 py-2 inline-block w-full" data-testid="theme-pro-precedence-hint">
              👑 {lang === "it" ? "Il tuo colore PRO ha la precedenza: ripristina il colore originale nelle Impostazioni per vedere il tema." : "Your PRO color takes precedence: restore the original color in Settings to see the theme."}
            </p>
          )}
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {filteredItems.map((item) => {
              const isOwned = ownedItemIds.includes(item.id);
              const canAfford = (selectedChild?.coins || 0) >= item.price;
              const isEquipped = (item.category === "avatar" && selectedChild?.avatar === item.data?.avatar) ||
                (item.category === "theme" && selectedChild?.theme === item.name);
              
              return (
                <div
                  key={item.id}
                  className={`bg-white rounded-2xl p-4 shadow-lg ${isOwned ? "ring-2 ring-green-400" : ""}`}
                  data-testid={`shop-item-${item.id}`}
                >
                  <div className="text-5xl text-center mb-2">{item.icon}</div>
                  <h3 className="font-bold text-gray-800 text-center">{lang === "it" ? item.name_it : item.name_en}</h3>
                  <p className="text-sm text-gray-500 text-center mb-3">{lang === "it" ? item.description_it : item.description_en}</p>
                  
                  <div className="flex items-center justify-center gap-1 mb-3">
                    <span className="text-xl">💰</span>
                    <span className={`font-bold ${canAfford ? "text-yellow-600" : "text-red-400"}`}>{item.price}</span>
                  </div>
                  
                  {isOwned ? (
                    item.category === "avatar" ? (
                      <button
                        onClick={() => equipItem("avatar", item.data.avatar)}
                        disabled={isEquipped}
                        data-testid={`shop-equip-${item.name}`}
                        className={`w-full py-2 rounded-xl font-bold transition-colors ${isEquipped ? "bg-green-100 text-green-700" : "bg-blue-500 text-white hover:bg-blue-600"}`}
                      >
                        {isEquipped ? t.equipped : t.equip}
                      </button>
                    ) : item.category === "theme" ? (
                      <button
                        onClick={() => equipItem("theme", item.name)}
                        disabled={isEquipped}
                        data-testid={`shop-equip-${item.name}`}
                        className={`w-full py-2 rounded-xl font-bold transition-colors ${isEquipped ? "bg-green-100 text-green-700" : "bg-blue-500 text-white hover:bg-blue-600"}`}
                      >
                        {isEquipped ? t.equipped : t.equip}
                      </button>
                    ) : (
                      <div className="w-full py-2 rounded-xl bg-green-100 text-green-700 text-center font-bold">
                        ✓ {t.owned}
                      </div>
                    )
                  ) : (
                    <button
                      onClick={() => purchaseItem(item)}
                      disabled={!canAfford}
                      data-testid={`shop-buy-${item.name}`}
                      className={`w-full py-2 rounded-xl font-bold transition-colors ${canAfford ? "bg-gradient-to-r from-amber-500 to-orange-500 text-white hover:opacity-90" : "bg-gray-200 text-gray-400 cursor-not-allowed"}`}
                    >
                      {t.buy}
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    );
  }

  // Challenges Screen
  if (view === "premium") {
    const premiumActive = isPremium(selectedChild);
    const benefits = lang === "it"
      ? ["Personalizzazione completa (colori e layout)", "Accesso a tutti i contenuti e le funzioni", "Badge Premium 👑 sul profilo", "Sostieni CelestApp"]
      : ["Full personalization (colors & layout)", "Access to all content and features", "Premium 👑 badge on your profile", "Support CelestApp"];
    return (
      <div className={`min-h-screen ${bgClass("bg-gradient-to-br from-amber-500 via-orange-500 to-pink-500")} p-4`}>
        <div className="max-w-lg mx-auto">
          <button
            onClick={() => setView("gameSelect")}
            className="bg-white/20 text-white px-4 py-2 rounded-full font-bold hover:bg-white/30 transition-colors mb-6"
            data-testid="premium-back-btn"
          >
            ← {t.backHome}
          </button>

          <div className="bg-white rounded-3xl p-8 shadow-2xl text-center" data-testid="premium-page">
            <div className="text-6xl mb-3">👑</div>
            <h2 className="text-3xl font-bold text-gray-800 mb-1">CelestApp Premium</h2>
            <p className="text-gray-500 mb-6">
              {lang === "it" ? "Sblocca tutti i contenuti di CelestApp" : "Unlock all CelestApp content"}
            </p>

            {premiumActive ? (
              <div className="bg-green-100 border-2 border-green-300 rounded-2xl p-6" data-testid="premium-active-box">
                <div className="text-2xl font-bold text-green-700 mb-1">✅ {lang === "it" ? "Premium attivo!" : "Premium active!"}</div>
                <p className="text-gray-600 text-sm">
                  {lang === "it" ? "Hai accesso a tutti i contenuti. Grazie!" : "You have access to all content. Thank you!"}
                </p>
                {selectedChild?.premium_until && (
                  <p className="text-gray-500 text-xs mt-2">
                    {lang === "it" ? "Rinnovo il " : "Renews on "}
                    {new Date(selectedChild.premium_until).toLocaleDateString(lang === "it" ? "it-IT" : "en-GB")}
                  </p>
                )}
              </div>
            ) : (
              <>
                <div className="bg-amber-50 rounded-2xl p-5 mb-6 text-left">
                  <ul className="space-y-2">
                    {benefits.map((b, i) => (
                      <li key={b} className="flex items-start gap-2 text-gray-700">
                        <span className="text-green-500 font-bold">✓</span>
                        <span>{b}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="grid grid-cols-2 gap-3 mb-5">
                  <button
                    onClick={() => setPremiumPlan("monthly")}
                    className={`rounded-2xl p-4 border-2 text-center transition-all ${premiumPlan === "monthly" ? "border-amber-500 bg-amber-50" : "border-gray-200 hover:border-amber-300"}`}
                    data-testid="premium-plan-monthly"
                  >
                    <div className="text-sm font-semibold text-gray-500">{lang === "it" ? "Mensile" : "Monthly"}</div>
                    <div className="text-2xl font-bold text-gray-800">€4,99</div>
                    <div className="text-xs text-gray-500">{lang === "it" ? "al mese" : "per month"}</div>
                  </button>
                  <button
                    onClick={() => setPremiumPlan("yearly")}
                    className={`relative rounded-2xl p-4 border-2 text-center transition-all ${premiumPlan === "yearly" ? "border-amber-500 bg-amber-50" : "border-gray-200 hover:border-amber-300"}`}
                    data-testid="premium-plan-yearly"
                  >
                    <span className="absolute -top-2 left-1/2 -translate-x-1/2 bg-green-500 text-white text-[10px] font-bold px-2 py-0.5 rounded-full">
                      {lang === "it" ? "-35%" : "-35%"}
                    </span>
                    <div className="text-sm font-semibold text-gray-500">{lang === "it" ? "Annuale" : "Yearly"}</div>
                    <div className="text-2xl font-bold text-gray-800">€39,00</div>
                    <div className="text-xs text-gray-500">{lang === "it" ? "all'anno" : "per year"}</div>
                  </button>
                </div>
                <button
                  onClick={() => buyPremium(premiumPlan)}
                  disabled={loading || paymentsConfig?.stripe_enabled === false}
                  className="w-full bg-gradient-to-r from-amber-500 to-orange-500 text-white px-6 py-4 rounded-2xl font-bold text-lg hover:opacity-90 disabled:opacity-60 shadow-lg"
                  data-testid="premium-checkout-btn"
                >
                  {loading ? (lang === "it" ? "Attendi..." : "Please wait...") : `👑 ${lang === "it" ? "Abbonati a Premium" : "Subscribe to Premium"}`}
                </button>
                {premiumError && (
                  <p className="text-red-600 text-sm font-semibold mt-3" data-testid="premium-error">⚠️ {premiumError}</p>
                )}
                {paymentsConfig?.stripe_enabled === false && (
                  <p className="text-amber-700 text-sm font-semibold mt-3" data-testid="premium-disabled-note">
                    {lang === "it" ? "I pagamenti non sono ancora attivi. Torna presto!" : "Payments are not active yet. Check back soon!"}
                  </p>
                )}
                <p className="text-gray-400 text-xs mt-3" data-testid="premium-payment-note">
                  🔒 {lang === "it" ? "Pagamento sicuro con Stripe. Puoi annullare quando vuoi." : "Secure payment via Stripe. Cancel anytime."}
                  {paymentsConfig?.mode === "test" && (
                    <span className="ml-1 bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full font-bold" data-testid="premium-test-mode-badge">
                      {lang === "it" ? "Modalità test" : "Test mode"}
                    </span>
                  )}
                </p>
              </>
            )}
          </div>
        </div>
      </div>
    );
  }

  if (view === "challenges") {
    return (
      <div className={`min-h-screen ${bgClass("bg-gradient-to-br from-purple-600 via-indigo-600 to-blue-600")} p-4`}>
        <div className="max-w-4xl mx-auto">
          <button
            onClick={() => setView("gameSelect")}
            className="bg-white/20 text-white px-4 py-2 rounded-full font-bold hover:bg-white/30 transition-colors mb-6"
            data-testid="challenges-back-btn"
          >
            ← {t.backHome}
          </button>
          
          <h2 className="text-3xl font-bold text-white text-center mb-6">⚔️ {t.challengesTitle}</h2>

          {selectedChild?.is_guest ? (
            <div className="bg-white/95 rounded-2xl p-8 text-center" data-testid="challenges-guest-prompt">
              <div className="text-5xl mb-3">🔒</div>
              <p className="text-gray-700 font-bold mb-2">{lang === "it" ? "Crea un account per sfidare i tuoi amici!" : "Create an account to challenge your friends!"}</p>
              <p className="text-gray-500 text-sm mb-5">{lang === "it" ? "Giochi come ospite: registrati per le sfide 1v1." : "You're playing as a guest: sign up for 1v1 challenges."}</p>
              <button
                onClick={() => { setView("gameSelect"); openUpgrade(); }}
                className="bg-gradient-to-r from-purple-600 to-pink-500 text-white px-6 py-3 rounded-2xl font-bold hover:opacity-90"
                data-testid="challenges-guest-register-btn"
              >
                💾 {lang === "it" ? "Salva i progressi e registrati" : "Save progress & sign up"}
              </button>
            </div>
          ) : (
          <>

          {/* Create Challenge Button */}
          <div className="bg-white/20 backdrop-blur rounded-2xl p-6 mb-6">
            <h3 className="text-xl font-bold text-white mb-4 text-center">{t.createChallenge}</h3>
            <div className="grid grid-cols-3 md:grid-cols-5 gap-2">
              {[
                { type: "addition", icon: "➕" },
                { type: "subtraction", icon: "➖" },
                { type: "multiplication", icon: "✖️" },
                { type: "division", icon: "➗" },
                { type: "mixed", icon: "🎲" }
              ].map((game) => (
                <button
                  key={game.type}
                  onClick={() => createChallenge(game.type)}
                  disabled={loading}
                  className="bg-white rounded-xl p-3 hover:scale-105 transition-transform shadow-lg"
                  data-testid={`create-challenge-${game.type}`}
                >
                  <div className="text-2xl mb-1">{game.icon}</div>
                  <div className="text-xs font-bold text-gray-600">{t.gameTypes[game.type]}</div>
                </button>
              ))}
            </div>
          </div>

          {/* Tabs */}
          <div className="flex justify-center gap-2 mb-6">
            {[
              { key: "pending", label: t.pendingChallenges },
              { key: "active", label: t.activeChallenges },
              { key: "history", label: t.challengeHistory }
            ].map((tab) => (
              <button
                key={tab.key}
                onClick={() => setChallengeTab(tab.key)}
                className={`px-4 py-2 rounded-full font-bold transition-all ${challengeTab === tab.key ? "bg-white text-indigo-600" : "bg-white/30 text-white hover:bg-white/50"}`}
                data-testid={`challenge-tab-${tab.key}`}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {/* Content based on tab */}
          <div className="space-y-4">
            {challengeTab === "pending" && (
              pendingChallenges.length === 0 ? (
                <div className="bg-white rounded-2xl p-8 text-center">
                  <div className="text-5xl mb-4">🎯</div>
                  <p className="text-gray-500">{t.noChallenges}</p>
                  <p className="text-gray-400 text-sm mt-2">{t.createYourChallenge}</p>
                </div>
              ) : (
                pendingChallenges.map((challenge) => (
                  <div key={challenge.id} className="bg-white rounded-2xl p-4 shadow-lg">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <span className="text-4xl">{challenge.challenger_avatar}</span>
                        <div>
                          <div className="font-bold text-gray-800">{challenge.challenger_name}</div>
                          <div className="text-sm text-gray-500">{t.gameTypes[challenge.game_type]}</div>
                        </div>
                      </div>
                      <button
                        onClick={() => acceptChallenge(challenge)}
                        disabled={loading}
                        data-testid={`accept-challenge-${challenge.id}`}
                        className="bg-gradient-to-r from-green-500 to-emerald-500 text-white px-6 py-2 rounded-xl font-bold hover:opacity-90"
                      >
                        {t.acceptChallenge}
                      </button>
                    </div>
                  </div>
                ))
              )
            )}

            {challengeTab === "active" && (
              activeChallenges.length === 0 ? (
                <div className="bg-white rounded-2xl p-8 text-center">
                  <div className="text-5xl mb-4">⏳</div>
                  <p className="text-gray-500">{t.noChallenges}</p>
                </div>
              ) : (
                activeChallenges.map((challenge) => {
                  const isChallenger = challenge.challenger_id === selectedChild.id;
                  const opponent = isChallenger 
                    ? { name: challenge.opponent_name, avatar: challenge.opponent_avatar }
                    : { name: challenge.challenger_name, avatar: challenge.challenger_avatar };
                  
                  return (
                    <div key={challenge.id} className="bg-white rounded-2xl p-4 shadow-lg">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <span className="text-3xl">{selectedChild.avatar}</span>
                          <span className="font-bold text-purple-600">{t.vsText}</span>
                          <span className="text-3xl">{opponent?.avatar || "❓"}</span>
                          <span className="text-gray-600">{opponent?.name || t.waitingOpponent}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-sm text-gray-500">{t.gameTypes[challenge.game_type]}</span>
                          {!challenge.opponent_id && isChallenger && (
                            <>
                              <button
                                onClick={() => copyChallengeLink(challenge.id)}
                                data-testid={`copy-active-challenge-link-${challenge.id}`}
                                className="bg-gradient-to-r from-blue-500 to-indigo-500 text-white px-3 py-1.5 rounded-lg font-bold hover:opacity-90 text-xs"
                              >
                                {copiedId === challenge.id ? t.linkCopied : `🔗 ${t.copyLink}`}
                              </button>
                              <button
                                onClick={() => cancelChallenge(challenge.id)}
                                data-testid={`cancel-challenge-${challenge.id}`}
                                title={t.cancel}
                                className="bg-red-100 text-red-600 px-2.5 py-1.5 rounded-lg font-bold hover:bg-red-200 text-xs"
                              >
                                🗑️
                              </button>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })
              )
            )}

            {challengeTab === "history" && (
              challengeHistory.length === 0 ? (
                <div className="bg-white rounded-2xl p-8 text-center">
                  <div className="text-5xl mb-4">📜</div>
                  <p className="text-gray-500">{t.noChallenges}</p>
                </div>
              ) : (
                challengeHistory.map((challenge) => {
                  const isChallenger = challenge.challenger_id === selectedChild.id;
                  const opponent = isChallenger 
                    ? { name: challenge.opponent_name, avatar: challenge.opponent_avatar, score: challenge.opponent_score }
                    : { name: challenge.challenger_name, avatar: challenge.challenger_avatar, score: challenge.challenger_score };
                  const myScore = isChallenger ? challenge.challenger_score : challenge.opponent_score;
                  
                  return (
                    <div key={challenge.id} className={`bg-white rounded-2xl p-4 shadow-lg ${challenge.result === "win" ? "ring-2 ring-green-400" : challenge.result === "lose" ? "ring-2 ring-red-400" : ""}`}>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="text-center">
                            <span className="text-3xl">{selectedChild.avatar}</span>
                            <div className="text-lg font-bold">{myScore}</div>
                          </div>
                          <span className="font-bold text-gray-400">{t.vsText}</span>
                          <div className="text-center">
                            <span className="text-3xl">{opponent?.avatar}</span>
                            <div className="text-lg font-bold">{opponent?.score}</div>
                          </div>
                          <span className="text-gray-600">{opponent?.name}</span>
                        </div>
                        <div className={`px-4 py-2 rounded-full font-bold ${
                          challenge.result === "win" ? "bg-green-100 text-green-700" : 
                          challenge.result === "lose" ? "bg-red-100 text-red-700" : 
                          "bg-gray-100 text-gray-700"
                        }`}>
                          {challenge.result === "win" ? t.challengeWon : challenge.result === "lose" ? t.challengeLost : t.challengeTie}
                        </div>
                      </div>
                    </div>
                  );
                })
              )
            )}
          </div>
          </>
          )}
        </div>
      </div>
    );
  }

  // Multiplayer Results Screen
  if (view === "multiplayerResults" && multiplayerResults.length > 0) {
    const winner = multiplayerResults[0];
    
    return (
      <div className="min-h-screen bg-gradient-to-br from-pink-500 via-red-500 to-yellow-500 p-4 flex items-center justify-center">
        <div className="max-w-lg w-full">
          <div className="bg-white rounded-3xl p-8 shadow-2xl text-center">
            <div className="text-6xl mb-4">🏆</div>
            <h2 className="text-3xl font-bold text-gray-800 mb-2">{t.multiplayerResults}</h2>
            
            {/* Winner */}
            <div className="bg-gradient-to-r from-yellow-100 to-amber-100 rounded-2xl p-6 mb-6">
              <div className="text-lg text-amber-600 font-bold mb-2">{t.winner}</div>
              <div className="text-5xl mb-2">{winner.avatar}</div>
              <div className="text-2xl font-bold text-gray-800" data-testid="multiplayer-winner-name">{winner.name}</div>
              <div className="text-xl text-amber-600">{winner.score}/10 • {(winner.time_ms / 1000).toFixed(1)}s</div>
            </div>
            
            {/* All Players Ranking */}
            <div className="space-y-3 mb-6">
              {multiplayerResults.map((player, idx) => (
                <div 
                  key={player.id || player.name || idx}
                  className={`flex items-center justify-between p-4 rounded-xl ${
                    idx === 0 ? "bg-yellow-50 ring-2 ring-yellow-400" : "bg-gray-50"
                  }`}
                  data-testid={`multiplayer-rank-${idx}`}
                >
                  <div className="flex items-center gap-3">
                    <span className="text-2xl font-bold text-gray-400">
                      {idx === 0 ? "🥇" : idx === 1 ? "🥈" : idx === 2 ? "🥉" : `#${idx + 1}`}
                    </span>
                    <span className="text-3xl">{player.avatar}</span>
                    <span className="font-bold text-gray-700">{player.name}</span>
                  </div>
                  <div className="text-right">
                    <div className="font-bold text-lg">{player.score}/10</div>
                    <div className="text-sm text-gray-500">{(player.time_ms / 1000).toFixed(1)}s</div>
                  </div>
                </div>
              ))}
            </div>
            
            <div className="flex gap-4">
              <button
                onClick={() => { setView("gameSelect"); setMultiplayerResults([]); setMultiplayerPlayers([]); }}
                className="flex-1 px-4 py-3 rounded-xl bg-gradient-to-r from-purple-600 to-pink-500 text-white font-bold hover:opacity-90"
                data-testid="multiplayer-home-btn"
              >
                🏠 {t.backHome}
              </button>
              <button
                onClick={() => { setShowMultiplayerSetup(true); setView("gameSelect"); }}
                className="flex-1 px-4 py-3 rounded-xl border-2 border-pink-400 text-pink-600 font-bold hover:bg-pink-50"
                data-testid="multiplayer-again-btn"
              >
                🔄 {t.playAgain}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Drawing Canvas Screen
  if (view === "drawing") {
    return (
      <DrawingCanvas 
        t={t} 
        onBack={() => { setView("gameSelect"); setSelectedCategory(null); }} 
      />
    );
  }

  // Coloring Book Screen
  if (view === "coloring") {
    return (
      <ColoringBook 
        t={t} 
        lang={lang}
        onBack={() => { setView("gameSelect"); setSelectedCategory(null); }} 
      />
    );
  }

  // Puzzle Game Screen
  if (view === "puzzle") {
    return (
      <PuzzleGame 
        t={t} 
        lang={lang}
        ageGroup={selectedChild?.age_group || "6-7"}
        onBack={() => { setView("gameSelect"); setSelectedCategory(null); }}
        onComplete={(result) => setTimeout(() => quickComplete("puzzle", { moves: result.moves, time_s: result.time }, "puzzle"), 1500)}
      />
    );
  }

  // Odd One Out Game Screen
  if (view === "oddOneOut") {
    return (
      <OddOneOutGame 
        t={t} 
        lang={lang}
        ageGroup={selectedChild?.age_group || "6-7"}
        profileId={selectedChild?.id}
        onBack={() => { setView("gameSelect"); setSelectedCategory(null); }}
        onComplete={(result) => quickComplete("oddOneOut", { score: result.score, total: result.total, max_combo: result.maxCombo || 0 }, "oddOneOut")}
      />
    );
  }

  // Sequences Game Screen
  if (view === "sequences") {
    return (
      <SequenceGame 
        t={t} 
        lang={lang}
        ageGroup={selectedChild?.age_group || "6-7"}
        onBack={() => { setView("gameSelect"); setSelectedCategory(null); }}
        onComplete={(result) => quickComplete("sequences", { score: result.score, total: result.total, max_combo: result.maxCombo || 0 }, "sequences")}
      />
    );
  }

  // Memory Game Screen
  if (view === "memory") {
    return (
      <MemoryGame 
        t={t} 
        ageGroup={selectedChild?.age_group || "6-7"}
        onBack={() => { setView("gameSelect"); setSelectedCategory(null); }}
        onComplete={(result) => setTimeout(() => quickComplete("memory", { moves: result.moves, time_s: result.time }, "memory"), 1500)}
      />
    );
  }

  // Riddles Game Screen
  if (view === "riddles") {
    return (
      <RiddleGame 
        t={t} 
        lang={lang}
        ageGroup={selectedChild?.age_group || "6-7"}
        profileId={selectedChild?.id}
        onBack={() => { setView("gameSelect"); setSelectedCategory(null); }}
        onComplete={(result) => quickComplete("riddles", { score: result.score, total: result.total, max_combo: result.maxCombo || 0 }, "riddles")}
      />
    );
  }

  if (view === "italianQuiz") {
    return (
      <ItalianQuizGame
        t={t}
        lang={lang}
        ageGroup={selectedChild?.age_group || "6-7"}
        playSound={playSound}
        onBack={() => { setView("gameSelect"); setSelectedCategory(null); }}
        onComplete={(result) => quickComplete("italianQuiz", { score: result.score, total: result.total, max_combo: result.maxCombo || 0 }, "italianQuiz")}
      />
    );
  }

  if (view === "aiQuiz") {
    return (
      <AIQuizGame
        child={selectedChild}
        lang={lang}
        api={API}
        t={t}
        onBack={() => { setView("gameSelect"); setSelectedCategory(null); }}
        onComplete={async () => {
          try {
            const updated = await axios.get(`${API}/children/${selectedChild.id}`);
            setSelectedChild(updated.data);
            setChildren(prev => prev.map(c => c.id === updated.data.id ? updated.data : c));
          } catch (e) {
            console.error("Error refreshing child:", e);
          }
        }}
      />
    );
  }

  if (view === "enigmistica") {
    return (
      <>
      {levelUp && <LevelUpOverlay level={levelUp.level} bonus={levelUp.bonus} lang={lang} onClose={() => setLevelUp(null)} />}
      <SettimanaEnigmistica
        onBack={() => setView("gameSelect")}
        apiBase={API}
        onSolved={async (game, extra = {}) => {
          try {
            const res = await axios.post(`${API}/games/quick-complete`, { game, ...extra });
            playSound(res.data.leveled_up ? "levelup" : "coins");
            if (res.data.leveled_up) setLevelUp({ level: res.data.level_after, bonus: res.data.level_bonus });
            refreshSelectedChild();
            return res.data;
          } catch (e) {
            console.error("Error rewarding enigma:", e);
            return null;
          }
        }}
      />
      </>
    );
  }

  if (view === "chess") {
    const refreshMe = async () => {
      const updated = await axios.get(`${API}/children/${selectedChild.id}`);
      setSelectedChild(updated.data);
      setChildren(prev => prev.map(c => c.id === updated.data.id ? updated.data : c));
    };
    return (
      <>
      {levelUp && <LevelUpOverlay level={levelUp.level} bonus={levelUp.bonus} lang={lang} onClose={() => setLevelUp(null)} />}
      <ChessGame
        t={t}
        playSound={playSound}
        child={selectedChild}
        others={children.filter(c => c.id !== selectedChild.id)}
        onBack={() => setView("gameSelect")}
        onFinish={async (result, bot) => {
          try {
            const res = await axios.post(`${API}/chess/complete`, { result, bot });
            if (res.data.leveled_up) setLevelUp({ level: res.data.level_after, bonus: res.data.level_bonus });
            await refreshMe();
            return res.data;
          } catch (e) {
            console.error("Error completing chess:", e);
            return null;
          }
        }}
        onVersusFinish={async (winnerId, loserId, draw) => {
          try {
            const res = await axios.post(`${API}/chess/versus`, { winner_id: winnerId, loser_id: loserId, draw });
            await refreshMe();
            fetchChildren();
            return res.data;
          } catch (e) {
            console.error("Error finishing chess versus:", e);
            return null;
          }
        }}
      />
      </>
    );
  }

  if (view === "checkers") {
    return (
      <>
      {levelUp && <LevelUpOverlay level={levelUp.level} bonus={levelUp.bonus} lang={lang} onClose={() => setLevelUp(null)} />}
      <CheckersGame
        lang={lang}
        t={t}
        playSound={playSound}
        child={selectedChild}
        others={children.filter(c => c.id !== selectedChild.id)}
        onVersusFinish={async (winnerId, loserId) => {
          try {
            const res = await axios.post(`${API}/checkers/versus`, { winner_id: winnerId, loser_id: loserId });
            const updated = await axios.get(`${API}/children/${selectedChild.id}`);
            setSelectedChild(updated.data);
            fetchChildren();
            return res.data;
          } catch (e) {
            console.error("Error finishing versus:", e);
            return null;
          }
        }}
        onBack={() => setView("gameSelect")}
        onFinish={async (won, bot) => {
          try {
            const res = await axios.post(`${API}/checkers/complete`, { child_id: selectedChild.id, won, bot });
            if (res.data.leveled_up) setLevelUp({ level: res.data.level_after, bonus: res.data.level_bonus });
            playSound(won ? "victory" : "coins");
            const updated = await axios.get(`${API}/children/${selectedChild.id}`);
            setSelectedChild(updated.data);
            setChildren(prev => prev.map(c => c.id === updated.data.id ? updated.data : c));
            return res.data;
          } catch (e) {
            console.error("Error completing checkers:", e);
            return null;
          }
        }}
      />
      </>
    );
  }

  // Tournaments Screen
  if (view === "tournaments") {
    return (
      <div className="min-h-screen bg-gradient-to-br from-yellow-500 via-orange-500 to-red-500 p-4">
        <div className="max-w-4xl mx-auto">
          <button
            onClick={() => setView("gameSelect")}
            className="bg-white/20 text-white px-4 py-2 rounded-full font-bold hover:bg-white/30 mb-4"
          >
            ← {t.backHome}
          </button>
          
          <h2 className="text-3xl font-bold text-white text-center mb-6">🏆 {t.tournamentsTitle}</h2>
          
          <div className="space-y-4">
            {tournaments.map((tournament) => (
              <div key={tournament.id} className={`bg-white rounded-2xl p-6 shadow-lg ${tournament.featured ? "ring-4 ring-yellow-400" : ""}`}>
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      {tournament.tournament_type === "premium" && <span className="bg-gradient-to-r from-yellow-400 to-amber-500 text-white text-xs px-2 py-1 rounded-full font-bold">PREMIUM</span>}
                      {tournament.tournament_type === "daily" && <span className="bg-blue-100 text-blue-600 text-xs px-2 py-1 rounded-full font-bold">{t.dailyTournament}</span>}
                      {tournament.tournament_type === "weekly" && <span className="bg-purple-100 text-purple-600 text-xs px-2 py-1 rounded-full font-bold">{t.weeklyTournament}</span>}
                    </div>
                    <h3 className="text-xl font-bold text-gray-800">{lang === "it" ? tournament.name_it : tournament.name_en}</h3>
                    <p className="text-gray-500 text-sm mb-3">{lang === "it" ? tournament.description_it : tournament.description_en}</p>
                    
                    <div className="flex flex-wrap gap-4 text-sm">
                      <div className="flex items-center gap-1">
                        <span>🎮</span>
                        <span className="text-gray-600">{tournament.game_type}</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <span>👥</span>
                        <span className="text-gray-600">{tournament.current_participants}/{tournament.max_participants}</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <span>💰</span>
                        <span className="text-yellow-600 font-bold">{tournament.prize_pool} {t.coins}</span>
                      </div>
                    </div>
                  </div>
                  
                  <div className="text-right">
                    <div className="text-2xl font-bold text-gray-800 mb-2">
                      {tournament.entry_fee > 0 ? `€${tournament.entry_fee}` : t.free}
                    </div>
                    <button
                      onClick={() => joinTournament(tournament)}
                      disabled={loading}
                      className="px-6 py-3 rounded-xl bg-gradient-to-r from-orange-500 to-red-500 text-white font-bold hover:opacity-90"
                      data-testid={`join-tournament-${tournament.id}`}
                    >
                      {t.joinTournament}
                    </button>
                    <div className="flex gap-2 mt-2 justify-end">
                      <button
                        onClick={() => openTournamentLeaderboard(tournament)}
                        className="px-3 py-2 rounded-lg bg-blue-100 text-blue-700 text-xs font-bold hover:bg-blue-200"
                        data-testid={`tournament-lb-btn-${tournament.id}`}
                      >
                        📊 {t.viewRanking}
                      </button>
                      <button
                        onClick={async () => {
                          setShowGroupPicker(tournament);
                          if (myGroups.length === 0) {
                            setGroupPickerLoading(true);
                            await fetchMyGroups();
                            setGroupPickerLoading(false);
                          }
                        }}
                        className="px-3 py-2 rounded-lg bg-green-100 text-green-700 text-xs font-bold hover:bg-green-200"
                        data-testid={`tournament-group-btn-${tournament.id}`}
                      >
                        👥 {t.joinAsGroup}
                      </button>
                    </div>
                  </div>
                </div>
                
                {/* Prizes Preview */}
                <div className="mt-4 pt-4 border-t border-gray-100 flex gap-4">
                  {tournament.prizes.slice(0, 3).map((prize, idx) => (
                    <div key={`${tournament.id}-prize-${idx}`} className="flex items-center gap-2 text-sm">
                      <span>{idx === 0 ? "🥇" : idx === 1 ? "🥈" : "🥉"}</span>
                      <span className="text-yellow-600 font-bold">{prize.coins}</span>
                      {prize.trophy && <span>🏆</span>}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Tournament Leaderboard Modal */}
        {selectedTournament && (
          <div className="fixed inset-0 bg-black/60 flex items-center justify-center p-4 z-50" data-testid="tournament-lb-modal">
            <div className="bg-white rounded-3xl p-6 max-w-md w-full max-h-[85vh] overflow-y-auto">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-xl font-bold text-gray-800">📊 {lang === "it" ? selectedTournament.name_it : selectedTournament.name_en}</h3>
                <button onClick={() => setSelectedTournament(null)} className="text-gray-400 hover:text-gray-600 text-2xl" data-testid="close-tournament-lb">✕</button>
              </div>
              <div className="flex gap-2 mb-4">
                {[{ key: "players", label: `🧒 ${t.playersTab}` }, { key: "groups", label: `👥 ${t.groupsTab}` }].map((tab) => (
                  <button
                    key={tab.key}
                    onClick={() => setTournamentLbTab(tab.key)}
                    className={`flex-1 px-3 py-2 rounded-full font-bold text-sm ${tournamentLbTab === tab.key ? "bg-orange-500 text-white" : "bg-gray-100 text-gray-600"}`}
                    data-testid={`tournament-lb-tab-${tab.key}`}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>
              {tournamentLbTab === "players" && (
                tournamentLeaderboard.length === 0 ? (
                  <p className="text-center text-gray-400 py-6">{t.noScores}</p>
                ) : (
                  <div className="space-y-2">
                    {tournamentLeaderboard.map((s) => (
                      <div key={s.id} className="flex items-center gap-3 bg-gray-50 rounded-xl p-3" data-testid="tournament-player-row">
                        <span className="font-bold text-gray-400 w-8">{s.rank === 1 ? "🥇" : s.rank === 2 ? "🥈" : s.rank === 3 ? "🥉" : `#${s.rank}`}</span>
                        <span className="text-2xl">{s.child_avatar}</span>
                        <span className="flex-1 font-bold text-gray-700">{s.child_name}</span>
                        <span className="font-bold text-orange-600">{s.best_score}</span>
                      </div>
                    ))}
                  </div>
                )
              )}
              {tournamentLbTab === "groups" && (
                groupTournamentLb.length === 0 ? (
                  <p className="text-center text-gray-400 py-6">{t.noScores}</p>
                ) : (
                  <div className="space-y-2">
                    {groupTournamentLb.map((g) => (
                      <div key={g.id} className="flex items-center gap-3 bg-gray-50 rounded-xl p-3" data-testid="tournament-group-row">
                        <span className="font-bold text-gray-400 w-8">{g.rank === 1 ? "🥇" : g.rank === 2 ? "🥈" : g.rank === 3 ? "🥉" : `#${g.rank}`}</span>
                        <span className="text-2xl">{g.group_avatar}</span>
                        <div className="flex-1">
                          <div className="font-bold text-gray-700">{g.group_name}</div>
                          <div className="text-xs text-gray-400">{(g.members || []).length} 🧒</div>
                        </div>
                        <div className="text-right">
                          <div className="font-bold text-green-600">{g.combined_score}</div>
                          <div className="text-xs text-gray-400">{t.teamScore}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                )
              )}
            </div>
          </div>
        )}

        {/* Group Picker Modal */}
        {showGroupPicker && (
          <div className="fixed inset-0 bg-black/60 flex items-center justify-center p-4 z-50" data-testid="group-picker-modal">
            <div className="bg-white rounded-3xl p-6 max-w-md w-full">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-xl font-bold text-gray-800">👥 {t.chooseGroup}</h3>
                <button onClick={() => setShowGroupPicker(null)} className="text-gray-400 hover:text-gray-600 text-2xl" data-testid="close-group-picker">✕</button>
              </div>
              {groupPickerLoading ? (
                <p className="text-center text-gray-400 py-6 animate-pulse" data-testid="group-picker-loading">⏳ ...</p>
              ) : myGroups.length === 0 ? (
                <p className="text-center text-gray-500 py-6">{t.noGroupsYet}</p>
              ) : (
                <div className="space-y-2">
                  {myGroups.map((g) => (
                    <button
                      key={g.id}
                      onClick={() => joinTournamentAsGroup(showGroupPicker.id, g.id)}
                      disabled={loading}
                      className="w-full flex items-center gap-3 bg-green-50 hover:bg-green-100 border-2 border-green-200 rounded-xl p-3 text-left font-bold text-gray-700 disabled:opacity-50"
                      data-testid={`pick-group-${g.id}`}
                    >
                      <span className="text-2xl">{g.avatar}</span>
                      <span className="flex-1">{g.name}</span>
                      <span className="text-xs text-gray-400">{(g.members || []).length} 🧒</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Group Join Toast */}
        {groupJoinMsg && (
          <div className={`fixed bottom-6 left-1/2 -translate-x-1/2 px-6 py-3 rounded-full font-bold shadow-xl z-50 ${groupJoinMsg.ok ? "bg-green-500 text-white" : "bg-red-500 text-white"}`} data-testid="group-join-toast">
            {groupJoinMsg.ok ? "✅" : "⚠️"} {groupJoinMsg.text}
          </div>
        )}
      </div>
    );
  }

  // Friends Screen
  if (view === "friends") {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-500 via-indigo-500 to-purple-500 p-4">
        <div className="max-w-4xl mx-auto">
          <button
            onClick={() => setView("gameSelect")}
            className="bg-white/20 text-white px-4 py-2 rounded-full font-bold hover:bg-white/30 mb-4"
          >
            ← {t.backHome}
          </button>
          
          <h2 className="text-3xl font-bold text-white text-center mb-6">👫 {t.friendsTitle}</h2>
          
          {/* Tabs */}
          <div className="flex justify-center gap-2 mb-6 flex-wrap">
            {[
              { key: "list", label: t.friends, count: friendsList.length },
              { key: "requests", label: t.friendRequests, count: friendRequests.length },
              { key: "search", label: t.addFriend },
              { key: "virtual", label: `🤖 ${t.virtualFriendsTab}` }
            ].map((tab) => (
              <button
                key={tab.key}
                onClick={() => setFriendsTab(tab.key)}
                className={`px-4 py-2 rounded-full font-bold transition-all ${friendsTab === tab.key ? "bg-white text-indigo-600" : "bg-white/30 text-white hover:bg-white/50"}`}
                data-testid={`friends-tab-${tab.key}`}
              >
                {tab.label} {tab.count > 0 && `(${tab.count})`}
              </button>
            ))}
          </div>

          {friendsTab === "virtual" && (
            <VirtualFriendsList data={virtualFriends} lang={lang} t={t} onChallenge={(id) => startBotChallenge(id, "mixed")} loading={loading} />
          )}
          
          {/* Friends List */}
          {friendsTab === "list" && (
            <div className="space-y-3">
              {friendsList.length === 0 ? (
                <div className="bg-white rounded-2xl p-8 text-center">
                  <div className="text-5xl mb-4">🤝</div>
                  <p className="text-gray-500 mb-4">{t.noFriends}</p>
                  <div className="flex gap-2 justify-center flex-wrap">
                    <button onClick={() => setFriendsTab("search")} className="px-4 py-2 rounded-xl bg-blue-500 text-white font-bold hover:bg-blue-600" data-testid="friends-empty-search-btn">🔍 {t.addFriend}</button>
                    <button onClick={() => setFriendsTab("virtual")} className="px-4 py-2 rounded-xl bg-purple-500 text-white font-bold hover:bg-purple-600" data-testid="friends-empty-virtual-btn">🤖 {t.virtualFriends}</button>
                  </div>
                </div>
              ) : (
                friendsList.map((friend) => (
                  <div key={friend.id} className="bg-white rounded-2xl p-4 flex items-center justify-between" data-testid={`friend-row-${friend.id}`}>
                    <div className="flex items-center gap-3">
                      <span className="text-4xl">{friend.avatar}</span>
                      <span className="font-bold text-gray-700">{friend.name}</span>
                    </div>
                    <button
                      onClick={() => inviteToChallenge(friend.id, "mixed")}
                      disabled={loading}
                      className="px-4 py-2 rounded-xl bg-indigo-500 text-white font-bold hover:bg-indigo-600 disabled:opacity-50"
                      data-testid={`friend-challenge-${friend.id}`}
                    >
                      ⚔️ {t.challengeBtn}
                    </button>
                  </div>
                ))
              )}
            </div>
          )}
          
          {/* Friend Requests */}
          {friendsTab === "requests" && (
            <div className="space-y-3">
              {friendRequests.length === 0 ? (
                <div className="bg-white rounded-2xl p-8 text-center">
                  <div className="text-5xl mb-4">📬</div>
                  <p className="text-gray-500">{t.noRequests}</p>
                </div>
              ) : (
                friendRequests.map((req) => (
                  <div key={req.id} className="bg-white rounded-2xl p-4 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <span className="text-4xl">{req.requester_avatar}</span>
                      <span className="font-bold text-gray-700">{req.requester_name}</span>
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={() => acceptFriendRequest(req.id)}
                        className="px-4 py-2 rounded-xl bg-green-500 text-white font-bold hover:bg-green-600"
                        data-testid={`friend-accept-${req.id}`}
                      >
                        ✓
                      </button>
                      <button
                        onClick={() => rejectFriendRequest(req.id)}
                        className="px-4 py-2 rounded-xl bg-red-500 text-white font-bold hover:bg-red-600"
                        data-testid={`friend-reject-${req.id}`}
                      >
                        ✕
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>
          )}
          
          {/* Search */}
          {friendsTab === "search" && (
            <div>
              <div className="mb-2">
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => { setSearchQuery(e.target.value); searchFriends(e.target.value); }}
                  placeholder={t.searchFriend}
                  className="w-full px-4 py-3 rounded-xl border-2 border-white/30 bg-white/20 text-white placeholder-white/70 focus:border-white focus:outline-none"
                  data-testid="friend-search-input"
                />
              </div>
              <p className="text-white/80 text-xs mb-4 text-center" data-testid="friend-search-hint">📧 {t.searchFriendHint}</p>
              {searchQuery.length >= 2 && searchResults.length === 0 && (
                <div className="bg-white/90 rounded-2xl p-6 text-center text-gray-500 text-sm" data-testid="friend-search-empty">
                  {lang === "it" ? "Nessun giocatore trovato. Prova con il nome esatto o l'email dell'account." : "No player found. Try the exact name or the account email."}
                </div>
              )}
              <div className="space-y-3">
                {searchResults.map((child) => (
                  <div key={child.id} className="bg-white rounded-2xl p-4 flex items-center justify-between" data-testid={`friend-search-result-${child.id}`}>
                    <div className="flex items-center gap-3">
                      <span className="text-4xl">{child.avatar}</span>
                      <div>
                        <span className="font-bold text-gray-700">{child.name}</span>
                        <span className="text-sm text-gray-400 ml-2">{child.age_group} {t.years}</span>
                        {child.found_by === "email" && <div className="text-xs text-green-600 font-bold">📧 {t.foundByEmail}</div>}
                      </div>
                    </div>
                    {child.friend_status === "accepted" ? (
                      <span className="px-3 py-2 rounded-xl bg-green-100 text-green-700 font-bold text-sm">✓ {t.alreadyFriend}</span>
                    ) : child.friend_status === "pending" ? (
                      <span className="px-3 py-2 rounded-xl bg-amber-100 text-amber-700 font-bold text-sm">⏳ {t.requestPending}</span>
                    ) : (
                    <button
                      onClick={() => sendFriendRequest(child.id)}
                      className="px-4 py-2 rounded-xl bg-blue-500 text-white font-bold hover:bg-blue-600"
                      data-testid={`friend-add-${child.id}`}
                    >
                      + {t.addFriend}
                    </button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  // Groups Screen
  if (view === "groups") {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-500 via-teal-500 to-cyan-500 p-4">
        <div className="max-w-4xl mx-auto">
          <button
            onClick={() => setView("gameSelect")}
            className="bg-white/20 text-white px-4 py-2 rounded-full font-bold hover:bg-white/30 mb-4"
          >
            ← {t.backHome}
          </button>
          
          <h2 className="text-3xl font-bold text-white text-center mb-6">👥 {t.groupsTitle}</h2>
          
          {/* Create Group Button */}
          <button
            onClick={() => setShowCreateGroup(true)}
            className="w-full mb-6 py-4 rounded-2xl bg-white text-teal-600 font-bold text-lg hover:bg-gray-50"
          >
            + {t.createGroup}
          </button>
          
          {/* My Groups */}
          <h3 className="text-xl font-bold text-white mb-3">{t.groupsTitle}</h3>
          <div className="space-y-3 mb-6">
            {myGroups.length === 0 ? (
              <div className="bg-white/20 backdrop-blur rounded-2xl p-6 text-center text-white">
                {t.noGroups}
              </div>
            ) : (
              myGroups.map((group) => (
                <div key={group.id} className="bg-white rounded-2xl p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <span className="text-4xl">{group.avatar}</span>
                      <div>
                        <div className="font-bold text-gray-800">{group.name}</div>
                        <div className="text-sm text-gray-500">{group.members.length}/{group.max_members} {t.groupMembers}</div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {group.owner_id === selectedChild.id && (
                        <span className="bg-yellow-100 text-yellow-600 text-xs px-2 py-1 rounded-full">{t.groupAdmin}</span>
                      )}
                      <button
                        onClick={() => leaveGroup(group.id)}
                        disabled={group.owner_id === selectedChild.id}
                        className="px-3 py-1 rounded-lg bg-red-100 text-red-600 text-sm font-bold hover:bg-red-200 disabled:opacity-50"
                      >
                        {t.leaveGroup}
                      </button>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
          
          {/* Open Groups to Join */}
          <h3 className="text-xl font-bold text-white mb-3">{t.openGroup}</h3>
          <div className="space-y-3">
            {openGroups.filter(g => !myGroups.find(mg => mg.id === g.id)).map((group) => (
              <div key={group.id} className="bg-white rounded-2xl p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <span className="text-4xl">{group.avatar}</span>
                    <div>
                      <div className="font-bold text-gray-800">{group.name}</div>
                      <div className="text-sm text-gray-500">{group.members.length}/{group.max_members} {t.groupMembers}</div>
                    </div>
                  </div>
                  <button
                    onClick={() => joinGroup(group.id)}
                    className="px-4 py-2 rounded-xl bg-teal-500 text-white font-bold hover:bg-teal-600"
                  >
                    {t.joinGroup}
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
        
        {/* Create Group Modal */}
        {showCreateGroup && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-3xl p-6 max-w-md w-full">
              <h3 className="text-2xl font-bold text-teal-600 mb-4">{t.createGroup}</h3>
              
              <div className="space-y-4">
                <input
                  type="text"
                  value={newGroup.name}
                  onChange={(e) => setNewGroup({ ...newGroup, name: e.target.value })}
                  placeholder={t.name}
                  className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 focus:border-teal-500 focus:outline-none"
                />
                
                <div className="flex gap-2 flex-wrap">
                  {["👥", "🦁", "🐉", "⚡", "🌟", "🔥", "💎", "🎮"].map((emoji) => (
                    <button
                      key={emoji}
                      onClick={() => setNewGroup({ ...newGroup, avatar: emoji })}
                      className={`text-3xl p-2 rounded-xl ${newGroup.avatar === emoji ? "bg-teal-100 ring-2 ring-teal-500" : "bg-gray-100"}`}
                    >
                      {emoji}
                    </button>
                  ))}
                </div>
                
                <div className="flex gap-2">
                  {[
                    { type: "open", label: t.openGroup },
                    { type: "private", label: t.privateGroup }
                  ].map((opt) => (
                    <button
                      key={opt.type}
                      onClick={() => setNewGroup({ ...newGroup, group_type: opt.type })}
                      className={`flex-1 py-2 rounded-xl font-bold ${newGroup.group_type === opt.type ? "bg-teal-500 text-white" : "bg-gray-100 text-gray-600"}`}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
                
                <div className="flex gap-2">
                  <button
                    onClick={() => setShowCreateGroup(false)}
                    className="flex-1 py-3 rounded-xl border-2 border-gray-300 text-gray-600 font-bold"
                  >
                    {t.cancel}
                  </button>
                  <button
                    onClick={createGroup}
                    disabled={!newGroup.name.trim() || loading}
                    className="flex-1 py-3 rounded-xl bg-teal-500 text-white font-bold disabled:opacity-50"
                  >
                    {t.create}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  return null;
}

export default App;
